/**
 * EEG Waveform Renderer
 *
 * Handles rendering of EEG waveforms on HTML5 Canvas with support for:
 * - Multi-channel display with configurable montages
 * - Real-time filtering (high-pass, low-pass, notch)
 * - Dynamic scaling based on sensitivity
 * - Time windowing and paging
 * - Annotation overlays
 * - Grid lines and scale markers
 * - Real-time Laplacian derivation (Small, Large, Hjorth) via LAP_/LLAP_/HJR_ conventions
 */

// ---------------------------------------------------------------------------
// 10-20 Laplacian Neighbour Maps
//
// These define the neighbourhood topology for three spatial-filter families:
//
//   LAP_X  — Small Laplacian: 4 immediate 10-20 neighbours (1-electrode spacing)
//   LLAP_X — Large Laplacian: electrodes at 2-hop distance
//   HJR_X  — Hjorth:          exactly 3 nearest neighbours
//
// Each entry lists the UPPERCASE electrode names to average. The renderer's
// getMontageChannelData() resolves "EEG LAP_FP1" → mean of the listed channels.
// ---------------------------------------------------------------------------

/** Small Laplacian: immediate (one inter-electrode spacing) neighbours */
const LAPLACIAN_SMALL_NEIGHBORS: Record<string, string[]> = {
  FP1: ["F3", "F7", "FP2"],
  FP2: ["F4", "F8", "FP1"],
  F7: ["FP1", "F3", "T3"],
  F3: ["FP1", "F7", "FZ", "C3"],
  FZ: ["F3", "F4", "CZ"],
  F4: ["FP2", "F8", "FZ", "C4"],
  F8: ["FP2", "F4", "T4"],
  T3: ["F7", "C3", "T5"],
  C3: ["F3", "T3", "CZ", "P3"],
  CZ: ["C3", "C4", "FZ", "PZ"],
  C4: ["F4", "T4", "CZ", "P4"],
  T4: ["F8", "C4", "T6"],
  T5: ["T3", "P3", "O1"],
  P3: ["C3", "T5", "PZ", "O1"],
  PZ: ["P3", "P4", "CZ"],
  P4: ["C4", "T6", "PZ", "O2"],
  T6: ["T4", "P4", "O2"],
  O1: ["T5", "P3", "O2"],
  O2: ["T6", "P4", "O1"],
};

/** Large Laplacian: 2-hop neighbours in the 10-20 graph */
const LAPLACIAN_LARGE_NEIGHBORS: Record<string, string[]> = {
  FP1: ["C3", "F8", "FP2", "F4"],
  FP2: ["C4", "F7", "FP1", "F3"],
  F7: ["FP2", "FZ", "T5"],
  F3: ["FP2", "F8", "T3", "P3"],
  FZ: ["FP1", "FP2", "CZ"],
  F4: ["FP1", "F7", "T4", "P4"],
  F8: ["FP1", "FZ", "T6"],
  T3: ["FP1", "CZ", "O1"],
  C3: ["FP1", "T4", "PZ"],
  CZ: ["FP1", "FP2", "O1", "O2"],
  C4: ["FP2", "T3", "PZ"],
  T4: ["FP2", "CZ", "O2"],
  T5: ["F7", "P4", "O2"],
  P3: ["F3", "T4", "O2"],
  PZ: ["C3", "C4", "O1", "O2"],
  P4: ["F4", "T3", "O1"],
  T6: ["F8", "P3", "O1"],
  O1: ["C3", "T6", "P4"],
  O2: ["C4", "T5", "P3"],
};

/** Hjorth: exactly 3 nearest neighbours */
const HJORTH_NEIGHBORS: Record<string, string[]> = {
  FP1: ["F3", "F7", "FP2"],
  FP2: ["F4", "F8", "FP1"],
  F7: ["FP1", "F3", "T3"],
  F3: ["FP1", "FZ", "C3"],
  FZ: ["F3", "F4", "CZ"],
  F4: ["FP2", "FZ", "C4"],
  F8: ["FP2", "F4", "T4"],
  T3: ["F7", "C3", "T5"],
  C3: ["F3", "CZ", "P3"],
  CZ: ["C3", "C4", "PZ"],
  C4: ["F4", "CZ", "P4"],
  T4: ["F8", "C4", "T6"],
  T5: ["T3", "P3", "O1"],
  P3: ["C3", "PZ", "O1"],
  PZ: ["P3", "P4", "CZ"],
  P4: ["C4", "PZ", "O2"],
  T6: ["T4", "P4", "O2"],
  O1: ["T5", "P3", "O2"],
  O2: ["T6", "P4", "O1"],
};

import WebGLRendererAdapter from "../renderers/webglRenderer";
import logger from "@shared/utils/logger";
import {
  EEG_VIEWER_TRUE_OVERLAP_BLIT_MIN_SPEED,
  resolveEEGViewerPlaybackMaxBlitFraction,
} from "../components/eegViewerPlaybackHelpers";
import { resolveRecordingChannelName } from "../components/eegViewerDisplayHelpers";
import { findPeakSampleIndex } from "../utils/eegCaliperMeasurement";
import {
  PAGE_PLAYHEAD_CHIP_HEIGHT_PX,
  resolvePagePlayheadTimeChipLayout,
} from "../utils/pagePlayhead";
import { resolveEegGridDensityPlan } from "../utils/eegGridDensity";

export interface EEGChannel {
  name: string;
  data: Float32Array;
  sampleRate: number;
  unit: string;
  /** True minimum μV per bin — from pyramid parquet _min columns (level > 0 only) */
  envelopeMin?: Float32Array;
  /** True maximum μV per bin — from pyramid parquet _max columns (level > 0 only) */
  envelopeMax?: Float32Array;
  displayGeometry?: {
    min: Float32Array;
    max: Float32Array;
    mean: Float32Array;
    sourceSamplesPerBin: number;
  };
}

export interface EEGMontageChannel {
  label: string;
  positiveInput: string;
  negativeInput?: string;
  color?: string;
}

export interface RenderConfig {
  sensitivity: number; // μV/mm
  pixelsPerMm?: number; // screen calibration in px/mm
  timeScale: number; // seconds per page
  highPassFilter: number; // Hz
  lowPassFilter: number; // Hz
  notchFilter: boolean; // 60Hz notch
  notch50Hz?: boolean; // EEG-CTRL-003: 50 Hz mains notch
  removeDcOffset?: boolean; // EEG-CTRL-020: subtract per-channel mean before render
  polarity?: "negative-up" | "positive-up"; // EEG-CTRL-001: invert Y axis when "positive-up"
  channelColorMode?: "uniform" | "alternating" | "by-type"; // EEG-CTRL-021
  channelTypeColors?: Partial<
    Record<"EEG" | "ECG" | "EMG" | "EOG" | "ANNOTATION" | "OTHER", string>
  >; // DB/config-driven palette for by-type mode
  channelGainOverrides?: Record<string, number>; // EEG-CTRL-019: per-channel gain multiplier (default 1.0)
  channelScrollOffset?: number; // EEG-CTRL-011: first visible channel index
  visibleChannelCount?: number; // EEG-CTRL-011: number of rendered channels in viewport
  playbackSpeed: number; // multiplier
  playbackTransportMode?: "play" | "inspect"; // play-only overview can suppress detail-only traces
  showGrid: boolean;
  gridStyle?: "standard" | "clinical"; // clinical: EDF-style major/minor grid alignment
  gridDensity?: "low" | "medium" | "high"; // low = sparse, high = dense visual subdivisions
  showTimeline: boolean; // Show/hide time ruler
  timelinePosition: "top" | "bottom"; // Position of time ruler
  backgroundColor: string;
  waveformColor: string;
  gridColor: string;
  // CP-S17-001: Display profile visual parameters
  waveformThickness?: number; // canvas lineWidth for signal traces (default 1)
  channelSpacing?: number; // vertical spacing multiplier per channel (default 1.0)
  labelFontSize?: number; // channel label font size in px (default 12)
  channelPositionOffsets?: Record<string, number>; // per-channel vertical offset in channel-height units
  // EEG-UX-006: Channel group separators
  showChannelGroupLabels?: boolean; // draw region separators + group labels (default true)
  showScaleMarkers?: boolean; // draw persistent timebase readout (default true)
  /**
   * When true, derived paper speed (mm/s) may be shown. Only set this after
   * measured screen calibration has passed; OS/CSS px/mm is not sufficient.
   */
  physicalTimebaseTrusted?: boolean;
  amplitudeScalingMode?: "auto" | "fixed"; // fixed: sensitivity-calibrated µV scaling
  persistentTimestamp?: boolean; // draw always-on timestamp readout (default true)
  recordingStartIso?: string | null; // optional absolute timeline anchor
  displayMode?: "stacked" | "butterfly"; // stacked keeps per-channel lanes; butterfly overlays visible channels
  allowTraceOverflow?: boolean; // stacked-only expert mode; preserves lane baselines while skipping lane clipping
  rendererMode?: "gpu" | "cpu"; // default: "cpu"; "gpu" offloads envelope band to WebGL2
  annotationOpacity?: number; // alpha for duration annotation regions on the canvas
  /**
   * Sprint 4C-004: first playable paint draws waveform only; grid/labels/HUD
   * overlays wait until after playbackControlsInteractive.
   */
  minimalStartupPaint?: boolean;
}

export interface RenderPlotGeometry {
  left: number;
  width: number;
  right: number;
  canvasWidth: number;
  top: number;
  height: number;
  bottom: number;
  pixelsPerSecond: number;
}

export interface RenderViewportMetrics {
  waveformAreaTop: number;
  waveformAreaBottom: number;
  waveformAreaHeight: number;
  visibleChannelCount: number;
  channelHeight: number;
  pixelsPerMm: number;
  plotLeft: number;
  plotWidth: number;
}

export interface WaveformRenderMetrics {
  useBlit: boolean;
  blitReady: boolean;
  deltaPx: number;
  clipLeft: number;
  rawClipLeft: number;
  clipWidth: number;
  blitSeamOverlapPx: number;
  fullRedraw: boolean;
  blitBlockReason: string | null;
  maxBlitFraction: number;
  transitionFrameIndex: number | null;
  transitionBlitRelaxed: boolean;
  rapidReviewSourceStrideMax: number;
  envelopeChannelCount: number;
  envelopeRenderStrideMin: number | null;
  envelopeRenderStrideMax: number | null;
  envelopeSamplesPerPixelMax: number;
  paintLuma: number | null;
  paintLumaDelta: number | null;
  paintTransition: "blit" | "full-redraw" | "held" | "post-flush" | null;
}

const DEFAULT_PIXELS_PER_MM = 96 / 25.4;
const MIN_PIXELS_PER_MM = 1;
const MAX_PIXELS_PER_MM = 40;
const BASELINE_SENSITIVITY_UV_PER_MM = 10;

export function resolveRapidReviewSourceStride(input: {
  playbackSpeed: number;
  transportMode?: "play" | "inspect";
  sourceLength: number;
  pixelWidth: number;
}): number {
  if (input.transportMode !== "play" || input.playbackSpeed < 30) return 1;
  // Under dual-video+k6, 30x–150x long tasks are dominated by waveform traversal.
  // Keep budgets tight enough that a single paint stays under the soak LOAF gate.
  const maxPointsPerPixel =
    input.playbackSpeed >= 200
      ? 0.0625
      : input.playbackSpeed >= 150
        ? 0.125
        : input.playbackSpeed >= 100
          ? 0.2
          : 0.15;
  const pointBudget = Math.max(
    1,
    Math.ceil(input.pixelWidth * maxPointsPerPixel),
  );
  const computed = Math.max(1, Math.ceil(input.sourceLength / pointBudget));
  // LOD tiles are already short; without a floor, stride collapses to 1–3 and
  // envelope overview still walks nearly every sample under k6.
  const minStride = 8;
  return Math.max(computed, minStride);
}

export function resolveEnvelopeRenderStride(input: {
  hasEnvelope: boolean;
  rapidReviewSourceStride: number;
  samplesPerPixel: number;
}): number {
  const rapidStride = Math.max(1, Math.floor(input.rapidReviewSourceStride));
  if (!input.hasEnvelope) return rapidStride;
  if (rapidStride <= 1) {
    if (!Number.isFinite(input.samplesPerPixel) || input.samplesPerPixel <= 1) {
      return 1;
    }
    return Math.max(1, Math.floor(input.samplesPerPixel * 0.5));
  }
  // Rapid-review already chose a traversal budget. Do not densify envelopes
  // back toward 1 sample/pixel — that undoes the LOAF win under dual-video+k6.
  if (!Number.isFinite(input.samplesPerPixel) || input.samplesPerPixel <= 1) {
    return rapidStride;
  }
  const coverageStride = Math.max(1, Math.floor(input.samplesPerPixel * 0.5));
  return Math.max(rapidStride, coverageStride);
}

export function shouldUseRapidReviewEnvelopeOverview(input: {
  playbackSpeed: number;
  transportMode?: "play" | "inspect";
  hasEnvelope: boolean;
}): boolean {
  return (
    input.transportMode === "play" &&
    input.playbackSpeed >= 30 &&
    input.hasEnvelope
  );
}

function recordRendererPerfEvent(
  kind: string,
  payload: Record<string, string | number | boolean | null>,
): void {
  if (typeof window === "undefined") return;
  const recorder = (window as any).__eegPlaybackPerfRecord;
  if (typeof recorder === "function") {
    recorder(kind, payload);
  }
}

/**
 * Converts sensitivity + screen calibration into a waveform scale multiplier.
 * 1.0 means baseline behavior (10 μV/mm at ~96 DPI).
 */
export const calculateWaveformScaleMultiplier = (
  sensitivityUvPerMm: number,
  pixelsPerMm: number = DEFAULT_PIXELS_PER_MM,
): number => {
  const safeSensitivity =
    typeof sensitivityUvPerMm === "number" &&
    Number.isFinite(sensitivityUvPerMm)
      ? Math.max(0.1, sensitivityUvPerMm)
      : BASELINE_SENSITIVITY_UV_PER_MM;
  const safePixelsPerMm =
    typeof pixelsPerMm === "number" && Number.isFinite(pixelsPerMm)
      ? Math.max(MIN_PIXELS_PER_MM, Math.min(MAX_PIXELS_PER_MM, pixelsPerMm))
      : DEFAULT_PIXELS_PER_MM;

  const sensitivityScale = BASELINE_SENSITIVITY_UV_PER_MM / safeSensitivity;
  const calibrationScale = safePixelsPerMm / DEFAULT_PIXELS_PER_MM;

  // Keep scaling bounded to avoid unreadable clipping.
  return Math.max(0.2, Math.min(5, sensitivityScale * calibrationScale));
};

/**
 * Horizontal paper speed implied by the current page width and s/page setting.
 * Requires a trusted px/mm (measured calibration), not assumed OS DPI.
 */
export const calculateHorizontalMmPerSecond = (
  pageWidthPx: number,
  timeScaleSeconds: number,
  pixelsPerMm: number,
): number | null => {
  if (
    !Number.isFinite(pageWidthPx) ||
    !Number.isFinite(timeScaleSeconds) ||
    !Number.isFinite(pixelsPerMm) ||
    pageWidthPx <= 0 ||
    timeScaleSeconds <= 0 ||
    pixelsPerMm <= 0
  ) {
    return null;
  }
  return pageWidthPx / pixelsPerMm / timeScaleSeconds;
};

type ChannelGroupLabel =
  | "FRONTAL"
  | "CENTRAL"
  | "TEMPORAL"
  | "PARIETAL"
  | "OCCIPITAL"
  | "REFERENCE";

const CHANNEL_GROUP_PATTERNS: Array<{
  label: ChannelGroupLabel;
  patterns: RegExp[];
}> = [
  {
    label: "FRONTAL",
    patterns: [/^FP1$/, /^FP2$/, /^F3$/, /^F4$/, /^F7$/, /^F8$/, /^FZ$/],
  },
  {
    label: "CENTRAL",
    patterns: [/^C3$/, /^C4$/, /^CZ$/],
  },
  {
    label: "TEMPORAL",
    patterns: [/^T3$/, /^T4$/, /^T5$/, /^T6$/, /^T7$/, /^T8$/],
  },
  {
    label: "PARIETAL",
    patterns: [/^P3$/, /^P4$/, /^PZ$/, /^P7$/, /^P8$/],
  },
  {
    label: "OCCIPITAL",
    patterns: [/^O1$/, /^O2$/],
  },
  {
    label: "REFERENCE",
    patterns: [/^A1$/, /^A2$/, /^E1$/, /^E2$/],
  },
];

const classifyChannelGroup = (
  channelName: string,
): ChannelGroupLabel | "OTHER" => {
  const tokens = channelName
    .toUpperCase()
    .replace(/[^A-Z0-9\-/ ]/g, " ")
    .split(/[\s\-/]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  for (const token of tokens) {
    for (const group of CHANNEL_GROUP_PATTERNS) {
      if (group.patterns.some((pattern) => pattern.test(token))) {
        return group.label;
      }
    }
  }
  return "OTHER";
};

export interface Annotation {
  annotation_id: string;
  start_time: number;
  end_time?: number;
  annotation_type: string;
  annotation_text?: string;
  notes?: string | null;
  channel_scope?: "all" | "channel_set" | string;
  channels_affected?: string[];
  color?: string;
}

export class EEGWaveformRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private channels: EEGChannel[] = [];
  private montageChannels: EEGMontageChannel[] = [];
  private config: RenderConfig;
  private currentTime: number = 0;
  private dataTimeOffset: number = 0; // Time offset of loaded data (seconds) — sample[0] corresponds to this time
  private sampleRate: number = 256; // Default 256 Hz
  private frameCount: number = 0; // For throttled debug logging
  private selectedMontageChannelLabels: Set<string> = new Set();
  private crosshairPosition: { x: number; y: number } | null = null;
  private pinnedCursorTime: number | null = null;
  /** Trackit-style reading-line offset from viewport left, in seconds. */
  private pagePlayheadOffsetSeconds: number = 0;

  // Per-channel filter cache — keyed by Float32Array object identity so each
  // channel buffer is distinct. Inner map key is filter-settings string.
  // WeakMap ensures GC'd buffers are evicted automatically.
  // Recomputing O(n²) moving-average filters on every 60fps RAF tick was
  // causing 180+ms stalls; caching amortises the cost to once per data load.
  private filterCache: WeakMap<Float32Array, Map<string, Float32Array>> =
    new WeakMap();

  // Derivation cache — stores bipolar/Laplacian computed Float32Arrays so
  // getMontageChannelData() doesn't allocate a fresh array on every frame.
  // Without this, each new array object causes a WeakMap filter-cache miss,
  // making the filter cache completely ineffective for bipolar channels and
  // causing ~4.4 MB of GC pressure per frame (37 channels × 4 passes × 6 KB).
  // Cleared in loadChannels() when the underlying raw data changes.
  private derivationCache: Map<string, Float32Array> = new Map();
  private displayGeometryCache: Map<
    string,
    NonNullable<EEGChannel["displayGeometry"]>
  > = new Map();

  // Pre-warmed derivation cache installed by preWarmForChannels().
  // When non-null, loadChannels() swaps this in instead of clearing the old
  // cache, giving the first render after a buffer flush all derivation hits.
  private pendingPreWarmDerivCache: Map<string, Float32Array> | null = null;

  // Virtual reference envelopes (AVG / linked-ears) are derived from the raw
  // channel envelopes and reused across all channels in the same montage.
  private virtualReferenceEnvelopeCache: Map<
    string,
    { min: Float32Array; max: Float32Array }
  > = new Map();

  // Pre-allocated filter scratch buffers — reused inside applyFilters() as
  // ping-pong intermediates so only one final Float32Array(n) is allocated per
  // channel per settings key.  Without this, the cache-miss path allocates
  // 3-4 × Float32Array(6000) = 72-96KB per channel; across 37 channels per
  // preWarmForChannels() call that is 2.7-3.5MB of short-lived allocations,
  // triggering GC pauses of 50-148ms that delay RAF ticks.
  private filterScratchA: Float32Array = new Float32Array(0);
  private filterScratchB: Float32Array = new Float32Array(0);

  // Pre-allocated scratch buffers for the HP-prefix (overlap-save) path inside
  // applyFilters().  When hpPrefix is provided, applyFilters() must concatenate
  // [prefix | newBuffer] before running the HP filter and then slice out the
  // new-buffer portion.  Without pre-allocation this creates two transient
  // Float32Array(pLen+n) objects per channel — at pLen≈512 and n≈30 000 that
  // is 2 × 122 KB × 37 channels = 9 MB of short-lived GC pressure per
  // preWarm cycle, matching the observed 58–108 ms 'setTimeout' violations.
  // These two buffers are grown lazily (one allocation when the buffer size
  // first exceeds current capacity) and then reused for all subsequent preWarm
  // cycles at that buffer size.
  private hpPrefixScratch: Float32Array = new Float32Array(0);
  private hpPrefixResultScratch: Float32Array = new Float32Array(0);

  // Pre-allocated scratch buffers for per-frame envelope rendering.
  // The envelope path allocates Float32Array/Float64Array/Uint16Array per pixel
  // per channel (37 channels × 4 arrays × 1200 px ≈ 800 KB) every frame.
  // Re-using these instance arrays and resetting them with typed fill() (a
  // near-free memset) eliminates virtually all of that per-frame GC pressure.
  private envMinYs: Float32Array = new Float32Array(2048);
  private envMaxYs: Float32Array = new Float32Array(2048);
  private envSumYs: Float64Array = new Float64Array(2048);
  private envCountPx: Uint16Array = new Uint16Array(2048);

  // Pre-allocated per-channel batch stats for batched waveform rendering.
  // Stores scalars computed in Phase 1 (band) for reuse in Phase 2 (mean)
  // without extra heap allocation between phases.
  private _batchDataMeans: Float64Array = new Float64Array(64);
  private _batchScales: Float64Array = new Float64Array(64);
  private _batchBaseYs: Float64Array = new Float64Array(64);
  private _batchTopBounds: Float64Array = new Float64Array(64);
  private _batchBotBounds: Float64Array = new Float64Array(64);
  private _batchGains: Float64Array = new Float64Array(64);
  private _batchSampleXOffsets: Float64Array = new Float64Array(64);
  private _batchSamplesPerPage: Float64Array = new Float64Array(64);
  private _batchEnvelopeIndexBases: Int32Array = new Int32Array(64);
  private _batchUseDisplayGeometry: Uint8Array = new Uint8Array(64);
  private _batchFixed: Uint8Array = new Uint8Array(64); // 1 = fixed-amplitude, 0 = auto
  // Object-reference arrays (can't use TypedArray for these).
  private _batchFiltered: (Float32Array | null)[] = [];
  private _batchRepData: (Float32Array | number[] | null)[] = []; // midpoint or filteredData
  private _batchEnvelopes: ({ min: Float32Array; max: Float32Array } | null)[] =
    [];

  // ── Envelope differential ring-buffer pool ───────────────────────────────
  // getMontageChannelEnvelope() for bipolar channels previously allocated two
  // new Float32Array(len) objects on every call (every rendered frame when
  // pyramid envelope data is present).  With 37 channels × 2 arrays × ~1200
  // samples that was ~350 KB of short-lived allocations per frame — a major
  // contributor to the 3 s of Major/Minor GC observed in the DevTools trace.
  // The pool keeps at most MAX_ENV_POOL_SIZE pairs; entries are keyed by
  // channel-pair string and reused by length, so no heap traffic on hot path.
  private static readonly MAX_ENV_POOL_SIZE = 128;
  private _envPool: Map<string, { min: Float32Array; max: Float32Array }> =
    new Map();

  /** Return or create a pooled {min, max} pair of length `len`. */
  private getPooledEnvPair(
    key: string,
    len: number,
  ): { min: Float32Array; max: Float32Array } {
    const existing = this._envPool.get(key);
    if (existing && existing.min.length === len) return existing;
    const pair = { min: new Float32Array(len), max: new Float32Array(len) };
    if (this._envPool.size >= EEGWaveformRenderer.MAX_ENV_POOL_SIZE) {
      // Evict the oldest entry to bound memory.
      const firstKey = this._envPool.keys().next().value;
      if (firstKey !== undefined) this._envPool.delete(firstKey);
    }
    this._envPool.set(key, pair);
    return pair;
  }

  private normalizeReferenceToken(name: string): string {
    return name
      .trim()
      .toUpperCase()
      .replace(/^(EEG|ECG|EMG|EOG)\s+/, "")
      .replace(/[^A-Z0-9]+/g, "");
  }

  private findChannelByInput(input?: string): EEGChannel | undefined {
    if (!input) return undefined;
    const exact = this.channels.find((channel) => channel.name === input);
    if (exact) return exact;
    const resolved = resolveRecordingChannelName(
      input,
      this.channels.map((channel) => channel.name),
    );
    if (!resolved) return undefined;
    return this.channels.find((channel) => channel.name === resolved);
  }

  private isAverageReferenceInput(name: string): boolean {
    return this.normalizeReferenceToken(name) === "AVG";
  }

  private isLinkedEarsReferenceInput(name: string): boolean {
    return this.normalizeReferenceToken(name) === "A1A2";
  }

  private isAuxiliaryChannelName(name: string): boolean {
    return /^(ECG|EKG|EMG|EOG)\b/.test(name.trim().toUpperCase());
  }

  private getAverageReferenceSourceChannels(): EEGChannel[] {
    return this.channels.filter((channel) => {
      if (this.isAuxiliaryChannelName(channel.name)) {
        return false;
      }

      const normalized = this.normalizeReferenceToken(channel.name);
      return (
        normalized.length > 0 && normalized !== "AVG" && normalized !== "A1A2"
      );
    });
  }

  private getLinkedEarsSourceChannels(): {
    left: EEGChannel | null;
    right: EEGChannel | null;
  } {
    let left: EEGChannel | null = null;
    let right: EEGChannel | null = null;

    for (const channel of this.channels) {
      const normalized = this.normalizeReferenceToken(channel.name);
      if (!left && (normalized === "A1" || normalized === "M1")) {
        left = channel;
        continue;
      }
      if (!right && (normalized === "A2" || normalized === "M2")) {
        right = channel;
      }
    }

    return { left, right };
  }

  private getVirtualReferenceData(referenceInput: string): Float32Array | null {
    const normalized = this.normalizeReferenceToken(referenceInput);
    const cacheKey = `__reference__:${normalized}`;
    const cached = this.derivationCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let result: Float32Array | null = null;

    if (normalized === "AVG") {
      const sourceChannels = this.getAverageReferenceSourceChannels();
      if (sourceChannels.length === 0) {
        return null;
      }

      const len = Math.min(
        ...sourceChannels.map((channel) => channel.data.length),
      );
      result = new Float32Array(len);
      for (let i = 0; i < len; i++) {
        let sum = 0;
        for (
          let channelIndex = 0;
          channelIndex < sourceChannels.length;
          channelIndex++
        ) {
          sum += sourceChannels[channelIndex].data[i] ?? 0;
        }
        result[i] = sum / sourceChannels.length;
      }
    } else if (normalized === "A1A2") {
      const { left, right } = this.getLinkedEarsSourceChannels();
      if (!left || !right) {
        return null;
      }

      const len = Math.min(left.data.length, right.data.length);
      result = new Float32Array(len);
      for (let i = 0; i < len; i++) {
        result[i] = ((left.data[i] ?? 0) + (right.data[i] ?? 0)) * 0.5;
      }
    }

    if (!result) {
      return null;
    }

    this.derivationCache.set(cacheKey, result);
    return result;
  }

  private getVirtualReferenceEnvelope(
    referenceInput: string,
  ): { min: Float32Array; max: Float32Array } | null {
    const normalized = this.normalizeReferenceToken(referenceInput);
    const cacheKey = `__reference_env__:${normalized}`;
    const cached = this.virtualReferenceEnvelopeCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let result: { min: Float32Array; max: Float32Array } | null = null;

    if (normalized === "AVG") {
      const sourceChannels = this.getAverageReferenceSourceChannels().filter(
        (channel) => !!channel.envelopeMin && !!channel.envelopeMax,
      ) as Array<
        EEGChannel & { envelopeMin: Float32Array; envelopeMax: Float32Array }
      >;
      if (sourceChannels.length === 0) {
        return null;
      }

      const len = Math.min(
        ...sourceChannels.map((channel) =>
          Math.min(channel.envelopeMin.length, channel.envelopeMax.length),
        ),
      );
      const pooled = this.getPooledEnvPair(cacheKey, len);
      for (let i = 0; i < len; i++) {
        let midSum = 0;
        let halfRangeSquareSum = 0;
        for (
          let channelIndex = 0;
          channelIndex < sourceChannels.length;
          channelIndex++
        ) {
          const channel = sourceChannels[channelIndex];
          const channelMin = channel.envelopeMin[i];
          const channelMax = channel.envelopeMax[i];
          const mid = (channelMin + channelMax) * 0.5;
          const halfRange = Math.max(0, (channelMax - channelMin) * 0.5);
          midSum += mid;
          halfRangeSquareSum += halfRange * halfRange;
        }
        const avgMid = midSum / sourceChannels.length;
        const avgHalfRange =
          Math.sqrt(halfRangeSquareSum) / sourceChannels.length;
        pooled.min[i] = avgMid - avgHalfRange;
        pooled.max[i] = avgMid + avgHalfRange;
      }
      result = pooled;
    } else if (normalized === "A1A2") {
      const { left, right } = this.getLinkedEarsSourceChannels();
      if (
        !left?.envelopeMin ||
        !left?.envelopeMax ||
        !right?.envelopeMin ||
        !right?.envelopeMax
      ) {
        return null;
      }

      const len = Math.min(
        left.envelopeMin.length,
        left.envelopeMax.length,
        right.envelopeMin.length,
        right.envelopeMax.length,
      );
      const pooled = this.getPooledEnvPair(cacheKey, len);
      for (let i = 0; i < len; i++) {
        const leftMin = left.envelopeMin[i];
        const leftMax = left.envelopeMax[i];
        const rightMin = right.envelopeMin[i];
        const rightMax = right.envelopeMax[i];
        const leftMid = (leftMin + leftMax) * 0.5;
        const rightMid = (rightMin + rightMax) * 0.5;
        const linkedMid = (leftMid + rightMid) * 0.5;
        const leftHalfRange = Math.max(0, (leftMax - leftMin) * 0.5);
        const rightHalfRange = Math.max(0, (rightMax - rightMin) * 0.5);
        const linkedHalfRange = Math.hypot(leftHalfRange, rightHalfRange) * 0.5;
        pooled.min[i] = linkedMid - linkedHalfRange;
        pooled.max[i] = linkedMid + linkedHalfRange;
      }
      result = pooled;
    }

    if (!result) {
      return null;
    }

    this.virtualReferenceEnvelopeCache.set(cacheKey, result);
    return result;
  }

  // ── WebGL GPU envelope path ────────────────────────────────────────────────
  // When config.rendererMode === "gpu" the envelope-band drawing (high-density
  // path, >1.5 samples/pixel) is offloaded to WebGL2 via WebGLRendererAdapter.
  // Only active on full redraws (!useBlit) — the blit scroll strip is tiny and
  // Canvas 2D is already fast for it.  Falls back to Canvas 2D automatically
  // when WebGL2 is unavailable (_webglAdapter.isSupported() === false).
  private _gpuCanvas: HTMLCanvasElement | null = null;
  private _webglAdapter: WebGLRendererAdapter | null = null;
  // Pre-allocated per-channel Y arrays for GPU envelope collection.
  // Indexed by channel position (ci). Grown lazily, never shrunk.
  // _gpuChanTopY[ci][px] = topmost pixel of envelope band (= maxYs in CPU terms)
  // _gpuChanBotY[ci][px] = bottommost pixel of envelope band (= minYs in CPU terms)
  private _gpuChanTopY: Float32Array[] = [];
  private _gpuChanBotY: Float32Array[] = [];

  // ── Scrolling blit cache (Fix G) ──────────────────────────────────────────
  // During continuous forward playback a frame only differs from the previous
  // one by a narrow right-hand strip (Δpx = Δt × width / timeScale, typically
  // 2–16px).  We capture the waveform layer into an off-screen canvas at the
  // end of each drawWaveforms() call and, on the next frame, blit the cached
  // image shifted left by Δpx so that only the new right strip needs to be
  // redrawn.  This reduces Canvas 2D path commands from ~84,000 to ~200 per
  // playback frame, dropping GPU compositor time from 90–125 ms to ~5–10 ms.
  private _blitCanvas: HTMLCanvasElement | null = null;
  private _blitCtx: CanvasRenderingContext2D | null = null;
  private _blitSnapCanvas: HTMLCanvasElement | null = null; // snapshot buffer for safe self-blit
  private _blitSnapCtx: CanvasRenderingContext2D | null = null;
  private _blitTime: number = -Infinity; // currentTime when blit was captured
  private _blitDirty: boolean = true; // forces a full redraw on next frame
  private _lastPaintLuma: number | null = null;
  private _paintSampleFrame = 0;
  private _postFlushTransitionFramesRemaining: number = 0;
  private _postFlushTransitionFrameOrdinal: number = 0;
  private _lastWaveformRenderMetrics: WaveformRenderMetrics | null = null;

  // Cached CSS layout dimensions pushed by the ResizeObserver via notifyResize().
  // Using these avoids reading canvas.clientWidth/clientHeight in the hot render
  // path, which forces a synchronous layout reflow on every RAF frame.
  private _cssWidth: number = 0;
  private _cssHeight: number = 0;

  // Timeline ruler dimensions - compact design
  private readonly RULER_HEIGHT = 12; // Compact 12px height
  private readonly RULER_MARGIN = 2; // Minimal margin
  private readonly MIN_CHANNEL_SPACING = 0.5;
  private readonly MAX_CHANNEL_SPACING = 3;
  private readonly MIN_LABEL_FONT_SIZE = 8;
  private readonly MAX_LABEL_FONT_SIZE = 24;
  private readonly MIN_WAVEFORM_THICKNESS = 0.5;
  private readonly MAX_WAVEFORM_THICKNESS = 6;

  constructor(canvas: HTMLCanvasElement, config: RenderConfig) {
    this.canvas = canvas;
    // willReadFrequently: paint-luma sampling uses getImageData periodically.
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      throw new Error("Canvas 2D context not supported");
    }
    this.ctx = ctx;
    this.config = config;

    if (config.rendererMode === "gpu" && typeof document !== "undefined") {
      const gpuCanvas = document.createElement("canvas");
      gpuCanvas.width = Math.max(1, canvas.width);
      gpuCanvas.height = Math.max(1, canvas.height);
      const adapter = new WebGLRendererAdapter(gpuCanvas);
      if (adapter.isSupported()) {
        this._gpuCanvas = gpuCanvas;
        this._webglAdapter = adapter;
      }
    }
  }

  /**
   * Load EEG data from channels
   */
  public loadChannels(channels: EEGChannel[], preserveBlitCache = false): void {
    this.channels = channels;
    this.virtualReferenceEnvelopeCache.clear();
    this.displayGeometryCache.clear();
    if (channels.length > 0) {
      this.sampleRate = channels[0].sampleRate;
    }
    if (this.pendingPreWarmDerivCache !== null) {
      // Pre-warm path: derivation cache already populated for these channels;
      // filter cache already has entries keyed on the new Float32Arrays.
      // WeakMap entries for old buffers are GC'd automatically — no need to
      // create a new WeakMap.  Just swap in the pre-warmed derivation cache.
      this.derivationCache = this.pendingPreWarmDerivCache;
      this.pendingPreWarmDerivCache = null;
    } else {
      // Cold path: new channels, old derivations are stale — clear both caches.
      this.filterCache = new WeakMap();
      this.derivationCache.clear();
    }
    // New channel data → blit cache is stale unless the caller guarantees
    // the new buffer covers the same visible time range (forward advance).
    if (!preserveBlitCache) {
      this._blitDirty = true;
    }
    if (preserveBlitCache) {
      this._postFlushTransitionFramesRemaining = 2;
      this._postFlushTransitionFrameOrdinal = 0;
    } else {
      this._postFlushTransitionFramesRemaining = 0;
      this._postFlushTransitionFrameOrdinal = 0;
    }
  }

  /**
   * Pre-compute derivations and filters for an incoming channel buffer while
   * it is still parked in the pending-buffer ref (between RAF ticks).  This
   * moves the ~80ms cold-cache cost out of the RAF callback and into the React
   * passive-effect scheduler, eliminating the per-buffer-swap RAF violation.
   *
   * The filter cache (WeakMap, keyed by Float32Array identity) can be populated
   * immediately — entries for new Float32Arrays are invisible to renders that
   * still use the old channel buffers, so there is zero correctness risk.
   * The derivation cache (string keys) is computed into a separate Map and
   * atomically swapped in by loadChannels() when the buffer is actually flushed.
   *
   * @param newChannels  Channel data from the pending buffer
   */
  public preWarmForChannels(
    newChannels: EEGChannel[],
    newWindowStart?: number,
  ): void {
    if (newChannels.length === 0 || this.montageChannels.length === 0) return;

    // Snapshot the current instance state we'll temporarily override.
    const prevChannels = this.channels;
    const prevDerivCache = this.derivationCache;

    // ── Overlap-save HP filter initialisation ────────────────────────────────
    // The centred moving-average high-pass filter computes
    //   result[i] = signal[i] - mean(signal[i−half .. i+half])
    // Without left-context at position 0, the first `half` samples
    // (= sampleRate/cutoffHz ≈ 512 for 0.5 Hz at 256 sps = 2 seconds) use a
    // truncated window, producing values that differ from what the same physical
    // EEG samples would yield when processed mid-window.  The blit-scroll path
    // then composites old (correct) blit pixels with new (edge-artefact) right-
    // strip pixels, causing the "wave pattern changes" discontinuity for ~2 s
    // after every buffer swap.
    //
    // Fix: before switching channels, extract the last `half` DC-removed samples
    // from each old-window derivation and pass them as `hpPrefix` to applyFilters.
    // The HP filter sees a full centred window at position 0 of the new buffer;
    // the prefix portion is discarded from the result.  After this the filterCache
    // holds the corrected result, so subsequent render calls are cache hits.
    const hpHalf =
      this.config.highPassFilter > 0
        ? Math.floor(this.sampleRate / this.config.highPassFilter)
        : 0;

    const hpTails: Array<Float32Array | undefined> = this.montageChannels.map(
      (mc) => {
        if (hpHalf <= 0 || prevChannels.length === 0) return undefined;

        // Obtain the raw derivation from the old window.
        let oldDeriv: Float32Array | null | undefined;
        if (mc.negativeInput) {
          // Bipolar: look up the cached derivation (computed on a previous render).
          oldDeriv = prevDerivCache.get(
            `${mc.positiveInput}|${mc.negativeInput}`,
          );
          if (!oldDeriv) {
            // Not yet cached — compute it while still on the old channels.
            oldDeriv = this.getMontageChannelData(mc);
          }
        } else {
          // Unipolar: raw channel data (not stored in derivationCache).
          const ch = prevChannels.find((c) => c.name === mc.positiveInput);
          oldDeriv = ch?.data;
        }
        if (!oldDeriv || oldDeriv.length === 0) return undefined;

        // Subtract the old window's DC mean so the tail baseline matches the
        // DC-removed new-window data it will be prepended to inside applyFilters.
        let oldMean = 0;
        if (this.config.removeDcOffset) {
          let s = 0;
          for (let j = 0; j < oldDeriv.length; j++) s += oldDeriv[j];
          oldMean = s / oldDeriv.length;
        }

        // Extract the tail from the position in the old buffer immediately
        // before newWindowStart, not from the end of the old buffer.
        // e.g. for 0-30 s → 10-40 s: tail ends at old-buffer sample 2560
        // (= t=10 s) giving context from t=8-10 s — the signal that flows
        // into the HP filter window at sample 0 of the new buffer.
        // Falls back to end-of-buffer when newWindowStart is not provided
        // (backward-compat: montage switches on the same time offset).
        const tailEndSample =
          newWindowStart !== undefined && this.sampleRate > 0
            ? Math.min(
                oldDeriv.length,
                Math.max(
                  0,
                  Math.round(
                    (newWindowStart - this.dataTimeOffset) * this.sampleRate,
                  ),
                ),
              )
            : oldDeriv.length;
        const tailLen = Math.min(hpHalf, tailEndSample);
        const tail = new Float32Array(tailLen);
        const tailStart = tailEndSample - tailLen;
        for (let j = 0; j < tailLen; j++) {
          tail[j] = oldDeriv[tailStart + j] - oldMean;
        }
        return tail;
      },
    );
    // ─────────────────────────────────────────────────────────────────────────

    // Switch to the new channels so getMontageChannelData() resolves against them.
    this.channels = newChannels;
    this.derivationCache = new Map<string, Float32Array>(); // fresh for new data

    const fullLen = newChannels[0].data.length;

    for (let i = 0; i < this.montageChannels.length; i++) {
      const mc = this.montageChannels[i];
      const derivedData = this.getMontageChannelData(mc);
      if (!derivedData) continue;
      // applyFilters writes the result into filterCache keyed on derivedData
      // (a new Float32Array that exists only in the new buffer — safe to pre-warm
      // now because the old channels still use different object references).
      // Pass the overlap-save HP prefix so edge artefacts are eliminated.
      this.applyFilters(derivedData, 0, fullLen, hpTails[i]);
    }

    // Save the freshly-warmed derivation cache for loadChannels() to install.
    this.pendingPreWarmDerivCache = this.derivationCache;

    // Restore the live state so ongoing renders keep working correctly.
    this.channels = prevChannels;
    this.derivationCache = prevDerivCache;
    if (process.env.NODE_ENV !== "production") {
      logger.debug("[EEGRenderer] preWarmForChannels complete", {
        channels: newChannels.length,
        cacheEntries: this.pendingPreWarmDerivCache.size,
      });
    }
  }

  /**
   * Async variant of preWarmForChannels that processes channels in batches
   * and yields via MessageChannel between batches. Large 30k-sample / 37-channel
   * buffers are auto-coarsened down to smaller batches, and the HP-tail work is
   * computed inside those batches instead of synchronously up front. This keeps
   * the passive-effect that schedules the warm path short and reduces long-task
   * spikes before the first yield.
   *
   * @param newChannels     Channel data for the incoming buffer.
   * @param newWindowStart  Start time of the new buffer (for HP-prefix alignment).
   * @param batchSize       Requested channels processed per yield.
   * @returns Promise that resolves when all channels are warmed.
   */
  public preWarmForChannelsAsync(
    newChannels: EEGChannel[],
    newWindowStart?: number,
    batchSize = 4,
  ): Promise<void> {
    if (newChannels.length === 0 || this.montageChannels.length === 0) {
      return Promise.resolve();
    }

    // Snapshot the current live state so HP-tail extraction and any
    // drawWaveforms() calls between yields always use the correct channels.
    const prevChannels = this.channels;
    const prevDerivCache = this.derivationCache;

    // Shared warm derivation cache written incrementally across batches.
    const warmDerivCache = new Map<string, Float32Array>();
    const fullLen = newChannels[0].data.length;
    const totalChannels = this.montageChannels.length;
    const effectiveBatchSize =
      totalChannels >= 32 || fullLen >= 20000
        ? Math.max(1, Math.min(batchSize, 2))
        : Math.max(1, batchSize);
    // Snapshot the montage to use consistently — a montage change between
    // batches should not use the partially-built warmDerivCache.
    const montageSnapshot = this.montageChannels.slice();
    const prevChannelDataByName = new Map(
      prevChannels.map((channel) => [channel.name, channel.data]),
    );
    const hpHalf =
      this.config.highPassFilter > 0
        ? Math.floor(this.sampleRate / this.config.highPassFilter)
        : 0;
    const getHpTailForMontageChannel = (
      mc: EEGMontageChannel,
    ): Float32Array | undefined => {
      if (hpHalf <= 0 || prevChannelDataByName.size === 0) return undefined;

      let oldDeriv: Float32Array | undefined;
      if (mc.negativeInput) {
        const derivationKey = `${mc.positiveInput}|${mc.negativeInput}`;
        oldDeriv = prevDerivCache.get(derivationKey);
        if (!oldDeriv) {
          const positiveData = prevChannelDataByName.get(mc.positiveInput);
          const negativeData = prevChannelDataByName.get(mc.negativeInput);
          if (!positiveData || !negativeData) return undefined;
          const derivationLength = Math.min(
            positiveData.length,
            negativeData.length,
          );
          oldDeriv = new Float32Array(derivationLength);
          for (let index = 0; index < derivationLength; index++) {
            oldDeriv[index] = positiveData[index] - negativeData[index];
          }
          prevDerivCache.set(derivationKey, oldDeriv);
        }
      } else {
        oldDeriv = prevChannelDataByName.get(mc.positiveInput);
      }

      if (!oldDeriv || oldDeriv.length === 0) return undefined;

      let oldMean = 0;
      if (this.config.removeDcOffset) {
        let sum = 0;
        for (let index = 0; index < oldDeriv.length; index++) {
          sum += oldDeriv[index];
        }
        oldMean = sum / oldDeriv.length;
      }

      const tailEndSample =
        newWindowStart !== undefined && this.sampleRate > 0
          ? Math.min(
              oldDeriv.length,
              Math.max(
                0,
                Math.round(
                  (newWindowStart - this.dataTimeOffset) * this.sampleRate,
                ),
              ),
            )
          : oldDeriv.length;
      const tailLen = Math.min(hpHalf, tailEndSample);
      const tail = new Float32Array(tailLen);
      const tailStart = tailEndSample - tailLen;
      for (let index = 0; index < tailLen; index++) {
        tail[index] = oldDeriv[tailStart + index] - oldMean;
      }

      return tail;
    };

    // MessageChannel gives a macrotask-level yield that runs AFTER any pending
    // RAF callbacks, unlike setTimeout(0) which may fire before paint.
    const yieldFrame = (): Promise<void> =>
      new Promise<void>((resolve) => {
        const { port1, port2 } = new MessageChannel();
        port1.onmessage = () => {
          port1.close();
          resolve();
        };
        port2.postMessage(null);
        port2.close();
      });

    return (async () => {
      for (let i = 0; i < totalChannels; i += effectiveBatchSize) {
        // Always yield BEFORE each batch (including the first) so that the
        // React passive-effects commit that calls preWarmForChannelsAsync
        // returns quickly (~1ms) instead of blocking for the synchronous
        // first-batch filter work (~30-65ms on 37+ ch @ 30k samples).
        await yieldFrame();

        // If the renderer swapped to new channels while we were yielding
        // (e.g. seek or recording change), discard this warm work.
        if (this.channels !== prevChannels) {
          return;
        }

        // ── Process one batch synchronously ──────────────────────────────────
        // Temporarily swap to newChannels/warmDerivCache so getMontageChannelData
        // and applyFilters see the incoming buffer.  Restore immediately after
        // the batch so any drawWaveforms() between yields uses the live state.
        this.channels = newChannels;
        this.derivationCache = warmDerivCache;

        const end = Math.min(i + effectiveBatchSize, totalChannels);
        for (let ci = i; ci < end; ci++) {
          const mc = montageSnapshot[ci];
          const derivedData = this.getMontageChannelData(mc);
          if (!derivedData) continue;
          this.applyFilters(
            derivedData,
            0,
            fullLen,
            getHpTailForMontageChannel(mc),
          );
        }

        // Restore live state immediately so drawWaveforms() uses correct channels.
        this.channels = prevChannels;
        this.derivationCache = prevDerivCache;
        // ─────────────────────────────────────────────────────────────────────
      }

      // All batches complete — install the warmed derivation cache.
      // Guard: skip if the renderer was replaced with new channels while we
      // were processing (e.g. user seeked or changed recording).  We detect
      // this by checking that the renderer's channels are still the same prev
      // snapshot we computed HP-tails from — if they changed, the warm cache
      // is out of date and should be discarded.
      if (this.channels === prevChannels) {
        this.pendingPreWarmDerivCache = warmDerivCache;
      }

      if (process.env.NODE_ENV !== "production") {
        logger.debug("[EEGRenderer] preWarmForChannels complete", {
          channels: newChannels.length,
          cacheEntries: warmDerivCache.size,
        });
      }
    })();
  }

  /**
   * Set montage configuration
   */
  public setMontage(montageChannels: EEGMontageChannel[]): void {
    this.montageChannels = montageChannels;
    this._blitDirty = true;
  }

  /**
   * Update render configuration
   */
  public updateConfig(config: Partial<RenderConfig>): void {
    const prev = this.config;
    this.config = { ...this.config, ...config };
    if (
      config.timeScale !== undefined &&
      Number.isFinite(this.config.timeScale)
    ) {
      const scale = Math.max(0, this.config.timeScale);
      this.pagePlayheadOffsetSeconds = Math.max(
        0,
        Math.min(scale, this.pagePlayheadOffsetSeconds),
      );
    }
    // Invalidate filter cache when any filter setting changes
    if (
      (config.highPassFilter !== undefined &&
        config.highPassFilter !== prev.highPassFilter) ||
      (config.lowPassFilter !== undefined &&
        config.lowPassFilter !== prev.lowPassFilter) ||
      (config.notchFilter !== undefined &&
        config.notchFilter !== prev.notchFilter) ||
      (config.notch50Hz !== undefined && config.notch50Hz !== prev.notch50Hz) ||
      (config.removeDcOffset !== undefined &&
        config.removeDcOffset !== prev.removeDcOffset)
    ) {
      this.filterCache = new WeakMap();
    }
    // Only invalidate the blit cache when at least one value actually changed.
    // No-op updateConfig calls (same effective values) during playback would
    // otherwise force a full 30s canvas redraw on the very next frame.
    //
    // Object-typed fields (channelGainOverrides, channelPositionOffsets,
    // channelTypeColors) are compared by shallow content rather than reference,
    // because the caller passes a new object literal on every React render even
    // when nothing inside changed.  A reference-only comparison would mark
    // every config push as a visual change and invalidate the blit cache on
    // every render, causing a full-canvas redraw that visually rescales waveform
    // amplitudes and channel positions mid-playback.
    const SHALLOW_COMPARE_FIELDS = new Set<keyof RenderConfig>([
      "channelGainOverrides",
      "channelPositionOffsets",
      "channelTypeColors",
    ]);
    const shallowObjectEqual = (
      a: Record<string, unknown> | undefined,
      b: Record<string, unknown> | undefined,
    ): boolean => {
      if (a === b) return true;
      if (!a || !b) return false;
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      return aKeys.every((k) => a[k] === b[k]);
    };
    const NON_VISUAL_CONFIG_FIELDS = new Set<keyof RenderConfig>([
      "playbackSpeed",
      "playbackTransportMode",
      "minimalStartupPaint",
    ]);
    const hasVisualChange = (
      Object.keys(config) as Array<keyof RenderConfig>
    ).some((k) => {
      if (NON_VISUAL_CONFIG_FIELDS.has(k)) {
        return false;
      }
      if (SHALLOW_COMPARE_FIELDS.has(k)) {
        return !shallowObjectEqual(
          config[k] as Record<string, unknown> | undefined,
          prev[k] as Record<string, unknown> | undefined,
        );
      }
      return config[k] !== prev[k];
    });
    if (hasVisualChange) {
      this._blitDirty = true;
    }
  }

  /**
   * Set current time position
   */
  public setCurrentTime(time: number): void {
    this.currentTime = time;
  }

  /**
   * Set selected montage channel labels for visual emphasis.
   */
  public setSelectedMontageChannelLabels(labels: string[]): void {
    this.selectedMontageChannelLabels = new Set(
      (labels || []).map((label) => label.trim()).filter(Boolean),
    );
  }

  public setCrosshairPosition(x: number, y: number): void {
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      this.crosshairPosition = null;
      return;
    }
    this.crosshairPosition = { x, y };
  }

  public clearCrosshair(): void {
    this.crosshairPosition = null;
  }

  public setPinnedCursorTime(time: number | null): void {
    if (time === null || !Number.isFinite(time)) {
      this.pinnedCursorTime = null;
      return;
    }
    this.pinnedCursorTime = Math.max(0, time);
  }

  public setPagePlayheadOffsetSeconds(offsetSeconds: number): void {
    const scale = Math.max(0, this.config.timeScale || 0);
    if (!Number.isFinite(offsetSeconds) || scale <= 0) {
      this.pagePlayheadOffsetSeconds = 0;
      return;
    }
    this.pagePlayheadOffsetSeconds = Math.max(
      0,
      Math.min(scale, offsetSeconds),
    );
  }

  public getPagePlayheadOffsetSeconds(): number {
    return this.pagePlayheadOffsetSeconds;
  }

  public getPagePlayheadCanvasX(): number {
    const plot = this.getPlotGeometry();
    const scale = Math.max(0, this.config.timeScale || 0);
    if (plot.canvasWidth <= 0 || scale <= 0) return plot.left;
    return (
      plot.left + (this.pagePlayheadOffsetSeconds / scale) * plot.width
    );
  }

  public canvasXToRelativeSeconds(canvasX: number): number {
    const plot = this.getPlotGeometry();
    const scale = Math.max(0.001, this.config.timeScale || 10);
    if (plot.width <= 0) return 0;
    const x = Math.max(plot.left, Math.min(plot.right, canvasX));
    return ((x - plot.left) / plot.width) * scale;
  }

  private getPolarityMultiplier(): number {
    return this.config.polarity === "positive-up" ? 1 : -1;
  }

  /**
   * Extract "R, G, B" string from this.config.gridColor (#RRGGBBAA format).
   * Used by drawClinicalGrid so it respects the configured theme grid colour
   * (dark=#444444, light=#94a3b8, high_contrast=#ffffff) instead of hardcoding.
   */
  private parseGridColorRgb(): string {
    const c = (this.config.gridColor || "").trim();
    if (c.startsWith("#") && c.length >= 7) {
      const r = parseInt(c.slice(1, 3), 16);
      const g = parseInt(c.slice(3, 5), 16);
      const b = parseInt(c.slice(5, 7), 16);
      if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
        return `${r}, ${g}, ${b}`;
      }
    }
    return this.isBackgroundDark() ? "93, 101, 110" : "30, 41, 59";
  }

  private parseGridColorAlpha(): number {
    const color = (this.config.gridColor || "").trim();
    if (color.startsWith("#")) {
      if (color.length >= 9) {
        const alpha = parseInt(color.slice(7, 9), 16);
        if (Number.isFinite(alpha)) {
          return Math.max(0.05, Math.min(1, alpha / 255));
        }
      }

      if (color.length === 5) {
        const alphaHex = color.slice(4, 5);
        const alpha = parseInt(alphaHex + alphaHex, 16);
        if (Number.isFinite(alpha)) {
          return Math.max(0.05, Math.min(1, alpha / 255));
        }
      }
    }

    const rgbaMatch = color.match(/rgba\([^,]+,[^,]+,[^,]+,\s*([0-9.]+)\s*\)/i);
    if (rgbaMatch) {
      const alpha = Number(rgbaMatch[1]);
      if (Number.isFinite(alpha)) {
        return Math.max(0.05, Math.min(1, alpha));
      }
    }

    return 1;
  }

  private getOverlayLabelColor(dimmed = false): string {
    if (!this.isBackgroundDark()) {
      return dimmed ? "#4b5563" : "#0f172a";
    }
    return dimmed ? "#d1d5db" : "#ffffff";
  }

  private getSelectedOverlayLabelColor(): string {
    // Strong clinical accent so selection is distinct from body labels and
    // from annotation chrome in the plot area (Trackit-style affordance).
    return this.isBackgroundDark() ? "#7dd3fc" : "#0052a3";
  }

  private getSelectedChannelRailColor(): string {
    return this.isBackgroundDark()
      ? "rgba(125, 211, 252, 0.95)"
      : "rgba(0, 82, 163, 0.95)";
  }

  private getSelectedChannelLaneFillColor(): string {
    return this.isBackgroundDark()
      ? "rgba(125, 211, 252, 0.14)"
      : "rgba(0, 102, 204, 0.12)";
  }

  private isBackgroundDark(): boolean {
    const color = (this.config.backgroundColor || "").trim();
    if (color.startsWith("#")) {
      const hex = color.slice(1);
      const rgbHex =
        hex.length === 3
          ? hex
              .split("")
              .map((char) => char + char)
              .join("")
          : hex.length >= 6
            ? hex.slice(0, 6)
            : "";
      if (rgbHex.length === 6) {
        const r = parseInt(rgbHex.slice(0, 2), 16);
        const g = parseInt(rgbHex.slice(2, 4), 16);
        const b = parseInt(rgbHex.slice(4, 6), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
          const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
          return luminance < 0.5;
        }
      }
      return true;
    }

    const rgbMatch = color.match(
      /rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i,
    );
    if (rgbMatch) {
      const r = Number(rgbMatch[1]);
      const g = Number(rgbMatch[2]);
      const b = Number(rgbMatch[3]);
      if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
        const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        return luminance < 0.5;
      }
    }

    return true;
  }

  private getChannelGainMultiplier(channelLabel: string): number {
    const source = this.config.channelGainOverrides;
    if (!source || typeof source !== "object") return 1;
    const raw = source[channelLabel];
    if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return 1;
    return Math.max(0.05, Math.min(20, raw));
  }

  private getMontageChannelValueAtSample(
    montageChannel: EEGMontageChannel,
    sampleIndex: number,
  ): number | null {
    if (!Number.isInteger(sampleIndex) || sampleIndex < 0) return null;

    const positiveChannel = this.findChannelByInput(montageChannel.positiveInput);
    if (!positiveChannel || sampleIndex >= positiveChannel.data.length) {
      return null;
    }

    let value = positiveChannel.data[sampleIndex];
    if (montageChannel.negativeInput) {
      const negInput = montageChannel.negativeInput;
      let laplacianNeighborMap: Record<string, string[]> | null = null;
      let lapElectrode = "";

      if (negInput.startsWith("EEG LAP_")) {
        lapElectrode = negInput.slice(8).toUpperCase();
        laplacianNeighborMap = LAPLACIAN_SMALL_NEIGHBORS;
      } else if (negInput.startsWith("EEG LLAP_")) {
        lapElectrode = negInput.slice(9).toUpperCase();
        laplacianNeighborMap = LAPLACIAN_LARGE_NEIGHBORS;
      } else if (negInput.startsWith("EEG HJR_")) {
        lapElectrode = negInput.slice(8).toUpperCase();
        laplacianNeighborMap = HJORTH_NEIGHBORS;
      }

      if (laplacianNeighborMap && lapElectrode) {
        const neighborNames = laplacianNeighborMap[lapElectrode] ?? [];
        const neighborChannels = neighborNames
          .map((n) => this.findChannelByInput(n))
          .filter((ch): ch is NonNullable<typeof ch> => ch !== undefined);
        if (neighborChannels.length > 0) {
          let neighborSum = 0;
          for (const nch of neighborChannels)
            neighborSum += nch.data[sampleIndex] ?? 0;
          value -= neighborSum / neighborChannels.length;
        }
      } else {
        const negativeChannel = this.findChannelByInput(negInput);
        if (negativeChannel && sampleIndex < negativeChannel.data.length) {
          value -= negativeChannel.data[sampleIndex];
        }
      }
    }

    return Number.isFinite(value) ? value : null;
  }

  private getChannelOffsetUnits(channelLabel: string): number {
    const source = this.config.channelPositionOffsets;
    if (!source || typeof source !== "object") return 0;
    const raw = source[channelLabel];
    if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
    // Safety clamp; DB policy should already bound this.
    return Math.max(-2, Math.min(2, raw));
  }

  private getSafeChannelSpacing(): number {
    const raw = this.config.channelSpacing;
    if (typeof raw !== "number" || !Number.isFinite(raw)) {
      return 1;
    }
    return Math.max(
      this.MIN_CHANNEL_SPACING,
      Math.min(this.MAX_CHANNEL_SPACING, raw),
    );
  }

  private getSafeLabelFontSize(): number {
    const raw = this.config.labelFontSize;
    if (typeof raw !== "number" || !Number.isFinite(raw)) {
      return 12;
    }
    return Math.max(
      this.MIN_LABEL_FONT_SIZE,
      Math.min(this.MAX_LABEL_FONT_SIZE, raw),
    );
  }

  private getSafeWaveformThickness(): number {
    const raw = this.config.waveformThickness;
    if (typeof raw !== "number" || !Number.isFinite(raw)) {
      return 1;
    }
    return Math.max(
      this.MIN_WAVEFORM_THICKNESS,
      Math.min(this.MAX_WAVEFORM_THICKNESS, raw),
    );
  }

  private getSafePixelsPerMm(): number {
    const raw = this.config.pixelsPerMm;
    if (typeof raw !== "number" || !Number.isFinite(raw)) {
      return DEFAULT_PIXELS_PER_MM;
    }
    return Math.max(MIN_PIXELS_PER_MM, Math.min(MAX_PIXELS_PER_MM, raw));
  }

  private inferWaveformChannelType(
    label: string,
  ): "EEG" | "ECG" | "EMG" | "EOG" | "ANNOTATION" | "OTHER" {
    const normalized = label.toUpperCase();
    if (normalized.includes("ANNOT") || normalized.includes("MARKER")) {
      return "ANNOTATION";
    }
    if (normalized.includes("ECG") || normalized.includes("EKG")) {
      return "ECG";
    }
    if (normalized.includes("EMG")) {
      return "EMG";
    }
    if (normalized.includes("EOG")) {
      return "EOG";
    }
    if (
      normalized.includes("EEG") ||
      /(^|[\s\-/])(FP|F|C|P|O|T|A|CZ|FZ|PZ|T[1-8]|X[1-9]|PG[12])([\s\-/]|$)/.test(
        normalized,
      )
    ) {
      return "EEG";
    }
    return "OTHER";
  }

  private getWaveformStrokeColor(
    montageChannel: EEGMontageChannel,
    channelIndex: number,
  ): string {
    const explicitChannelColor = this.normalizeStrokeColor(
      montageChannel.color,
    );
    if (explicitChannelColor) {
      return explicitChannelColor;
    }

    const mode = this.config.channelColorMode || "uniform";
    if (mode === "alternating") {
      return channelIndex % 2 === 0 ? "#f8fafc" : "#67e8f9";
    }
    if (mode === "by-type") {
      const inferredType = this.inferWaveformChannelType(montageChannel.label);
      const typePaletteColor = this.normalizeStrokeColor(
        this.config.channelTypeColors?.[inferredType],
      );
      if (typePaletteColor) {
        return typePaletteColor;
      }
    }
    return this.config.waveformColor;
  }

  private normalizeStrokeColor(color?: string): string | null {
    if (typeof color !== "string") return null;
    const value = color.trim();
    return value.length > 0 ? value : null;
  }

  private getChannelBaselineY(
    channelIndex: number,
    channelLabel: string,
    channelHeight: number,
    waveformAreaTop: number,
    waveformAreaBottom: number,
  ): number {
    const offsetUnits = this.getChannelOffsetUnits(channelLabel);
    const nominalY =
      waveformAreaTop + (channelIndex + 0.5 + offsetUnits) * channelHeight;
    const padding = Math.max(6, channelHeight * 0.18);
    return Math.max(
      waveformAreaTop + padding,
      Math.min(waveformAreaBottom - padding, nominalY),
    );
  }

  private getSafeDisplayMode(): "stacked" | "butterfly" {
    return this.config.displayMode === "butterfly" ? "butterfly" : "stacked";
  }

  private getLayoutVisibleChannelCount(visibleChannelCount: number): number {
    if (visibleChannelCount <= 0) return 0;
    return this.getSafeDisplayMode() === "butterfly" ? 1 : visibleChannelCount;
  }

  private getWaveformBaselineY(
    channelIndex: number,
    channelLabel: string,
    channelHeight: number,
    waveformAreaTop: number,
    waveformAreaBottom: number,
  ): number {
    if (this.getSafeDisplayMode() === "butterfly") {
      return waveformAreaTop + (waveformAreaBottom - waveformAreaTop) / 2;
    }

    return this.getChannelBaselineY(
      channelIndex,
      channelLabel,
      channelHeight,
      waveformAreaTop,
      waveformAreaBottom,
    );
  }

  private getChannelVerticalBounds(
    channelIndex: number,
    channelHeight: number,
    waveformAreaTop: number,
    waveformAreaBottom: number,
  ): { top: number; bottom: number } {
    if (this.getSafeDisplayMode() === "butterfly") {
      return {
        top: waveformAreaTop,
        bottom: waveformAreaBottom,
      };
    }

    const top = waveformAreaTop + channelIndex * channelHeight;
    return {
      top,
      bottom: Math.min(waveformAreaBottom, top + channelHeight),
    };
  }

  private getWaveformVerticalBounds(
    baseY: number,
    channelHeight: number,
    waveformAreaTop: number,
    waveformAreaHeight: number,
    spacingMultiplier: number,
    fixedAmplitudeScaling: boolean,
  ): {
    usableHeight: number;
    channelTopBound: number;
    channelBottomBound: number;
  } {
    const waveformAreaBottom = waveformAreaTop + waveformAreaHeight;

    if (this.getSafeDisplayMode() === "butterfly") {
      return {
        usableHeight: waveformAreaHeight * (fixedAmplitudeScaling ? 0.96 : 0.8),
        channelTopBound: waveformAreaTop,
        channelBottomBound: waveformAreaBottom,
      };
    }

    const usableHeight =
      channelHeight * (fixedAmplitudeScaling ? 0.96 : 0.8) * spacingMultiplier;

    return {
      usableHeight,
      channelTopBound: Math.max(waveformAreaTop, baseY - usableHeight / 2),
      channelBottomBound: Math.min(
        waveformAreaBottom,
        baseY + usableHeight / 2,
      ),
    };
  }

  private getVisibleMontageChannels(): Array<{
    channel: EEGMontageChannel;
    index: number;
  }> {
    const totalChannels = this.montageChannels.length;
    if (totalChannels === 0) {
      return [];
    }
    const requestedCount = Number.isFinite(this.config.visibleChannelCount)
      ? Math.floor(this.config.visibleChannelCount as number)
      : totalChannels;
    const visibleCount = Math.max(1, Math.min(totalChannels, requestedCount));
    const maxOffset = Math.max(0, totalChannels - visibleCount);
    const requestedOffset = Number.isFinite(this.config.channelScrollOffset)
      ? Math.floor(this.config.channelScrollOffset as number)
      : 0;
    const clampedOffset = Math.max(0, Math.min(maxOffset, requestedOffset));
    const visible = this.montageChannels.slice(
      clampedOffset,
      clampedOffset + visibleCount,
    );
    return visible.map((channel, idx) => ({
      channel,
      index: clampedOffset + idx,
    }));
  }

  /**
   * Hit-test a montage channel using a canvas Y coordinate.
   */
  public getMontageChannelAtCanvasY(canvasY: number): {
    channel: EEGMontageChannel;
    index: number;
  } | null {
    if (!Number.isFinite(canvasY) || this.montageChannels.length === 0) {
      return null;
    }

    const visibleMontageChannels = this.getVisibleMontageChannels();
    if (visibleMontageChannels.length === 0) {
      return null;
    }
    if (this.getSafeDisplayMode() === "butterfly") {
      return null;
    }

    const waveformArea = this.getWaveformArea();
    const waveformAreaHeight = waveformArea.height - 2;
    if (canvasY < waveformArea.top || canvasY > waveformArea.bottom) {
      return null;
    }

    const layoutVisibleChannelCount = this.getLayoutVisibleChannelCount(
      visibleMontageChannels.length,
    );
    const channelHeight = waveformAreaHeight / layoutVisibleChannelCount;
    if (!Number.isFinite(channelHeight) || channelHeight <= 0) {
      return null;
    }

    let bestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;

    visibleMontageChannels.forEach(({ channel, index }, visibleIndex) => {
      const baselineY = this.getWaveformBaselineY(
        visibleIndex,
        channel.label,
        channelHeight,
        waveformArea.top,
        waveformArea.bottom,
      );
      const distance = Math.abs(canvasY - baselineY);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    if (bestIndex < 0 || bestDistance > channelHeight * 0.8) {
      return null;
    }
    const hit = visibleMontageChannels.find((item) => item.index === bestIndex);
    if (!hit) return null;
    return { channel: hit.channel, index: hit.index };
  }

  public getMontageChannelHitAtCanvasPoint(
    canvasX: number,
    canvasY: number,
  ): {
    channel: EEGMontageChannel;
    index: number;
    traceY: number;
  } | null {
    if (
      !Number.isFinite(canvasX) ||
      !Number.isFinite(canvasY) ||
      this.montageChannels.length === 0
    ) {
      return null;
    }

    if (this.getSafeDisplayMode() !== "butterfly") {
      const hit = this.getMontageChannelAtCanvasY(canvasY);
      return hit ? { ...hit, traceY: canvasY } : null;
    }

    const visibleMontageChannels = this.getVisibleMontageChannels();
    if (visibleMontageChannels.length === 0) {
      return null;
    }

    const waveformArea = this.getWaveformArea();
    const waveformAreaHeight = Math.max(0, waveformArea.height - 2);
    if (waveformAreaHeight <= 0) {
      return null;
    }
    if (canvasY < waveformArea.top || canvasY > waveformArea.bottom) {
      return null;
    }

    const width = Math.max(1, this.canvas.width);
    const clampedX = Math.max(0, Math.min(width, canvasX));
    const hitTime =
      this.currentTime + this.canvasXToRelativeSeconds(clampedX);
    const absoluteSampleIndex = Math.round(
      (hitTime - this.dataTimeOffset) * this.sampleRate,
    );
    if (!Number.isInteger(absoluteSampleIndex) || absoluteSampleIndex < 0) {
      return null;
    }

    const startSample = Math.max(
      0,
      Math.floor((this.currentTime - this.dataTimeOffset) * this.sampleRate),
    );
    const relativeSampleIndex = absoluteSampleIndex - startSample;
    if (relativeSampleIndex < 0) {
      return null;
    }

    const polarityMultiplier = this.getPolarityMultiplier();
    const maxDistancePx = Math.max(8, Math.min(24, waveformAreaHeight * 0.08));
    let bestHit: {
      channel: EEGMontageChannel;
      index: number;
      traceY: number;
      distance: number;
    } | null = null;

    for (
      let visibleIndex = 0;
      visibleIndex < visibleMontageChannels.length;
      visibleIndex += 1
    ) {
      const { channel, index } = visibleMontageChannels[visibleIndex];
      const filteredData = this._batchFiltered[visibleIndex];
      const repData = this._batchRepData[visibleIndex] ?? filteredData;
      if (!repData || relativeSampleIndex >= repData.length) {
        continue;
      }

      const value = repData[relativeSampleIndex];
      if (!Number.isFinite(value)) {
        continue;
      }

      const fixedAmplitudeScaling = this._batchFixed[visibleIndex] === 1;
      const normalizedValue = fixedAmplitudeScaling
        ? value
        : value - this._batchDataMeans[visibleIndex];
      const orientedValue =
        normalizedValue * polarityMultiplier * this._batchGains[visibleIndex];
      const rawY =
        this._batchBaseYs[visibleIndex] -
        orientedValue * this._batchScales[visibleIndex];
      const traceY = Math.max(
        this._batchTopBounds[visibleIndex],
        Math.min(this._batchBotBounds[visibleIndex], rawY),
      );
      const distance = Math.abs(canvasY - traceY);

      if (!bestHit || distance < bestHit.distance) {
        bestHit = {
          channel,
          index,
          traceY,
          distance,
        };
      }
    }

    if (!bestHit || bestHit.distance > maxDistancePx) {
      return null;
    }

    return {
      channel: bestHit.channel,
      index: bestHit.index,
      traceY: bestHit.traceY,
    };
  }

  public getViewportMetrics(): RenderViewportMetrics {
    const waveformArea = this.getWaveformArea();
    const waveformAreaHeight = Math.max(0, waveformArea.height - 2);
    const visibleMontageChannelCount = this.getVisibleMontageChannels().length;
    const visibleChannelCount = this.getLayoutVisibleChannelCount(
      visibleMontageChannelCount,
    );

    const plot = this.getPlotGeometry();
    return {
      waveformAreaTop: waveformArea.top,
      waveformAreaBottom: waveformArea.top + waveformAreaHeight,
      waveformAreaHeight,
      visibleChannelCount,
      channelHeight:
        visibleChannelCount > 0
          ? waveformAreaHeight / visibleChannelCount
          : waveformAreaHeight,
      pixelsPerMm: this.getSafePixelsPerMm(),
      plotLeft: plot.left,
      plotWidth: plot.width,
    };
  }

  public getSelectionOverlayBoundsDisplayCss(
    displayHeight: number,
    channelLabels: string[],
  ): { top: number; height: number } {
    const canvasHeight = Math.max(1, this.canvas.height);
    const scaleY = displayHeight / canvasHeight;
    const visibleMontageChannels = this.getVisibleMontageChannels();

    if (
      channelLabels.length === 0 ||
      channelLabels.length >= visibleMontageChannels.length
    ) {
      return { top: 0, height: displayHeight };
    }

    const normalizedRequested = channelLabels.map((label) =>
      label.trim().toLowerCase(),
    );
    const matchesChannel = (label: string) => {
      const normalizedLabel = label.trim().toLowerCase();
      return normalizedRequested.some((requested) => {
        if (requested === normalizedLabel) {
          return true;
        }
        if (
          requested.includes(normalizedLabel) ||
          normalizedLabel.includes(requested)
        ) {
          return true;
        }
        const stripPrefix = (value: string) =>
          value.replace(/^(eeg|emg|eog|ecg)\s+/, "").trim();
        const strippedRequested = stripPrefix(requested);
        const strippedLabel = stripPrefix(normalizedLabel);
        return (
          strippedRequested === strippedLabel ||
          strippedRequested.includes(strippedLabel) ||
          strippedLabel.includes(strippedRequested)
        );
      });
    };

    const metrics = this.getViewportMetrics();
    let minTop = Number.POSITIVE_INFINITY;
    let maxBottom = Number.NEGATIVE_INFINITY;

    visibleMontageChannels.forEach(({ channel }, visibleIndex) => {
      if (!matchesChannel(channel.label)) {
        return;
      }
      const bounds = this.getChannelVerticalBounds(
        visibleIndex,
        metrics.channelHeight,
        metrics.waveformAreaTop,
        metrics.waveformAreaBottom,
      );
      minTop = Math.min(minTop, bounds.top);
      maxBottom = Math.max(maxBottom, bounds.bottom);
    });

    if (!Number.isFinite(minTop) || !Number.isFinite(maxBottom)) {
      return { top: 0, height: displayHeight };
    }

    return {
      top: minTop * scaleY,
      height: Math.max(2, (maxBottom - minTop) * scaleY),
    };
  }

  public getMontageChannelLabelsInDisplayYRange(
    displayTop: number,
    displayBottom: number,
    displayHeight: number,
  ): string[] {
    const canvasHeight = Math.max(1, this.canvas.height);
    const scaleY = displayHeight / canvasHeight;
    const rangeTop = Math.min(displayTop, displayBottom);
    const rangeBottom = Math.max(displayTop, displayBottom);
    if (rangeBottom - rangeTop < 1) {
      return [];
    }

    const canvasRangeTop = rangeTop / scaleY;
    const canvasRangeBottom = rangeBottom / scaleY;
    const metrics = this.getViewportMetrics();
    const visibleMontageChannels = this.getVisibleMontageChannels();
    const labels: string[] = [];

    visibleMontageChannels.forEach(({ channel }, visibleIndex) => {
      const bounds = this.getChannelVerticalBounds(
        visibleIndex,
        metrics.channelHeight,
        metrics.waveformAreaTop,
        metrics.waveformAreaBottom,
      );
      const intersects =
        bounds.bottom > canvasRangeTop && bounds.top < canvasRangeBottom;
      if (intersects) {
        labels.push(channel.label);
      }
    });

    return labels;
  }

  public snapCaliperPoint(input: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): {
    x: number;
    y: number;
    timeMs: number;
    channelIndex: number;
    channelLabel: string;
  } | null {
    if (input.width <= 0 || input.height <= 0 || this.canvas.width <= 0) {
      return null;
    }
    const canvasX = (input.x / input.width) * this.canvas.width;
    const canvasY = (input.y / input.height) * this.canvas.height;
    const hit = this.getMontageChannelAtCanvasY(canvasY);
    if (!hit) {
      return null;
    }
    const series = this.getMontageChannelData(hit.channel);
    if (!series || series.length === 0 || this.sampleRate <= 0) {
      return null;
    }
    const timeScale = Math.max(0.001, this.config.timeScale || 10);
    const plot = this.getPlotGeometry();
    const pageTimeSeconds = this.canvasXToRelativeSeconds(canvasX);
    const absoluteTime = this.currentTime + pageTimeSeconds;
    const centerIndex = Math.round(
      (absoluteTime - this.dataTimeOffset) * this.sampleRate,
    );
    const windowSamples = Math.max(1, Math.round(0.03 * this.sampleRate));
    const peakIndex = findPeakSampleIndex(series, centerIndex, windowSamples);
    const peakAbsTime = this.dataTimeOffset + peakIndex / this.sampleRate;
    const peakPageSeconds = Math.max(
      0,
      Math.min(timeScale, peakAbsTime - this.currentTime),
    );
    const snappedCanvasX =
      plot.left + (peakPageSeconds / timeScale) * plot.width;
    const visible = this.getVisibleMontageChannels();
    const visibleIndex = visible.findIndex((item) => item.index === hit.index);
    const layoutIndex = visibleIndex >= 0 ? visibleIndex : 0;
    const metrics = this.getViewportMetrics();
    const baseY = this.getWaveformBaselineY(
      layoutIndex,
      hit.channel.label,
      metrics.channelHeight,
      metrics.waveformAreaTop,
      metrics.waveformAreaBottom,
    );
    const pxPerUv =
      this.getSafePixelsPerMm() /
      Math.max(0.1, this.config.sensitivity || BASELINE_SENSITIVITY_UV_PER_MM);
    const oriented =
      (series[peakIndex] ?? 0) *
      this.getPolarityMultiplier() *
      this.getChannelGainMultiplier(hit.channel.label);
    const snappedCanvasY = baseY - oriented * pxPerUv;
    const scaleX = input.width / this.canvas.width;
    const scaleY = input.height / this.canvas.height;
    return {
      x: snappedCanvasX * scaleX,
      y: snappedCanvasY * scaleY,
      timeMs: peakPageSeconds * 1000,
      channelIndex: layoutIndex,
      channelLabel: hit.channel.label,
    };
  }

  /**
   * Replace the canvas element the renderer draws on.
   * Required because React may replace the underlying DOM element during
   * reconciliation (e.g. when parent state changes), leaving the renderer
   * with a stale reference to a detached node.
   */
  public setCanvas(canvas: HTMLCanvasElement): void {
    if (canvas === this.canvas) return;
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context not supported");
    }
    this.ctx = ctx;
    // Canvas element replaced → blit canvas dimensions are wrong.
    this._blitDirty = true;
  }

  /**
   * Set the time offset of the currently loaded data.
   * Sample index 0 in loaded channels corresponds to this time (in seconds).
   * Used to correctly map absolute currentTime to sample indices when data
   * doesn't start at t=0 (e.g., buffered window starting at 20s).
   */
  public setDataTimeOffset(offset: number, preserveBlitCache = false): void {
    this.dataTimeOffset = offset;
    // New data window means the blit cache is no longer valid — pixel columns
    // no longer correspond to the same time positions.
    // Exception: forward-advance buffer swaps keep the same EEG signal in the
    // overlap region so existing blit pixels remain visually correct.
    if (!preserveBlitCache) {
      this._blitDirty = true;
    }
  }

  /**
   * Invalidate the scrolling blit cache so the next drawWaveforms() call
   * performs a full redraw.  Must be called on: seek, config changes that
   * affect waveform rendering (filters, amplitude, polarity, montage, gain),
   * channel/montage changes, and canvas resize.
   */
  public invalidateBlitCache(): void {
    this._blitDirty = true;
  }

  public dispose(): void {
    this._webglAdapter?.dispose();
    this._webglAdapter = null;
    this._gpuCanvas = null;
    this._gpuChanTopY = [];
    this._gpuChanBotY = [];
    this._blitCanvas = null;
    this._blitCtx = null;
    this._blitSnapCanvas = null;
    this._blitSnapCtx = null;
    this.channels = [];
    this.montageChannels = [];
    this.virtualReferenceEnvelopeCache.clear();
    this.displayGeometryCache.clear();
    this.derivationCache.clear();
    this.filterCache = new WeakMap();
  }

  /**
   * Notify the renderer that the canvas container has been resized.
   * Called by the ResizeObserver in EEGViewer with the exact contentRect
   * dimensions.  Caches these values so resize() never needs to call the
   * layout-triggering canvas.clientWidth/clientHeight in the hot render path.
   */
  public notifyResize(width: number, height: number): void {
    this._cssWidth = width;
    this._cssHeight = height;
    if (this._gpuCanvas) {
      const w = Math.round(Math.max(1, width));
      const h = Math.round(Math.max(1, height));
      if (this._gpuCanvas.width !== w || this._gpuCanvas.height !== h) {
        this._gpuCanvas.width = w;
        this._gpuCanvas.height = h;
      }
    }
  }

  /**
   * Ensure the canvas pixel buffer matches its CSS layout size.
   * Call before every render to guard against React re-mounts or layout
   * changes that reset the canvas element's width/height attributes to
   * the HTML default (300 × 150).
   *
   * Uses _cssWidth/_cssHeight cached by notifyResize() rather than reading
   * canvas.clientWidth/clientHeight, which would force a synchronous layout
   * reflow on every RAF frame (20+ ms stall when React has dirty DOM state).
   * Falls back to clientWidth/clientHeight when the cache is empty (first
   * render before the ResizeObserver has fired).
   */
  public resize(): void {
    // Round to nearest integer: contentRect dimensions from ResizeObserver are
    // floating-point CSS pixels (e.g. 1234.42) but canvas.width/height are
    // always integers.  Without rounding, the mismatch causes _blitDirty = true
    // on every single frame, forcing a full canvas redraw and completely
    // defeating the blit cache (the backward-scroll artefact root cause).
    const displayWidth = Math.round(
      this._cssWidth > 0 ? this._cssWidth : this.canvas.clientWidth,
    );
    const displayHeight = Math.round(
      this._cssHeight > 0 ? this._cssHeight : this.canvas.clientHeight,
    );
    if (
      displayWidth > 0 &&
      displayHeight > 0 &&
      (this.canvas.width !== displayWidth ||
        this.canvas.height !== displayHeight)
    ) {
      this.canvas.width = displayWidth;
      this.canvas.height = displayHeight;
      // Blit canvas is now the wrong size — force a full redraw.
      this._blitDirty = true;
    }
  }

  /**
   * Main render function
   */
  public render(annotations: Annotation[] = []): void {
    // Always sync canvas pixel buffer to CSS layout size before drawing
    this.resize();

    this.clear();

    const minimalStartupPaint = this.config.minimalStartupPaint === true;
    const rapidReviewPlay =
      this.config.playbackTransportMode === "play" &&
      this.config.playbackSpeed >= 30;
    const _rt0 = performance.now();
    // Grid fill/stroke is measurable under dual-video+k6; rapid-review play
    // keeps the scrolling waveform and restores grid when paused/inspecting.
    if (this.config.showGrid && !minimalStartupPaint && !rapidReviewPlay) {
      this.drawGrid();
    }
    const _rt1 = performance.now();

    if (this.montageChannels.length === 0 || this.channels.length === 0) {
      this.drawPlaceholder();
      return;
    }

    this.drawWaveforms(); // also increments this.frameCount
    const _rt2 = performance.now();
    if (minimalStartupPaint) {
      return;
    }
    const isPostFlushTransitionFrame =
      this._postFlushTransitionFramesRemaining > 0;
    const postFlushFrameIndex = isPostFlushTransitionFrame
      ? this._postFlushTransitionFrameOrdinal + 1
      : null;
    // Rapid-review play drops annotation/label/timeline chrome every frame so
    // waveform blit owns the main thread under dual-video+k6.
    const deferredOverlayMode = rapidReviewPlay ? "all" : "none";

    if (deferredOverlayMode === "none") {
      this.drawAnnotations(annotations);
    }
    const _rt3 = performance.now();
    if (deferredOverlayMode === "none") {
      this.drawLabels();
    }
    const _rt4 = performance.now();
    if (deferredOverlayMode !== "all") {
      // EEG-UX-006: Keep channel group separators independent from the main grid
      // toggle so "Channel group labels" remains effective even when the user
      // turns the waveform grid off.
      if (!this.config.showGrid) {
        this.drawChannelGroupSeparatorsOverlay();
      }
      if (this.config.showTimeline) {
        this.drawTimescale();
      }
      if (this.config.showScaleMarkers !== false) {
        this.drawCalibrationBar();
      }
      this.drawScaleMarkers();
      this.drawPersistentTimestamp();
      this.drawCursorOverlay();
    }
    const _rt5 = performance.now();

    if (postFlushFrameIndex !== null) {
      const waveformMetrics = this._lastWaveformRenderMetrics;
      recordRendererPerfEvent("postFlushRender", {
        frameIndex: postFlushFrameIndex,
        gridMs: Number((_rt1 - _rt0).toFixed(1)),
        waveformsMs: Number((_rt2 - _rt1).toFixed(1)),
        annotationsMs: Number((_rt3 - _rt2).toFixed(1)),
        labelsMs: Number((_rt4 - _rt3).toFixed(1)),
        restMs: Number((_rt5 - _rt4).toFixed(1)),
        totalMs: Number((_rt5 - _rt0).toFixed(1)),
        deferredOverlayMode,
        useBlit: waveformMetrics?.useBlit ?? false,
        fullRedraw: waveformMetrics?.fullRedraw ?? true,
        blitBlockReason: waveformMetrics?.blitBlockReason ?? null,
        deltaPx: waveformMetrics?.deltaPx ?? null,
        clipWidthPx: waveformMetrics?.clipWidth ?? null,
        maxBlitFraction: waveformMetrics?.maxBlitFraction ?? null,
        transitionBlitRelaxed: waveformMetrics?.transitionBlitRelaxed ?? false,
        rapidReviewSourceStrideMax:
          waveformMetrics?.rapidReviewSourceStrideMax ?? null,
        envelopeRenderStrideMin:
          waveformMetrics?.envelopeRenderStrideMin ?? null,
        envelopeRenderStrideMax:
          waveformMetrics?.envelopeRenderStrideMax ?? null,
        envelopeSamplesPerPixelMax:
          waveformMetrics?.envelopeSamplesPerPixelMax ?? null,
      });
      this._postFlushTransitionFrameOrdinal = postFlushFrameIndex;
      this._postFlushTransitionFramesRemaining = Math.max(
        0,
        this._postFlushTransitionFramesRemaining - 1,
      );
    }

    if (
      this.config.playbackTransportMode === "play" &&
      this.config.playbackSpeed >= 100
    ) {
      recordRendererPerfEvent("renderPhases", {
        gridMs: Number((_rt1 - _rt0).toFixed(1)),
        waveformsMs: Number((_rt2 - _rt1).toFixed(1)),
        annotationsMs: Number((_rt3 - _rt2).toFixed(1)),
        labelsMs: Number((_rt4 - _rt3).toFixed(1)),
        restMs: Number((_rt5 - _rt4).toFixed(1)),
        totalMs: Number((_rt5 - _rt0).toFixed(1)),
        deferredOverlayMode,
        showGrid: this.config.showGrid,
        showTimeline: this.config.showTimeline,
        useBlit: this._lastWaveformRenderMetrics?.useBlit ?? false,
        fullRedraw: this._lastWaveformRenderMetrics?.fullRedraw ?? true,
        blitBlockReason:
          this._lastWaveformRenderMetrics?.blitBlockReason ?? null,
        deltaPx: this._lastWaveformRenderMetrics?.deltaPx ?? null,
        clipWidthPx: this._lastWaveformRenderMetrics?.clipWidth ?? null,
        rapidReviewSourceStrideMax:
          this._lastWaveformRenderMetrics?.rapidReviewSourceStrideMax ?? null,
        envelopeRenderStrideMin:
          this._lastWaveformRenderMetrics?.envelopeRenderStrideMin ?? null,
        envelopeRenderStrideMax:
          this._lastWaveformRenderMetrics?.envelopeRenderStrideMax ?? null,
        envelopeSamplesPerPixelMax:
          this._lastWaveformRenderMetrics?.envelopeSamplesPerPixelMax ?? null,
      });
    }

    // Log render phase breakdown every 120 frames when any phase is slow (>8ms total).
    if (process.env.NODE_ENV !== "production" && this.frameCount % 120 === 0) {
      const total = _rt5 - _rt0;
      if (total > 8) {
        logger.debug("[EEGRenderer] render phases (ms)", {
          grid: (_rt1 - _rt0).toFixed(1),
          waveforms: (_rt2 - _rt1).toFixed(1),
          annotations: (_rt3 - _rt2).toFixed(1),
          labels: (_rt4 - _rt3).toFixed(1),
          rest: (_rt5 - _rt4).toFixed(1),
          total: total.toFixed(1),
        });
      }
    }
  }

  public getWaveformRenderMetrics(): WaveformRenderMetrics | null {
    return this._lastWaveformRenderMetrics
      ? { ...this._lastWaveformRenderMetrics }
      : null;
  }

  /**
   * Sample center-crop luma so soak/perf can detect fade/brightness pops
   * when blit reuse drops to a full redraw or display profile changes.
   */
  private samplePaintConsistency(
    kind: NonNullable<WaveformRenderMetrics["paintTransition"]>,
  ): void {
    const metrics = this._lastWaveformRenderMetrics;
    if (!metrics) return;
    metrics.paintTransition = kind;
    this._paintSampleFrame += 1;
    const shouldReadPixels =
      kind === "full-redraw" ||
      kind === "post-flush" ||
      this._paintSampleFrame % 8 === 0;
    if (!shouldReadPixels) {
      metrics.paintLuma = this._lastPaintLuma;
      metrics.paintLumaDelta = 0;
      return;
    }
    try {
      const { width, height } = this.canvas;
      if (width < 8 || height < 8) return;
      const sampleW = Math.min(48, width);
      const sampleH = Math.min(48, height);
      const sx = Math.max(0, Math.floor((width - sampleW) / 2));
      const sy = Math.max(0, Math.floor((height - sampleH) / 2));
      const data = this.ctx.getImageData(sx, sy, sampleW, sampleH).data;
      let sum = 0;
      let count = 0;
      for (let i = 0; i < data.length; i += 16) {
        if (data[i + 3] === 0) continue;
        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        count += 1;
      }
      const luma = count > 0 ? sum / count : 0;
      const delta =
        this._lastPaintLuma === null ? 0 : luma - this._lastPaintLuma;
      this._lastPaintLuma = luma;
      metrics.paintLuma = Number(luma.toFixed(2));
      metrics.paintLumaDelta = Number(delta.toFixed(2));
      if (Math.abs(delta) >= 12 || kind === "full-redraw") {
        recordRendererPerfEvent("paintConsistency", {
          luma: metrics.paintLuma,
          lumaDelta: metrics.paintLumaDelta,
          transition: kind,
          fullRedraw: metrics.fullRedraw,
          blitBlockReason: metrics.blitBlockReason,
          playbackSpeed: this.config.playbackSpeed,
          sensitivity: this.config.sensitivity,
          timeScale: this.config.timeScale,
        });
      }
    } catch {
      // getImageData can throw if the canvas is 0-sized or tainted.
    }
  }

  /**
   * EEG-UX-006: Draw channel group separators even when the grid is disabled.
   */
  private drawChannelGroupSeparatorsOverlay(): void {
    const displayMode = this.getSafeDisplayMode();
    if (displayMode === "butterfly") return;
    if (this.config.showChannelGroupLabels === false) return;
    const visibleMontageChannels = this.getVisibleMontageChannels();
    if (visibleMontageChannels.length <= 1) return;

    const waveformArea = this.getWaveformArea();
    const visibleChannelCount = this.getLayoutVisibleChannelCount(
      Math.max(1, visibleMontageChannels.length),
    );
    const channelHeight = waveformArea.height / visibleChannelCount;
    const channelNames = visibleMontageChannels.map(
      (entry) => entry.channel.label,
    );

    // When the grid is off, keep a light per-channel separator so channels
    // don’t visually merge into “blank bands” between group headings.
    const strokeRgb = this.parseGridColorRgb();
    const baseGridAlpha = this.parseGridColorAlpha();
    const separatorStroke = `rgba(${strokeRgb}, ${Math.min(1, baseGridAlpha * 0.34)})`;
    this.ctx.save();
    this.ctx.lineWidth = 0.8;
    this.ctx.strokeStyle = separatorStroke;
    this.ctx.beginPath();
    for (let i = 0; i <= visibleChannelCount; i += 1) {
      const y = waveformArea.top + i * channelHeight;
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(this.canvas.width, y);
    }
    this.ctx.stroke();
    this.ctx.restore();

    this.drawChannelGroupSeparators(channelNames, waveformArea.top, channelHeight);
  }

  /**
   * Clear canvas
   */
  private clear(): void {
    this.ctx.fillStyle = this.config.backgroundColor;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Get waveform area dimensions based on timeline visibility and position
   */
  private getWaveformArea(): { top: number; height: number; bottom: number } {
    const canvasHeight = this.canvas.height;
    const timelineSpace = this.config.showTimeline
      ? this.RULER_HEIGHT + this.RULER_MARGIN * 2
      : 0;

    if (!this.config.showTimeline) {
      return { top: 0, height: canvasHeight, bottom: canvasHeight };
    }

    if (this.config.timelinePosition === "bottom") {
      return {
        top: 0,
        height: canvasHeight - timelineSpace,
        bottom: canvasHeight - timelineSpace,
      };
    } else {
      return {
        top: timelineSpace,
        height: canvasHeight - timelineSpace,
        bottom: canvasHeight,
      };
    }
  }

  private getTimescaleRulerRect(): { top: number; height: number } | null {
    if (!this.config.showTimeline) {
      return null;
    }
    const rulerHeight = this.RULER_HEIGHT;
    const rulerTop =
      this.config.timelinePosition === "bottom"
        ? this.canvas.height - rulerHeight - this.RULER_MARGIN
        : this.RULER_MARGIN;
    return { top: rulerTop, height: rulerHeight };
  }

  private getMontageLabelColumnWidth(): number {
    const visibleMontageChannels = this.getVisibleMontageChannels();
    if (visibleMontageChannels.length === 0) {
      return 0;
    }
    const labelSize = this.getSafeLabelFontSize();
    this.ctx.font = `bold ${labelSize}px Arial`;
    let maxWidth = 0;
    for (const { channel } of visibleMontageChannels) {
      const label = channel.label?.trim() || "";
      if (!label) continue;
      const width = this.ctx.measureText(label).width;
      if (width > maxWidth) {
        maxWidth = width;
      }
    }
    return maxWidth > 0 ? Math.ceil(10 + maxWidth + 8) : 0;
  }

  private getPlotGeometry(): RenderPlotGeometry {
    const waveformArea = this.getWaveformArea();
    const canvasWidth = Math.max(1, this.canvas.width);
    const left = Math.max(
      0,
      Math.min(
        Math.floor(canvasWidth * 0.35),
        this.getMontageLabelColumnWidth(),
      ),
    );
    const width = Math.max(1, canvasWidth - left);
    const timeScale = Math.max(0.001, this.config.timeScale || 10);
    return {
      left,
      width,
      right: left + width,
      canvasWidth,
      top: waveformArea.top,
      height: waveformArea.height,
      bottom: waveformArea.bottom,
      pixelsPerSecond: width / timeScale,
    };
  }

  private timeToX(absoluteTime: number): number {
    const plot = this.getPlotGeometry();
    return plot.left + (absoluteTime - this.currentTime) * plot.pixelsPerSecond;
  }

  /**
   * Draw grid lines
   */
  private drawGrid(): void {
    if (this.config.gridStyle === "clinical") {
      this.drawClinicalGrid();
      return;
    }
    this.drawStandardGrid();
  }

  private drawStandardGrid(): void {
    const plot = this.getPlotGeometry();
    const waveformArea = this.getWaveformArea();
    const waveformAreaTop = waveformArea.top;
    const waveformAreaBottom = waveformArea.bottom;
    const waveformAreaHeight = waveformArea.height;
    const visibleMontageChannels = this.getVisibleMontageChannels();
    const displayMode = this.getSafeDisplayMode();
    const visibleChannelCount = this.getLayoutVisibleChannelCount(
      Math.max(1, visibleMontageChannels.length),
    );

    const channelHeight = waveformAreaHeight / visibleChannelCount;
    const pixelsPerSecond = plot.pixelsPerSecond;
    const pxPerMm = this.getSafePixelsPerMm();
    const densityPlan = resolveEegGridDensityPlan(this.config.gridDensity);
    const minorVerticalStepSeconds =
      (this.config.timeScale <= 10 ? 0.2 : 0.5) *
      densityPlan.timeMinorStepMultiplier;
    const minorVerticalTickCount = Math.max(
      1,
      Math.floor(this.config.timeScale / minorVerticalStepSeconds),
    );
    const majorSeconds = 1;
    const strokeRgb = this.parseGridColorRgb();
    const baseGridAlpha = this.parseGridColorAlpha();
    const strokeWithAlpha = (alpha: number) =>
      `rgba(${strokeRgb}, ${Math.min(1, baseGridAlpha * alpha)})`;

    // ── Data-coverage clip (mirrors drawClinicalGrid) ─────────────────────────
    // Only draw grid strokes over the loaded-data region so no lines appear in
    // the empty area beyond the data boundary (right of recording end, or left
    // before data starts).  This eliminates the "shadow" visible when the canvas
    // widens (e.g. right dock panel hidden) and waveforms don't yet cover the
    // newly exposed columns.
    const _sgDataLength =
      this.channels.length > 0 ? this.channels[0].data.length : 0;
    const _sgDataStartTime = this.dataTimeOffset;
    const _sgDataEndTime =
      this.dataTimeOffset + _sgDataLength / Math.max(1, this.sampleRate);
    const _sgViewportStart = this.currentTime;
    const _sgViewportEnd = _sgViewportStart + this.config.timeScale;
    const _sgClipStartX = Math.max(
      plot.left,
      this.timeToX(Math.max(_sgDataStartTime, _sgViewportStart)),
    );
    const _sgClipEndX = Math.min(
      plot.right,
      this.timeToX(Math.min(_sgDataEndTime, _sgViewportEnd)),
    );
    if (_sgClipEndX <= _sgClipStartX) return;

    this.ctx.save();
    // Apply data-coverage clip so strokes stay within the loaded data region.
    this.ctx.beginPath();
    this.ctx.rect(
      _sgClipStartX,
      waveformAreaTop,
      _sgClipEndX - _sgClipStartX,
      waveformAreaHeight,
    );
    this.ctx.clip();

    // Batch minor vertical lines into a single path with enough contrast to
    // remain visible against the dark waveform background.
    // Use an integer-ish width to avoid subpixel blur on many displays.
    if (densityPlan.showMinorTimeLines) {
      this.ctx.lineWidth = 0.55;
      this.ctx.strokeStyle = `rgba(${strokeRgb}, ${densityPlan.minorTimeAlpha})`;
      this.ctx.beginPath();
      for (let i = 0; i <= minorVerticalTickCount; i += 1) {
        const seconds = i * minorVerticalStepSeconds;
        const isMajor =
          Math.abs(seconds / majorSeconds - Math.round(seconds / majorSeconds)) <
          0.001;
        if (!isMajor) {
          const x = plot.left + seconds * pixelsPerSecond;
          this.ctx.moveTo(x, waveformAreaTop);
          this.ctx.lineTo(x, waveformAreaBottom);
        }
      }
      this.ctx.stroke();
    }

    // Batch major vertical lines into a single path.
    this.ctx.lineWidth = 0.85;
    this.ctx.strokeStyle = strokeWithAlpha(0.74);
    this.ctx.beginPath();
    for (let i = 0; i <= minorVerticalTickCount; i += 1) {
      const seconds = i * minorVerticalStepSeconds;
      const isMajor =
        Math.abs(seconds / majorSeconds - Math.round(seconds / majorSeconds)) <
        0.001;
      if (isMajor) {
        const x = plot.left + seconds * pixelsPerSecond;
        this.ctx.moveTo(x, waveformAreaTop);
        this.ctx.lineTo(x, waveformAreaBottom);
      }
    }
    this.ctx.stroke();

    // Horizontal grid: 1 mm minors (medium), 0.5 mm at high, majors only at low.
    // Keep 5 mm majors at every density so High is denser minors, not closer majors.
    const amplitudeMajorMm = 5;
    const amplitudeMinorMm = densityPlan.showMinorAmplitudeLines
      ? densityPlan.amplitudeMinorMm
      : amplitudeMajorMm;
    const horizontalMinorStepPx = Math.max(1, pxPerMm * amplitudeMinorMm);
    const horizontalMajorEvery = Math.max(
      1,
      Math.round(amplitudeMajorMm / amplitudeMinorMm),
    );
    const horizontalLineCount = Math.floor(
      waveformAreaHeight / horizontalMinorStepPx,
    );

    // Batch minor horizontal lines into a single path.
    if (densityPlan.showMinorAmplitudeLines) {
      this.ctx.lineWidth = 0.35;
      this.ctx.strokeStyle = `rgba(${strokeRgb}, ${densityPlan.minorAmplitudeAlpha})`;
      this.ctx.beginPath();
      for (let i = 0; i <= horizontalLineCount; i += 1) {
        if (i % horizontalMajorEvery !== 0) {
          const y = waveformAreaTop + i * horizontalMinorStepPx;
          this.ctx.moveTo(plot.left, y);
          this.ctx.lineTo(plot.right, y);
        }
      }
      this.ctx.stroke();
    }

    // Batch major horizontal lines into a single path.
    this.ctx.lineWidth = 0.55;
    this.ctx.strokeStyle = strokeWithAlpha(0.16);
    this.ctx.beginPath();
    for (let i = 0; i <= horizontalLineCount; i += 1) {
      if (i % horizontalMajorEvery === 0) {
        const y = waveformAreaTop + i * horizontalMinorStepPx;
        this.ctx.moveTo(plot.left, y);
        this.ctx.lineTo(plot.right, y);
      }
    }
    this.ctx.stroke();

    // Batch channel separator lines into a single path.
    this.ctx.lineWidth = 0.65;
    this.ctx.strokeStyle = strokeWithAlpha(0.3);
    this.ctx.beginPath();
    for (let i = 0; i <= visibleChannelCount; i++) {
      const y = waveformAreaTop + i * channelHeight;
      this.ctx.moveTo(plot.left, y);
      this.ctx.lineTo(plot.right, y);
    }
    this.ctx.stroke();

    this.ctx.restore();

    // EEG-UX-006: Channel group separators
    if (
      displayMode !== "butterfly" &&
      this.config.showChannelGroupLabels !== false &&
      visibleMontageChannels.length > 1
    ) {
      const channelNames = visibleMontageChannels.map(
        (entry) => entry.channel.label,
      );
      this.drawChannelGroupSeparators(
        channelNames,
        waveformAreaTop,
        channelHeight,
      );
    }
  }

  private drawClinicalGrid(): void {
    const plot = this.getPlotGeometry();
    const waveformArea = this.getWaveformArea();
    const waveformAreaTop = waveformArea.top;
    const waveformAreaBottom = waveformArea.bottom;
    const waveformAreaHeight = waveformArea.height;
    const visibleMontageChannels = this.getVisibleMontageChannels();
    const displayMode = this.getSafeDisplayMode();
    const visibleChannelCount = this.getLayoutVisibleChannelCount(
      Math.max(1, visibleMontageChannels.length),
    );
    const channelHeight = waveformAreaHeight / visibleChannelCount;
    const pxPerMm = this.getSafePixelsPerMm();
    const densityPlan = resolveEegGridDensityPlan(this.config.gridDensity);
    const amplitudeGridChannels =
      displayMode === "butterfly"
        ? visibleMontageChannels.slice(0, 1)
        : visibleMontageChannels;

    const viewportStart = this.currentTime;
    const viewportEnd = viewportStart + this.config.timeScale;

    const majorSeconds =
      this.config.timeScale <= 20 ? 1 : this.config.timeScale <= 60 ? 2 : 5;
    const minorSeconds =
      (this.config.timeScale <= 20
        ? 0.2
        : this.config.timeScale <= 60
          ? 0.5
          : 1) * densityPlan.timeMinorStepMultiplier;
    const isNearlyInteger = (value: number, step: number): boolean =>
      Math.abs(value / step - Math.round(value / step)) < 1e-3;

    // ── Data-coverage clip ────────────────────────────────────────────────────
    // Only draw grid lines where loaded data exists. Outside the data window
    // the canvas stays the pure backgroundColor — eliminating the "shadow" that
    // appears when the dense amplitude-subdivision lines are visible on a bare
    // background (no waveforms to cover them) at the start of playback or after
    // a seek before the new data window arrives.
    const dataLength =
      this.channels.length > 0 ? this.channels[0].data.length : 0;
    const dataStartTime = this.dataTimeOffset;
    const dataEndTime =
      this.dataTimeOffset + dataLength / Math.max(1, this.sampleRate);
    const clipStartX = Math.max(
      plot.left,
      this.timeToX(Math.max(dataStartTime, viewportStart)),
    );
    const clipEndX = Math.min(
      plot.right,
      this.timeToX(Math.min(dataEndTime, viewportEnd)),
    );
    // No data visible in current viewport — nothing to draw.
    if (clipEndX <= clipStartX) return;

    const strokeRgb = this.parseGridColorRgb();
    const baseGridAlpha = this.parseGridColorAlpha();
    const strokeWithAlpha = (alpha: number) =>
      `rgba(${strokeRgb}, ${Math.min(1, baseGridAlpha * alpha)})`;

    this.ctx.save();

    // Clip all grid strokes to the data-covered region so no lines appear in
    // the empty area to the left/right of loaded data.
    this.ctx.beginPath();
    this.ctx.rect(
      clipStartX,
      waveformAreaTop,
      clipEndX - clipStartX,
      waveformAreaHeight,
    );
    this.ctx.clip();

    const firstMinor = Math.floor(viewportStart / minorSeconds) * minorSeconds;

    // Minor vertical lines.
    if (densityPlan.showMinorTimeLines) {
      this.ctx.strokeStyle = `rgba(${strokeRgb}, ${densityPlan.minorTimeAlpha})`;
      this.ctx.lineWidth = 0.55;
      this.ctx.beginPath();
      for (
        let t = firstMinor;
        t <= viewportEnd + minorSeconds * 0.5;
        t += minorSeconds
      ) {
        const x = this.timeToX(t);
        if (x < plot.left || x > plot.right) continue;
        if (!isNearlyInteger(t, majorSeconds)) {
          this.ctx.moveTo(x, waveformAreaTop);
          this.ctx.lineTo(x, waveformAreaBottom);
        }
      }
      this.ctx.stroke();
    }

    // Major vertical lines.
    this.ctx.strokeStyle = strokeWithAlpha(0.74);
    this.ctx.lineWidth = 0.85;
    this.ctx.beginPath();
    for (
      let t = firstMinor;
      t <= viewportEnd + minorSeconds * 0.5;
      t += minorSeconds
    ) {
      const x = this.timeToX(t);
      if (x < plot.left || x > plot.right) continue;
      if (isNearlyInteger(t, majorSeconds)) {
        this.ctx.moveTo(x, waveformAreaTop);
        this.ctx.lineTo(x, waveformAreaBottom);
      }
    }
    this.ctx.stroke();

    // Horizontal EDF-like amplitude grid around each channel baseline.
    const sensitivityUvPerMm = Math.max(
      0.1,
      this.config.sensitivity ?? BASELINE_SENSITIVITY_UV_PER_MM,
    );
    const minorUvStep = Math.max(
      0.5,
      sensitivityUvPerMm * densityPlan.amplitudeMinorMm,
    );
    const majorUvStep = Math.max(minorUvStep, sensitivityUvPerMm * 5); // 5 mm
    const pxPerUv = pxPerMm / sensitivityUvPerMm;

    // Channel baselines.
    this.ctx.strokeStyle = strokeWithAlpha(0.34);
    this.ctx.lineWidth = 0.65;
    this.ctx.beginPath();
    amplitudeGridChannels.forEach(({ channel }, channelIndex) => {
      const baseY = this.getWaveformBaselineY(
        channelIndex,
        channel.label,
        channelHeight,
        waveformAreaTop,
        waveformAreaBottom,
      );
      this.ctx.moveTo(plot.left, baseY);
      this.ctx.lineTo(plot.right, baseY);
    });
    this.ctx.stroke();

    // Minor amplitude subdivision lines.
    if (densityPlan.showMinorAmplitudeLines) {
      this.ctx.strokeStyle = `rgba(${strokeRgb}, ${densityPlan.minorAmplitudeAlpha})`;
      this.ctx.lineWidth = 0.3;
      this.ctx.beginPath();
      amplitudeGridChannels.forEach(({ channel }, channelIndex) => {
        const channelBounds = this.getChannelVerticalBounds(
          channelIndex,
          channelHeight,
          waveformAreaTop,
          waveformAreaBottom,
        );
        const baseY = this.getWaveformBaselineY(
          channelIndex,
          channel.label,
          channelHeight,
          waveformAreaTop,
          waveformAreaBottom,
        );
        for (let uv = minorUvStep; uv <= 1000; uv += minorUvStep) {
          const offsetPx = uv * pxPerUv;
          const upY = baseY - offsetPx;
          const downY = baseY + offsetPx;
          const isMajor =
            Math.abs(uv / majorUvStep - Math.round(uv / majorUvStep)) < 1e-3;
          if (!isMajor) {
            if (upY >= channelBounds.top) {
              this.ctx.moveTo(plot.left, upY);
              this.ctx.lineTo(plot.right, upY);
            }
            if (downY <= channelBounds.bottom) {
              this.ctx.moveTo(plot.left, downY);
              this.ctx.lineTo(plot.right, downY);
            }
          }
          if (upY < channelBounds.top && downY > channelBounds.bottom) break;
        }
      });
      this.ctx.stroke();
    }

    // Major amplitude subdivision lines.
    this.ctx.strokeStyle = strokeWithAlpha(0.14);
    this.ctx.lineWidth = 0.45;
    this.ctx.beginPath();
    amplitudeGridChannels.forEach(({ channel }, channelIndex) => {
      const channelBounds = this.getChannelVerticalBounds(
        channelIndex,
        channelHeight,
        waveformAreaTop,
        waveformAreaBottom,
      );
      const baseY = this.getWaveformBaselineY(
        channelIndex,
        channel.label,
        channelHeight,
        waveformAreaTop,
        waveformAreaBottom,
      );
      for (let uv = minorUvStep; uv <= 1000; uv += minorUvStep) {
        const offsetPx = uv * pxPerUv;
        const upY = baseY - offsetPx;
        const downY = baseY + offsetPx;
        const isMajor =
          Math.abs(uv / majorUvStep - Math.round(uv / majorUvStep)) < 1e-3;
        if (isMajor) {
          if (upY >= channelBounds.top) {
            this.ctx.moveTo(plot.left, upY);
            this.ctx.lineTo(plot.right, upY);
          }
          if (downY <= channelBounds.bottom) {
            this.ctx.moveTo(plot.left, downY);
            this.ctx.lineTo(plot.right, downY);
          }
        }
        if (upY < channelBounds.top && downY > channelBounds.bottom) break;
      }
    });
    this.ctx.stroke();

    // Channel separator lines.
    // In clinical mode the dense amplitude subdivisions can visually overpower
    // separators; use a slightly brighter stroke on dark backgrounds so every
    // channel boundary remains readable even between region/group headings.
    this.ctx.strokeStyle = this.isBackgroundDark()
      ? "rgba(226, 232, 240, 0.22)"
      : strokeWithAlpha(0.72);
    this.ctx.lineWidth = 0.8;
    this.ctx.beginPath();
    for (let i = 0; i <= visibleChannelCount; i += 1) {
      const y = waveformAreaTop + i * channelHeight;
      this.ctx.moveTo(plot.left, y);
      this.ctx.lineTo(plot.right, y);
    }
    this.ctx.stroke();

    this.ctx.restore();
    this.ctx.globalAlpha = 1.0;

    if (
      displayMode !== "butterfly" &&
      this.config.showChannelGroupLabels !== false &&
      visibleMontageChannels.length > 1
    ) {
      const channelNames = visibleMontageChannels.map(
        (entry) => entry.channel.label,
      );
      this.drawChannelGroupSeparators(
        channelNames,
        waveformAreaTop,
        channelHeight,
      );
    }
  }

  /**
   * EEG-UX-006: Draw region separators and group labels between 10-20 scalp regions.
   */
  private drawChannelGroupSeparators(
    channelNames: string[],
    waveformAreaTop: number,
    channelHeight: number,
  ): void {
    const groupedChannels = channelNames.map((name) => ({
      group: classifyChannelGroup(name),
    }));
    const groupedCounts = groupedChannels.reduce<
      Partial<Record<ChannelGroupLabel, number>>
    >((acc, item) => {
      if (item.group === "OTHER") return acc;
      acc[item.group] = (acc[item.group] ?? 0) + 1;
      return acc;
    }, {});

    const boundaries: Array<{ afterIndex: number; label: ChannelGroupLabel }> =
      [];
    for (let i = 0; i < groupedChannels.length - 1; i += 1) {
      const current = groupedChannels[i]?.group ?? "OTHER";
      const next = groupedChannels[i + 1]?.group ?? "OTHER";
      if (current === next || current === "OTHER") continue;
      if ((groupedCounts[current] ?? 0) < 2) continue;
      boundaries.push({ afterIndex: i, label: current });
    }

    if (!boundaries.length) return;

    const ctx = this.ctx;
    const plot = this.getPlotGeometry();
    boundaries.forEach(({ afterIndex, label }) => {
      const y = waveformAreaTop + (afterIndex + 1) * channelHeight;
      const labelX = 8;
      const labelY = Math.max(waveformAreaTop + 13, y - 3);

      ctx.save();
      ctx.strokeStyle = this.isBackgroundDark()
        ? "rgba(226,232,240,0.26)"
        : "rgba(15,23,42,0.28)";
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(plot.canvasWidth, y);
      ctx.stroke();

      ctx.font = "700 9px 'Segoe UI', sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      const labelWidth = Math.ceil(ctx.measureText(label).width);
      ctx.fillStyle = this.isBackgroundDark()
        ? "rgba(2,6,23,0.72)"
        : "rgba(248,250,252,0.82)";
      ctx.fillRect(labelX - 3, labelY - 12, labelWidth + 8, 13);
      ctx.fillStyle = this.isBackgroundDark()
        ? "rgba(226,232,240,0.92)"
        : "rgba(15,23,42,0.9)";
      ctx.fillText(label, labelX, labelY);
      ctx.restore();
    });
  }

  /**
   * Grow pre-allocated batch arrays to at least `channelCount` entries.
   * Arrays never shrink so that GC pressure from repeated render calls stays
   * near zero.  Called by both the fast-batch and group-batch render paths.
   */
  private _ensureBatchArrays(channelCount: number): void {
    if (channelCount > this._batchDataMeans.length) {
      const sz = channelCount * 2;
      this._batchDataMeans = new Float64Array(sz);
      this._batchScales = new Float64Array(sz);
      this._batchBaseYs = new Float64Array(sz);
      this._batchTopBounds = new Float64Array(sz);
      this._batchBotBounds = new Float64Array(sz);
      this._batchGains = new Float64Array(sz);
      this._batchSampleXOffsets = new Float64Array(sz);
      this._batchSamplesPerPage = new Float64Array(sz);
      this._batchEnvelopeIndexBases = new Int32Array(sz);
      this._batchUseDisplayGeometry = new Uint8Array(sz);
      this._batchFixed = new Uint8Array(sz);
    }
    while (this._batchFiltered.length < channelCount) {
      this._batchFiltered.push(null);
      this._batchRepData.push(null);
      this._batchEnvelopes.push(null);
    }
  }

  private appendEnvelopeBandPolygon(
    clipLeft: number,
    pixelCount: number,
    channelTopBound: number,
    channelBottomBound: number,
  ): boolean {
    const allowTraceOverflow =
      this.config.allowTraceOverflow === true &&
      this.getSafeDisplayMode() === "stacked";
    const validPixels: number[] = [];
    for (let px = clipLeft; px < pixelCount; px++) {
      const top = this.envMaxYs[px];
      const bot = this.envMinYs[px];
      const valid =
        top < channelBottomBound &&
        bot > channelTopBound &&
        (allowTraceOverflow ||
          bot - top <= (channelBottomBound - channelTopBound) * 0.9);

      if (valid) {
        validPixels.push(px);
      }
    }

    if (validPixels.length === 0) {
      return false;
    }

    const topYs = this.envMaxYs;
    const botYs = this.envMinYs;
    const firstPx = validPixels[0];
    this.ctx.moveTo(firstPx + 0.5, topYs[firstPx]);
    for (let index = 1; index < validPixels.length; index++) {
      const px = validPixels[index];
      this.ctx.lineTo(px + 0.5, topYs[px]);
    }
    for (let index = validPixels.length - 1; index >= 0; index--) {
      const px = validPixels[index];
      this.ctx.lineTo(px + 0.5, botYs[px]);
    }
    this.ctx.closePath();
    return true;
  }

  /**
   * Draw all waveforms based on montage configuration
   */
  private drawWaveforms(): void {
    const { width, height } = this.canvas;
    const plot = this.getPlotGeometry();
    const plotLeft = plot.left;
    const plotWidth = plot.width;
    const clampWaveformsToLane = !(
      this.config.allowTraceOverflow === true &&
      this.getSafeDisplayMode() === "stacked"
    );

    // ── Scrolling blit cache (Fix G) ─────────────────────────────────────────
    // During smooth forward playback each frame only differs from the previous
    // one by a narrow right-hand strip of Δpx columns.  We blit the cached
    // waveform-only layer shifted left by Δpx and draw only the new right strip.
    // The blit canvas uses a transparent background so the grid (drawn before
    // drawWaveforms() is called) shows through correctly everywhere.
    //
    // Fall back to a full redraw when: cache is invalid, large/backward seek,
    // or the canvas size has changed.
    const pixelsPerSecond = plotWidth / this.config.timeScale;
    const deltaT = this.currentTime - this._blitTime;
    const deltaPx = Math.round(deltaT * pixelsPerSecond);
    const allowTrueOverlapBlitAtHighSpeedPlay =
      this.config.playbackTransportMode === "play" &&
      this.config.playbackSpeed >= EEG_VIEWER_TRUE_OVERLAP_BLIT_MIN_SPEED;
    const isPostFlushTransitionFrame =
      this._postFlushTransitionFramesRemaining > 0;
    const transitionBlitRelaxed =
      isPostFlushTransitionFrame && allowTrueOverlapBlitAtHighSpeedPlay;
    const maxBlitFraction = allowTrueOverlapBlitAtHighSpeedPlay
      ? resolveEEGViewerPlaybackMaxBlitFraction(this.config.playbackSpeed)
      : 0.25;
    // Check whether the blit canvas matches current canvas dimensions.
    const blitReady =
      !this._blitDirty &&
      this._blitCanvas !== null &&
      this._blitCanvas.width === width &&
      this._blitCanvas.height === height;
    // Rapid-review play: when the playhead did not advance a full pixel (or
    // briefly reports reverse under dual-video+k6), keep the cached waveform
    // instead of falling through to a full redraw. Those no-op/reverse paints
    // were the dominant blit-gate failure at 60x–150x under soak load.
    if (allowTrueOverlapBlitAtHighSpeedPlay && blitReady && deltaPx <= 0) {
      this._lastWaveformRenderMetrics = {
        useBlit: true,
        blitReady,
        deltaPx,
        clipLeft: width,
        rawClipLeft: width,
        clipWidth: 0,
        blitSeamOverlapPx: 0,
        fullRedraw: false,
        blitBlockReason: null,
        maxBlitFraction,
        transitionFrameIndex: isPostFlushTransitionFrame
          ? this._postFlushTransitionFrameOrdinal + 1
          : null,
        transitionBlitRelaxed,
        rapidReviewSourceStrideMax: 1,
        envelopeChannelCount: 0,
        envelopeRenderStrideMin: null,
        envelopeRenderStrideMax: null,
        envelopeSamplesPerPixelMax: 0,
        paintLuma: null,
        paintLumaDelta: null,
        paintTransition: "held",
      };
      this.ctx.drawImage(this._blitCanvas!, 0, 0);
      this.samplePaintConsistency("held");
      return;
    }
    // Normal playback keeps a conservative blit threshold because the redraw
    // strip grows quickly once the frame delta gets large. At 100x play the
    // expensive path is the fallback full redraw itself, so use exact overlap
    // reuse whenever any overlap remains (deltaPx < width), not only during
    // the immediate post-flush transition frames.
    const useBlit = allowTrueOverlapBlitAtHighSpeedPlay
      ? blitReady && deltaPx > 0 && deltaPx < plotWidth
      : blitReady && deltaPx > 0 && deltaPx < plotWidth * maxBlitFraction;
    const blitBlockReason = useBlit
      ? null
      : this._blitDirty
        ? "dirty"
        : this._blitCanvas === null
          ? "cache-missing"
          : this._blitCanvas.width !== width || this._blitCanvas.height !== height
            ? "size-mismatch"
            : deltaPx <= 0
              ? "delta-not-forward"
              : deltaPx >= plotWidth
                ? "delta-exceeds-canvas"
                : deltaPx >= plotWidth * maxBlitFraction
                  ? "delta-exceeds-threshold"
                  : "unknown";
    // clipLeft: leftmost pixel column that needs new waveform data drawn.
    // In full-redraw mode every plot column is drawn (clipLeft = plotLeft).
    //
    // Blit shifts cached pixels by whole columns, while the source samples and
    // anti-aliased strokes often land on fractional positions. Redrawing a
    // small overlap on the cached side prevents the visual seam from showing up
    // as random breaks during playback.
    const rawClipLeft = useBlit ? width - deltaPx : plotLeft;
    const blitSeamOverlapPx = useBlit
      ? Math.min(16, Math.max(4, Math.ceil(plotWidth * 0.004)))
      : 0;
    const clipLeft = Math.max(
      plotLeft,
      Math.floor(rawClipLeft - blitSeamOverlapPx),
    );
    this._lastWaveformRenderMetrics = {
      useBlit,
      blitReady,
      deltaPx,
      clipLeft,
      rawClipLeft,
      clipWidth: width - clipLeft,
      blitSeamOverlapPx,
      fullRedraw: !useBlit,
      blitBlockReason,
      maxBlitFraction,
      transitionFrameIndex: isPostFlushTransitionFrame
        ? this._postFlushTransitionFrameOrdinal + 1
        : null,
      transitionBlitRelaxed,
      rapidReviewSourceStrideMax: 1,
      envelopeChannelCount: 0,
      envelopeRenderStrideMin: null,
      envelopeRenderStrideMax: null,
      envelopeSamplesPerPixelMax: 0,
      paintLuma: null,
      paintLumaDelta: null,
      paintTransition: null,
    };
    const recordEnvelopeStride = (
      rapidReviewSourceStride: number,
      envelopeRenderStride: number,
      samplesPerPixel: number,
      hasEnvelope: boolean,
    ) => {
      const metrics = this._lastWaveformRenderMetrics;
      if (!metrics) return;
      metrics.rapidReviewSourceStrideMax = Math.max(
        metrics.rapidReviewSourceStrideMax,
        rapidReviewSourceStride,
      );
      if (!hasEnvelope) return;
      metrics.envelopeChannelCount += 1;
      metrics.envelopeRenderStrideMin =
        metrics.envelopeRenderStrideMin === null
          ? envelopeRenderStride
          : Math.min(metrics.envelopeRenderStrideMin, envelopeRenderStride);
      metrics.envelopeRenderStrideMax =
        metrics.envelopeRenderStrideMax === null
          ? envelopeRenderStride
          : Math.max(metrics.envelopeRenderStrideMax, envelopeRenderStride);
      metrics.envelopeSamplesPerPixelMax = Math.max(
        metrics.envelopeSamplesPerPixelMax,
        samplesPerPixel,
      );
    };

    if (useBlit) {
      // The blit operation (shift + clip + composite) is handled in the
      // "Prepare blit canvas" section below, after the blit canvas is
      // guaranteed to exist and have the correct dimensions.
    } else if (process.env.NODE_ENV !== "production" && this._blitDirty) {
      // Log full redraws caused by a dirty blit cache (staleness or data change).
      // Throttle to avoid spam: only log when the cache was explicitly marked dirty.
      logger.info("[EEGRenderer] FULL_REDRAW (blit dirty)", {
        currentTime: this.currentTime.toFixed(2),
        dataTimeOffset: this.dataTimeOffset.toFixed(2),
        blitTime:
          this._blitTime === -Infinity ? "none" : this._blitTime.toFixed(2),
        deltaPx,
        blitBlockReason,
      });
    }
    // ─────────────────────────────────────────────────────────────────────────

    const waveformArea = this.getWaveformArea();
    const waveformAreaTop = waveformArea.top;
    const waveformAreaHeight = waveformArea.height - 2; // Minimal bottom margin
    const visibleMontageChannels = this.getVisibleMontageChannels();
    const channelCount = visibleMontageChannels.length;
    if (channelCount <= 0) {
      this._lastWaveformRenderMetrics = null;
      return;
    }
    const layoutChannelCount = this.getLayoutVisibleChannelCount(channelCount);
    const channelHeight = waveformAreaHeight / layoutChannelCount;
    // Ensure envelope scratch buffers are wide enough for the current canvas.
    // Growing only; never shrink (re-allocating on every resize would negate the benefit).
    const envPxCount = Math.ceil(width);
    if (envPxCount > this.envMinYs.length) {
      this.envMinYs = new Float32Array(envPxCount);
      this.envMaxYs = new Float32Array(envPxCount);
      this.envSumYs = new Float64Array(envPxCount);
      this.envCountPx = new Uint16Array(envPxCount);
    }
    // Calculate start and end sample indices, accounting for data time offset.
    // The loaded data may not start at t=0; dataTimeOffset tells us where sample[0] begins.
    const startTime = this.currentTime;
    const endTime = startTime + this.config.timeScale;
    const dataLength =
      this.channels.length > 0 ? this.channels[0].data.length : 0;
    // Ideal sample range (may be out of bounds of loaded data)
    const idealStartSample = Math.floor(
      (startTime - this.dataTimeOffset) * this.sampleRate,
    );
    const startSample = Math.max(0, idealStartSample);
    const endSample = Math.min(
      dataLength,
      Math.ceil((endTime - this.dataTimeOffset) * this.sampleRate),
    );
    // Fixed samplesPerPage: always based on timeScale so X-coordinates are accurate.
    // When data is partial (end of recording), waveforms occupy correct portion of width.
    const samplesPerPage = Math.ceil(this.config.timeScale * this.sampleRate);
    // X offset in samples when startSample was clamped (data doesn't cover start of window)
    const sampleXOffset = startSample - idealStartSample;
    const hasSelection = this.selectedMontageChannelLabels.size > 0;
    const spacingMultiplier = this.getSafeChannelSpacing();
    const baseLineWidth = this.getSafeWaveformThickness();
    const isRapidReviewPlay =
      this.config.playbackTransportMode === "play" &&
      this.config.playbackSpeed >= 30;
    const rapidReviewSourceStrideFor = (sourceLength: number) =>
      resolveRapidReviewSourceStride({
        playbackSpeed: this.config.playbackSpeed,
        transportMode: this.config.playbackTransportMode,
        sourceLength,
        pixelWidth: plotWidth,
      });
    const filtersInactive =
      this.config.highPassFilter <= 0 &&
      this.config.lowPassFilter <= 0 &&
      !this.config.notchFilter &&
      !this.config.notch50Hz &&
      !this.config.removeDcOffset;

    // Debug sampling calculation (throttled to once per second)
    this.frameCount++;
    const shouldLog = this.frameCount % 300 === 0;

    if (shouldLog) {
      // throttled sampling debug (dev only — disable in production builds)
    }

    if (visibleMontageChannels.length > 0 && shouldLog) {
      (window as unknown as Record<string, unknown>).__samplingDebug = {
        currentTime: this.currentTime,
        timeScale: this.config.timeScale,
        sampleRate: this.sampleRate,
        startSample,
        endSample,
        samplesPerPage,
      };
    }

    // ── Prepare blit canvas and redirect waveform rendering ─────────────────
    // All waveform strokes are drawn into _blitCtx (transparent background)
    // rather than the main ctx.  After rendering we composite the blit canvas
    // onto the main canvas so the grid drawn before drawWaveforms() shows through.
    //
    // Scheme:
    //   Full path:  blitCtx cleared → all waveform strokes drawn → copy to mainCtx.
    //   Blit path:  blitCtx shifted left by deltaPx (self-draw, snapshot-safe per
    //               HTML spec §4.12.5) → right strip cleared → right-strip strokes
    //               drawn → _blitCanvas now holds full-width waveform layer →
    //               copy to mainCtx.
    if (
      !this._blitCanvas ||
      this._blitCanvas.width !== width ||
      this._blitCanvas.height !== height
    ) {
      this._blitCanvas = document.createElement("canvas");
      this._blitCanvas.width = width;
      this._blitCanvas.height = height;
      this._blitCtx = this._blitCanvas.getContext("2d");
      // Disable smoothing so drawImage shifts never blur 1px strokes into
      // adjacent pixels — prevents the accumulated thickness visible in the
      // first seconds of playback after blit warmup.
      if (this._blitCtx) this._blitCtx.imageSmoothingEnabled = false;
    }
    const blitCtx = this._blitCtx;
    const mainCtx = this.ctx;

    if (blitCtx) {
      if (useBlit) {
        // Shift the existing waveform layer left by deltaPx.
        // `drawImage(canvas, ...)` onto the same canvas is snapshot-safe; using
        // `copy` avoids the extra full-surface snapshot canvas and prevents the
        // source-over ghost trails that accumulated in the old self-blit path.
        blitCtx.save();
        blitCtx.globalCompositeOperation = "copy";
        blitCtx.drawImage(this._blitCanvas!, -deltaPx, 0);
        blitCtx.restore();
        blitCtx.clearRect(0, 0, plotLeft, height);
        // The right strip was not covered by the shifted drawImage (it grew from
        // pixels that moved off the left edge), but clear it anyway for safety.
        blitCtx.clearRect(clipLeft, 0, width - clipLeft, height);
      } else {
        // Full redraw: start from a blank transparent canvas.
        blitCtx.clearRect(0, 0, width, height);
      }
      // Redirect all ctx calls to the blit canvas for the rendering phase.
      this.ctx = blitCtx as unknown as CanvasRenderingContext2D;
    }
    // ─────────────────────────────────────────────────────────────────────────

    // ── Batched rendering for uniform color mode (no selection) ───────────────
    // In uniform mode every channel shares the same strokeStyle.  We accumulate
    // ALL channels' envelope-band segments into ONE canvas path and ALL mean-line
    // segments into another, then flush each with a single stroke() call.
    // Reduces GPU flushes from 74 (2 per channel × 37 channels) to 2, dropping
    // frame time from ~37 ms to ~2 ms and fixing the consistent avgFrameMs > 33
    // that was triggering degraded_playback_detected at every 10-second perf window.
    const colorMode = this.config.channelColorMode || "uniform";
    // Collect the distinct resolved channel colors.  Batching is still valid
    // when all channels share the SAME db-driven colour (size <= 1).  It only
    // breaks when channels have genuinely different colours (size > 1).
    const _pcColorSet = new Set(
      visibleMontageChannels
        .map(({ channel }) => this.normalizeStrokeColor(channel.color))
        .filter(Boolean),
    );
    const hasPerChannelColors = _pcColorSet.size > 1;
    // Fast 2-stroke batch: no selection + all channels share one color (or no
    // explicit color).  Dropping the old `colorMode === "uniform"` guard lets
    // by-type / alternating modes still use this path whenever every channel
    // resolves to the same DB-driven color (common for pure EEG recordings).
    const useBatchedRendering = !hasSelection && !hasPerChannelColors;
    // Group-batch: selection-free but genuinely multi-color (e.g., DB colors
    // differ per channel pair).  2×K strokes (K = distinct colors) instead of
    // 2×N strokes, keeping frame time well below the 33 ms budget.

    const allowGpuEnvelopeOnHighSpeedBlit =
      useBlit &&
      allowTrueOverlapBlitAtHighSpeedPlay &&
      width - clipLeft >= width * 0.5;
    const gpuEnvelopeStartPx = useBlit ? clipLeft : plotLeft;
    const gpuEnvelopeCopyWidth = width - gpuEnvelopeStartPx;

    // GPU envelope path: always available on full redraws and also on large
    // high-speed overlap blit strips where the 2D envelope band becomes the
    // dominant remaining redraw cost. For per-channel color schemes, keep the
    // envelope on Canvas 2D so each channel retains its own color.
    const useGpuEnvelope =
      !hasPerChannelColors &&
      !!this._webglAdapter &&
      this._webglAdapter.isSupported() &&
      !!this._gpuCanvas &&
      (!useBlit || allowGpuEnvelopeOnHighSpeedBlit);
    // Keep CPU envelopes available for rapid-review envelope-overview. Paint
    // cost is bounded by source stride + deferred overlays instead.
    const suppressCpuEnvelopeAtUltraHighSpeed = false;

    // Grow per-channel GPU pool arrays to cover the current channelCount × width.
    if (useGpuEnvelope) {
      while (this._gpuChanTopY.length < channelCount) {
        this._gpuChanTopY.push(new Float32Array(envPxCount));
        this._gpuChanBotY.push(new Float32Array(envPxCount));
      }
      for (let i = 0; i < channelCount; i++) {
        if (this._gpuChanTopY[i].length < envPxCount) {
          this._gpuChanTopY[i] = new Float32Array(envPxCount);
          this._gpuChanBotY[i] = new Float32Array(envPxCount);
        }
      }
    }
    const useGroupBatchRendering = !hasSelection && hasPerChannelColors;
    const polarityMultiplier = this.config.polarity === "positive-up" ? 1 : -1;

    if (shouldLog && process.env.NODE_ENV !== "production") {
      logger.debug("[EEGRenderer] drawWaveforms", {
        useBatched: useBatchedRendering,
        useGroupBatch: useGroupBatchRendering,
        colorMode,
        hasSelection,
        hasPerChannelColors,
        distinctColors: _pcColorSet.size,
        channelCount,
        startSample,
        endSample,
      });
    }

    if (useBatchedRendering) {
      // Grow pre-allocated batch arrays lazily — never shrink to avoid GC.
      this._ensureBatchArrays(channelCount);

      const fixedAmplitudeScalingGlobal =
        this.config.amplitudeScalingMode === "fixed";
      const pxPerMm = this.getSafePixelsPerMm();
      const sensitivity = this.config.sensitivity;
      const sensitivityUvMm = Math.max(
        0.1,
        sensitivity || BASELINE_SENSITIVITY_UV_PER_MM,
      );
      const fixedScale = pxPerMm / sensitivityUvMm;
      const batchScaleMultiplier = calculateWaveformScaleMultiplier(
        sensitivity,
        pxPerMm,
      );

      // ── Phase 1: compute per-channel stats + draw all envelope bands ────────
      // Use the shared DB colour when all channels agreed; else fall back to config.
      const batchColor =
        (_pcColorSet.size === 1 ? [..._pcColorSet][0] : undefined) ??
        this.config.waveformColor ??
        "#f8fafc";
      this.ctx.save();
      this.ctx.strokeStyle = batchColor;
      this.ctx.lineWidth = baseLineWidth;
      this.ctx.lineCap = "butt";
      this.ctx.lineJoin = "miter";
      this.ctx.globalAlpha = 0.35;
      if (!useGpuEnvelope) this.ctx.beginPath();

      for (let ci = 0; ci < channelCount; ci++) {
        const mc = visibleMontageChannels[ci].channel;
        const channelData = this.getMontageChannelData(mc);
        if (!channelData) {
          this._batchFiltered[ci] = null;
          continue;
        }
        const montageDisplayGeometry =
          isRapidReviewPlay && filtersInactive
            ? this.getMontageDisplayGeometry(mc)
            : null;
        const useDisplayGeometry =
          !!montageDisplayGeometry &&
          montageDisplayGeometry.mean.length > 0 &&
          montageDisplayGeometry.sourceSamplesPerBin > 1;
        const sourceSamplesPerBin = useDisplayGeometry
          ? Math.max(1, montageDisplayGeometry.sourceSamplesPerBin)
          : 1;
        const displayStartBin = useDisplayGeometry
          ? Math.max(
              0,
              Math.min(
                montageDisplayGeometry.mean.length,
                Math.floor(startSample / sourceSamplesPerBin),
              ),
            )
          : 0;
        const displayEndBin = useDisplayGeometry
          ? Math.max(
              displayStartBin,
              Math.min(
                montageDisplayGeometry.mean.length,
                Math.ceil(endSample / sourceSamplesPerBin),
              ),
            )
          : 0;
        const filteredData = useDisplayGeometry
          ? montageDisplayGeometry.mean.subarray(displayStartBin, displayEndBin)
          : this.applyFilters(channelData, startSample, endSample);
        if (!filteredData.length) {
          this._batchFiltered[ci] = null;
          continue;
        }
        this._batchFiltered[ci] = filteredData;

        const trueEnvelope = useDisplayGeometry
          ? {
              min: montageDisplayGeometry.min.subarray(
                displayStartBin,
                displayEndBin,
              ),
              max: montageDisplayGeometry.max.subarray(
                displayStartBin,
                displayEndBin,
              ),
            }
          : this.getMontageChannelEnvelope(mc);
        this._batchEnvelopes[ci] = trueEnvelope;
        const envelopeIndexBase = useDisplayGeometry ? 0 : startSample;
        const channelSampleXOffset = useDisplayGeometry
          ? sampleXOffset / sourceSamplesPerBin +
            (startSample / sourceSamplesPerBin - displayStartBin)
          : sampleXOffset;
        const channelSamplesPerPage = useDisplayGeometry
          ? Math.max(1, samplesPerPage / sourceSamplesPerBin)
          : samplesPerPage;

        // When pyramid envelope data is present, the base channel column is a
        // subsampled point that may fall OUTSIDE [min, max] (different decimation
        // phase).  Use (min+max)/2 as the representative value so stats and the
        // mean-line track the centre of the envelope band, eliminating the visual
        // disconnection that causes blur at 150x.
        const repData: Float32Array | number[] =
          trueEnvelope && !isRapidReviewPlay
            ? (() => {
                const mid = new Float32Array(filteredData.length);
                for (let i = 0; i < filteredData.length; i++)
                  mid[i] =
                    (trueEnvelope.min[envelopeIndexBase + i] +
                      trueEnvelope.max[envelopeIndexBase + i]) *
                    0.5;
                return mid;
              })()
            : filteredData;
        this._batchRepData[ci] = repData;
        const rapidReviewSourceStride = rapidReviewSourceStrideFor(
          filteredData.length,
        );

        // When trueEnvelope is present, repData is the unfiltered envelope midpoint
        // which includes DC drift. Using repData for dataRange inflates it (e.g. 3.5×
        // for channels with large DC drift), making scale too small → flat waveform.
        // Use filteredData (HP-filtered, DC-removed) for scale computation instead.
        // dataMean still comes from repData to correctly centre the mean-line.
        const scaleSource = trueEnvelope ? filteredData : repData;
        let dataMin = Infinity;
        let dataMax = -Infinity;
        let dataSum = 0;
        let scaleSampleCount = 0;
        for (let i = 0; i < scaleSource.length; i += rapidReviewSourceStride) {
          const v = scaleSource[i];
          if (v < dataMin) dataMin = v;
          if (v > dataMax) dataMax = v;
          scaleSampleCount++;
        }
        let meanSampleCount = 0;
        for (let i = 0; i < repData.length; i += rapidReviewSourceStride) {
          dataSum += repData[i];
          meanSampleCount++;
        }
        const dataMean =
          dataSum / Math.max(1, meanSampleCount || scaleSampleCount);
        const dataRange = dataMax - dataMin || 1;

        const baseY = this.getWaveformBaselineY(
          ci,
          mc.label,
          channelHeight,
          waveformAreaTop,
          waveformAreaTop + waveformAreaHeight,
        );
        const { usableHeight, channelTopBound, channelBottomBound } =
          this.getWaveformVerticalBounds(
            baseY,
            channelHeight,
            waveformAreaTop,
            waveformAreaHeight,
            spacingMultiplier,
            fixedAmplitudeScalingGlobal,
          );
        const scale = fixedAmplitudeScalingGlobal
          ? fixedScale
          : (usableHeight / dataRange) * batchScaleMultiplier;
        const channelGain = this.getChannelGainMultiplier(mc.label);

        // Store stats for Phase 2 (no recomputation needed)
        this._batchDataMeans[ci] = dataMean;
        this._batchScales[ci] = scale;
        this._batchBaseYs[ci] = baseY;
        this._batchTopBounds[ci] = channelTopBound;
        this._batchBotBounds[ci] = channelBottomBound;
        this._batchGains[ci] = channelGain;
        this._batchSampleXOffsets[ci] = channelSampleXOffset;
        this._batchSamplesPerPage[ci] = channelSamplesPerPage;
        this._batchEnvelopeIndexBases[ci] = envelopeIndexBase;
        this._batchUseDisplayGeometry[ci] = useDisplayGeometry ? 1 : 0;
        this._batchFixed[ci] = fixedAmplitudeScalingGlobal ? 1 : 0;

        // Add this channel's envelope-band segments to the shared path
        const samplesPerPixel = filteredData.length / plotWidth;
        const envelopeRenderStride = resolveEnvelopeRenderStride({
          hasEnvelope: !!trueEnvelope,
          rapidReviewSourceStride,
          samplesPerPixel,
        });
        recordEnvelopeStride(
          rapidReviewSourceStride,
          envelopeRenderStride,
          samplesPerPixel,
          !!trueEnvelope,
        );
        if (
          !suppressCpuEnvelopeAtUltraHighSpeed &&
          filteredData.length > 0 &&
          (samplesPerPixel > 4 || trueEnvelope)
        ) {
          const pixelCount = envPxCount;
          const minYs = this.envMinYs;
          const maxYs = this.envMaxYs;
          // In blit mode only fill / accumulate the right strip.
          minYs.fill(channelTopBound, clipLeft, pixelCount);
          maxYs.fill(channelBottomBound, clipLeft, pixelCount);

          for (
            let i = 0;
            i < filteredData.length;
            i += envelopeRenderStride
          ) {
            const px = Math.floor(
              plotLeft +
              ((i + channelSampleXOffset) / channelSamplesPerPage) * plotWidth,
            );
            if (px < clipLeft || px >= pixelCount) continue;
            // Shift envelope bounds by the same DC the HP filter removed from the
            // representative sample.  Without this, channels with large baseline
            // drift have their envelope band clamped off-screen.
            // Use repData (envelope midpoint) as the reference so the DC shift
            // is consistent with the mean-line's reference frame.
            const envDcShift =
              !useDisplayGeometry &&
              trueEnvelope &&
              this.config.highPassFilter > 0 &&
              channelData
                ? channelData[startSample + i] - repData[i]
                : 0;
            const rawMin = trueEnvelope
              ? (trueEnvelope.min[envelopeIndexBase + i] ?? repData[i]) -
                envDcShift
              : repData[i];
            const rawMax = trueEnvelope
              ? (trueEnvelope.max[envelopeIndexBase + i] ?? repData[i]) -
                envDcShift
              : repData[i];
            const toY = (raw: number): number => {
              const norm = fixedAmplitudeScalingGlobal ? raw : raw - dataMean;
              const oriented = norm * polarityMultiplier * channelGain;
              const rawY = baseY - oriented * scale;
              return clampWaveformsToLane
                ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
                : rawY;
            };
            const yForMin = toY(rawMin);
            const yForMax = toY(rawMax);
            const topY = Math.min(yForMin, yForMax);
            const botY = Math.max(yForMin, yForMax);
            // Skip samples where the clamped band fills ≥90% of the channel height.
            // Happens when trueEnvelope spread >> filteredDataRange (the scale
            // reference after the L3 flat-channel fix): both rawMin and rawMax
            // clamp to opposite channel bounds, producing a full-height shadow band
            // at 0.35α that appears as a dark stripe on right-temporal channels.
            if (botY - topY > (channelBottomBound - channelTopBound) * 0.9)
              continue;
            if (topY < maxYs[px]) maxYs[px] = topY;
            if (botY > minYs[px]) minYs[px] = botY;
          }

          if (useGpuEnvelope) {
            // Copy pixel Y-bounds into the GPU pool, applying the same sentinel guard
            // used by the CPU path.  Pixels with no data coverage keep their initial
            // sentinel values (maxYs=channelBottomBound, minYs=channelTopBound), which
            // span the FULL channel height.  Passing those directly to WebGL LINES would
            // draw a visible full-height band in the no-data region (e.g. at the page
            // boundary when sampleXOffset > 0).  Collapsing them to zero-height (0,0)
            // makes the degenerate GL LINES segment invisible.
            for (let px = gpuEnvelopeStartPx; px < pixelCount; px++) {
              const top = maxYs[px]; // topmost canvas Y (smallest value)
              const bot = minYs[px]; // bottommost canvas Y (largest value)
              if (top >= channelBottomBound || bot <= channelTopBound) {
                // No data covered this pixel — collapse to zero-height so GPU skips it
                this._gpuChanTopY[ci][px] = 0;
                this._gpuChanBotY[ci][px] = 0;
              } else {
                this._gpuChanTopY[ci][px] = top;
                this._gpuChanBotY[ci][px] = bot;
              }
            }
          } else {
            if (trueEnvelope && samplesPerPixel <= 1.0) {
              this.ctx.save();
              this.ctx.fillStyle = batchColor;
              this.ctx.globalAlpha = 0.18;
              this.ctx.beginPath();
              this.appendEnvelopeBandPolygon(
                clipLeft,
                pixelCount,
                channelTopBound,
                channelBottomBound,
              );
              this.ctx.fill();
              this.ctx.restore();
            } else {
              for (let px = clipLeft; px < pixelCount; px++) {
                const top = maxYs[px];
                const bot = minYs[px];
                if (top >= channelBottomBound || bot <= channelTopBound)
                  continue;
                this.ctx.moveTo(px + 0.5, top);
                this.ctx.lineTo(px + 0.5, bot);
              }
            }
          }
        } else if (useGpuEnvelope) {
          this._gpuChanTopY[ci].fill(0, gpuEnvelopeStartPx, envPxCount);
          this._gpuChanBotY[ci].fill(0, gpuEnvelopeStartPx, envPxCount);
        }
      }
      // ONE stroke for ALL channels' envelope bands — or GPU blit when GPU path active.
      if (useGpuEnvelope && this._webglAdapter && this._gpuCanvas) {
        // Ensure GPU canvas matches current main canvas dimensions.
        if (
          this._gpuCanvas.width !== width ||
          this._gpuCanvas.height !== height
        ) {
          this._gpuCanvas.width = width;
          this._gpuCanvas.height = height;
        }
        const envelopeChannels = visibleMontageChannels
          .slice(0, channelCount)
          .map(({ channel }, ci) => ({
            name: channel.label,
            yMin: this._gpuChanTopY[ci].subarray(
              gpuEnvelopeStartPx,
              envPxCount,
            ), // topmost pixel (smallest Y) = "min" in WebGL terms
            yMax: this._gpuChanBotY[ci].subarray(
              gpuEnvelopeStartPx,
              envPxCount,
            ), // bottommost pixel (largest Y) = "max" in WebGL terms
          }));
        const rendered =
          gpuEnvelopeCopyWidth > 0 &&
          this._webglAdapter.render({
            channels: envelopeChannels,
            width,
            height,
            xOffset: gpuEnvelopeStartPx,
            clear: [0, 0, 0, 0], // transparent background so blit layer shows through
          });
        if (rendered && this._blitCtx) {
          this._blitCtx.save();
          this._blitCtx.globalAlpha = 0.35; // match CPU envelope alpha
          this._blitCtx.drawImage(
            this._gpuCanvas,
            gpuEnvelopeStartPx,
            0,
            gpuEnvelopeCopyWidth,
            height,
            gpuEnvelopeStartPx,
            0,
            gpuEnvelopeCopyWidth,
            height,
          );
          this._blitCtx.restore();
        }
      } else {
        this.ctx.stroke();
      }

      // ── Phase 2: draw all mean lines ─────────────────────────────────────────
      this.ctx.globalAlpha = 1.0;
      this.ctx.beginPath();

      for (let ci = 0; ci < channelCount; ci++) {
        const filteredData = this._batchFiltered[ci];
        if (!filteredData) continue;

        // Use envelope midpoint as representative when pyramid data is present.
        const repData = this._batchRepData[ci] ?? filteredData;
        const rapidReviewSourceStride = rapidReviewSourceStrideFor(
          repData.length,
        );
        const dataMean = this._batchDataMeans[ci];
        const scale = this._batchScales[ci];
        const baseY = this._batchBaseYs[ci];
        const channelTopBound = this._batchTopBounds[ci];
        const channelBottomBound = this._batchBotBounds[ci];
        const channelGain = this._batchGains[ci];
        const channelSampleXOffset = this._batchSampleXOffsets[ci];
        const channelSamplesPerPage =
          this._batchSamplesPerPage[ci] > 0
            ? this._batchSamplesPerPage[ci]
            : samplesPerPage;
        const fixedAmpScaling = this._batchFixed[ci] === 1;

        const samplesPerPixel = filteredData.length / plotWidth;
        const useEnvelopeOnly =
          !!this._batchEnvelopes[ci] && samplesPerPixel <= 1.0;
        const suppressMeanTraceForChannel =
          shouldUseRapidReviewEnvelopeOverview({
            playbackSpeed: this.config.playbackSpeed,
            transportMode: this.config.playbackTransportMode,
            hasEnvelope: !!this._batchEnvelopes[ci],
          }) &&
          !suppressCpuEnvelopeAtUltraHighSpeed;
        if (useEnvelopeOnly || suppressMeanTraceForChannel) continue;
        if (filteredData.length > 0 && samplesPerPixel > 1.0) {
          const pixelCount = envPxCount;
          const sumYs = this.envSumYs;
          const countPx = this.envCountPx;
          sumYs.fill(0, clipLeft, pixelCount);
          countPx.fill(0, clipLeft, pixelCount);

          for (let i = 0; i < repData.length; i += rapidReviewSourceStride) {
            const px = Math.floor(
              plotLeft +
              ((i + channelSampleXOffset) / channelSamplesPerPage) * plotWidth,
            );
            if (px < clipLeft || px >= pixelCount) continue;
            const normalizedValue = fixedAmpScaling
              ? repData[i]
              : repData[i] - dataMean;
            const orientedValue =
              normalizedValue * polarityMultiplier * channelGain;
            const rawY = baseY - orientedValue * scale;
            const y = clampWaveformsToLane
              ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
              : rawY;
            sumYs[px] += y;
            countPx[px]++;
          }

          let meanStarted = false;
          for (let px = clipLeft; px < pixelCount; px++) {
            if (countPx[px] === 0) {
              meanStarted = false;
              continue;
            }
            const meanY = sumYs[px] / countPx[px];
            if (!meanStarted) {
              this.ctx.moveTo(px + 0.5, meanY);
              meanStarted = true;
            } else {
              this.ctx.lineTo(px + 0.5, meanY);
            }
          }
        } else {
          // Sample-by-sample mode: sub-pixel density or no envelope — use lineTo
          // for a fully connected waveform trace (no gaps at 150× still-view).
          // Uses repData (envelope midpoint) when available for consistency.
          for (let i = 0; i < repData.length; i++) {
            const x =
              plotLeft + ((i + sampleXOffset) / samplesPerPage) * plotWidth;
            if (x < clipLeft) continue;
            const normalizedValue = fixedAmpScaling
              ? repData[i]
              : repData[i] - dataMean;
            const orientedValue =
              normalizedValue * polarityMultiplier * channelGain;
            const rawY = baseY - orientedValue * scale;
            const y = clampWaveformsToLane
              ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
              : rawY;
            const prevX =
              plotLeft +
              ((i - 1 + sampleXOffset) / samplesPerPage) * plotWidth;
            if (i === 0 || prevX < clipLeft) {
              this.ctx.moveTo(x, y);
            } else {
              this.ctx.lineTo(x, y);
            }
          }
        }
      }
      // ONE stroke for ALL channels' mean lines
      this.ctx.stroke();
      this.ctx.restore();
    } else if (useGroupBatchRendering) {
      // ── Group-batch: 2×K strokes where K = number of distinct colors ─────────
      // Channels with different DB-driven colors are batched by color group.
      // This keeps stroke() calls at 2×K (typically 4–18) vs 2×N (36–74) for a
      // full 10-20 montage, staying well within the 33 ms/frame budget.

      // Grow pre-allocated batch arrays (same as fast-batch path).
      this._ensureBatchArrays(channelCount);

      const fixedAmplitudeScalingGlobal =
        this.config.amplitudeScalingMode === "fixed";
      const pxPerMm = this.getSafePixelsPerMm();
      const sensitivity = this.config.sensitivity;
      const sensitivityUvMm = Math.max(
        0.1,
        sensitivity || BASELINE_SENSITIVITY_UV_PER_MM,
      );
      const fixedScaleGroup = pxPerMm / sensitivityUvMm;
      const batchScaleMultiplierGroup = calculateWaveformScaleMultiplier(
        sensitivity,
        pxPerMm,
      );
      const defaultGroupColor = this.config.waveformColor ?? "#f8fafc";

      // Pass A: compute per-channel stats + store filtered data (no drawing yet).
      for (let ci = 0; ci < channelCount; ci++) {
        this._batchFiltered[ci] = null;
        this._batchEnvelopes[ci] = null;
        const mc = visibleMontageChannels[ci].channel;
        const channelData = this.getMontageChannelData(mc);
        if (!channelData) continue;
        const montageDisplayGeometry =
          isRapidReviewPlay && filtersInactive
            ? this.getMontageDisplayGeometry(mc)
            : null;
        const useDisplayGeometry =
          !!montageDisplayGeometry &&
          montageDisplayGeometry.mean.length > 0 &&
          montageDisplayGeometry.sourceSamplesPerBin > 1;
        const sourceSamplesPerBin = useDisplayGeometry
          ? Math.max(1, montageDisplayGeometry.sourceSamplesPerBin)
          : 1;
        const displayStartBin = useDisplayGeometry
          ? Math.max(
              0,
              Math.min(
                montageDisplayGeometry.mean.length,
                Math.floor(startSample / sourceSamplesPerBin),
              ),
            )
          : 0;
        const displayEndBin = useDisplayGeometry
          ? Math.max(
              displayStartBin,
              Math.min(
                montageDisplayGeometry.mean.length,
                Math.ceil(endSample / sourceSamplesPerBin),
              ),
            )
          : 0;
        const filteredData = useDisplayGeometry
          ? montageDisplayGeometry.mean.subarray(displayStartBin, displayEndBin)
          : this.applyFilters(channelData, startSample, endSample);
        if (!filteredData.length) continue;
        this._batchFiltered[ci] = filteredData;
        const trueEnvelopeG = useDisplayGeometry
          ? {
              min: montageDisplayGeometry.min.subarray(
                displayStartBin,
                displayEndBin,
              ),
              max: montageDisplayGeometry.max.subarray(
                displayStartBin,
                displayEndBin,
              ),
            }
          : this.getMontageChannelEnvelope(mc);
        this._batchEnvelopes[ci] = trueEnvelopeG;
        const envelopeIndexBase = useDisplayGeometry ? 0 : startSample;
        const channelSampleXOffset = useDisplayGeometry
          ? sampleXOffset / sourceSamplesPerBin +
            (startSample / sourceSamplesPerBin - displayStartBin)
          : sampleXOffset;
        const channelSamplesPerPage = useDisplayGeometry
          ? Math.max(1, samplesPerPage / sourceSamplesPerBin)
          : samplesPerPage;

        // When pyramid envelope data is present, use (min+max)/2 as representative.
        const repDataG: Float32Array | number[] =
          trueEnvelopeG && !isRapidReviewPlay
            ? (() => {
                const mid = new Float32Array(filteredData.length);
                for (let i = 0; i < filteredData.length; i++)
                  mid[i] =
                    (trueEnvelopeG.min[startSample + i] +
                      trueEnvelopeG.max[startSample + i]) *
                    0.5;
                return mid;
              })()
            : filteredData;
        this._batchRepData[ci] = repDataG;
        const rapidReviewSourceStride = rapidReviewSourceStrideFor(
          filteredData.length,
        );

        // Same DC-drift fix as fast-batch path: use filteredData for dataRange
        // when trueEnvelope is present; dataMean from repDataG for centering.
        const scaleSourceG = trueEnvelopeG ? filteredData : repDataG;
        let dataMin = Infinity;
        let dataMax = -Infinity;
        let dataSum = 0;
        for (let i = 0; i < scaleSourceG.length; i += rapidReviewSourceStride) {
          const v = scaleSourceG[i];
          if (v < dataMin) dataMin = v;
          if (v > dataMax) dataMax = v;
        }
        let meanSampleCount = 0;
        for (let i = 0; i < repDataG.length; i += rapidReviewSourceStride) {
          dataSum += repDataG[i];
          meanSampleCount++;
        }
        const dataMean = dataSum / Math.max(1, meanSampleCount);
        const dataRange = dataMax - dataMin || 1;

        const baseY = this.getWaveformBaselineY(
          ci,
          mc.label,
          channelHeight,
          waveformAreaTop,
          waveformAreaTop + waveformAreaHeight,
        );
        const { usableHeight, channelTopBound, channelBottomBound } =
          this.getWaveformVerticalBounds(
            baseY,
            channelHeight,
            waveformAreaTop,
            waveformAreaHeight,
            spacingMultiplier,
            fixedAmplitudeScalingGlobal,
          );
        const scale = fixedAmplitudeScalingGlobal
          ? fixedScaleGroup
          : (usableHeight / dataRange) * batchScaleMultiplierGroup;
        const channelGain = this.getChannelGainMultiplier(mc.label);

        this._batchDataMeans[ci] = dataMean;
        this._batchScales[ci] = scale;
        this._batchBaseYs[ci] = baseY;
        this._batchTopBounds[ci] = channelTopBound;
        this._batchBotBounds[ci] = channelBottomBound;
        this._batchGains[ci] = channelGain;
        this._batchSampleXOffsets[ci] = channelSampleXOffset;
        this._batchSamplesPerPage[ci] = channelSamplesPerPage;
        this._batchEnvelopeIndexBases[ci] = envelopeIndexBase;
        this._batchUseDisplayGeometry[ci] = useDisplayGeometry ? 1 : 0;
        this._batchFixed[ci] = fixedAmplitudeScalingGlobal ? 1 : 0;

        // GPU envelope: populate per-channel pixel Y-bounds during Pass A so the
        // GPU canvas can be rendered once (all channels, single color) before Pass B.
        if (useGpuEnvelope && this._gpuChanTopY[ci]) {
          const filteredData = this._batchFiltered[ci];
          const trueEnvelope = this._batchEnvelopes[ci];
          const samplesPerPixel = filteredData
            ? filteredData.length / plotWidth
            : 0;
            if (
              !suppressCpuEnvelopeAtUltraHighSpeed &&
              filteredData &&
              filteredData.length > 0 &&
              (samplesPerPixel > 4 || trueEnvelope)
          ) {
            const pixelCount = envPxCount;
            const minYs = this.envMinYs;
            const maxYs = this.envMaxYs;
            minYs.fill(channelTopBound, clipLeft, pixelCount);
            maxYs.fill(channelBottomBound, clipLeft, pixelCount);
            const fixedAmpScaling = this._batchFixed[ci] === 1;
            const channelSampleXOffset = this._batchSampleXOffsets[ci];
            const channelSamplesPerPage =
              this._batchSamplesPerPage[ci] > 0
                ? this._batchSamplesPerPage[ci]
                : samplesPerPage;
            const envelopeIndexBase = this._batchEnvelopeIndexBases[ci];
            const useDisplayGeometry = this._batchUseDisplayGeometry[ci] === 1;
            const mcForEnv = visibleMontageChannels[ci].channel;
            const isBipolarChanGpu =
              !!mcForEnv.negativeInput &&
              !mcForEnv.negativeInput.startsWith("EEG LAP_") &&
              !mcForEnv.negativeInput.startsWith("EEG LLAP_") &&
              !mcForEnv.negativeInput.startsWith("EEG HJR_");
            const repDataGpu = this._batchRepData[ci] ?? filteredData;
            const envelopeRenderStride = resolveEnvelopeRenderStride({
              hasEnvelope: !!trueEnvelope,
              rapidReviewSourceStride,
              samplesPerPixel,
            });
            recordEnvelopeStride(
              rapidReviewSourceStride,
              envelopeRenderStride,
              samplesPerPixel,
              !!trueEnvelope,
            );
            for (
              let i = 0;
              i < filteredData.length;
              i += envelopeRenderStride
            ) {
              const px = Math.floor(
                plotLeft +
              ((i + channelSampleXOffset) / channelSamplesPerPage) * plotWidth,
              );
              if (px < clipLeft || px >= pixelCount) continue;
              // DC-shift correction (same as useBatchedRendering Phase 1)
              // Use repData (envelope midpoint) as reference so the shift is
              // consistent with the mean-line drawn in Phase 2.
              // Bipolar channels use repData for both bounds — see isBipolarChanGpu.
              const envDcShift =
                !useDisplayGeometry &&
                !isBipolarChanGpu &&
                trueEnvelope &&
                this.config.highPassFilter > 0 &&
                channelData
                  ? channelData[startSample + i] - repDataGpu[i]
                  : 0;
              const rawMin =
                !isBipolarChanGpu && trueEnvelope
                  ? (trueEnvelope.min[envelopeIndexBase + i] ??
                      repDataGpu[i]) -
                    envDcShift
                  : repDataGpu[i];
              const rawMax =
                !isBipolarChanGpu && trueEnvelope
                  ? (trueEnvelope.max[envelopeIndexBase + i] ??
                      repDataGpu[i]) -
                    envDcShift
                  : repDataGpu[i];
              const toY = (raw: number): number => {
                const norm = fixedAmpScaling ? raw : raw - dataMean;
                const oriented = norm * polarityMultiplier * channelGain;
                const rawY = baseY - oriented * scale;
                return clampWaveformsToLane
                  ? Math.max(
                      channelTopBound,
                      Math.min(channelBottomBound, rawY),
                    )
                  : rawY;
              };
              const yForMin = toY(rawMin);
              const yForMax = toY(rawMax);
              const topY = Math.min(yForMin, yForMax);
              const botY = Math.max(yForMin, yForMax);
              if (topY < maxYs[px]) maxYs[px] = topY;
              if (botY > minYs[px]) minYs[px] = botY;
            }
            // Apply the same sentinel guard as the CPU path (top >= channelBottomBound
            // || bot <= channelTopBound) before writing to the GPU pool.  Direct .set()
            // would forward sentinel-initialised pixels (yMin=channelTopBound,
            // yMax=channelBottomBound) to WebGL LINES as full-channel-height segments,
            // producing a visible band in the no-data region at page boundaries.
            for (let px = gpuEnvelopeStartPx; px < pixelCount; px++) {
              const top = maxYs[px];
              const bot = minYs[px];
              if (
                top >= channelBottomBound ||
                bot <= channelTopBound ||
                // Suppress full-height shadow bands (scale-mismatch at L3).
                bot - top > (channelBottomBound - channelTopBound) * 0.9
              ) {
                this._gpuChanTopY[ci][px] = 0;
                this._gpuChanBotY[ci][px] = 0;
              } else {
                this._gpuChanTopY[ci][px] = top;
                this._gpuChanBotY[ci][px] = bot;
              }
            }
          } else {
            // No envelope data for this channel — fill with zero-height points so the
            // GPU LINES draw call produces nothing visible.  (Previously filled with
            // channelTopBound/channelBottomBound which drew a full-height band.)
            this._gpuChanTopY[ci].fill(0, gpuEnvelopeStartPx, envPxCount);
            this._gpuChanBotY[ci].fill(0, gpuEnvelopeStartPx, envPxCount);
          }
        }
      }

      // GPU envelope render (between Pass A and Pass B): draw all channels' envelope
      // bands in one WebGL call using the default waveform color at alpha 0.35.
      // The mean lines in Pass B remain per-color Canvas 2D strokes.
      if (useGpuEnvelope && this._webglAdapter && this._gpuCanvas) {
        if (
          this._gpuCanvas.width !== width ||
          this._gpuCanvas.height !== height
        ) {
          this._gpuCanvas.width = width;
          this._gpuCanvas.height = height;
        }
        const envelopeChannels = visibleMontageChannels
          .slice(0, channelCount)
          .map(({ channel }, ci) => ({
            name: channel.label,
            yMin: this._gpuChanTopY[ci].subarray(
              gpuEnvelopeStartPx,
              envPxCount,
            ),
            yMax: this._gpuChanBotY[ci].subarray(
              gpuEnvelopeStartPx,
              envPxCount,
            ),
          }));
        const defaultColor = this.config.waveformColor ?? "#f8fafc";
        // Parse hex color → [r, g, b, 1] for the GPU uniform.
        const hex = defaultColor.replace("#", "");
        const gpuColor: [number, number, number, number] = [
          parseInt(hex.slice(0, 2), 16) / 255,
          parseInt(hex.slice(2, 4), 16) / 255,
          parseInt(hex.slice(4, 6), 16) / 255,
          1,
        ];
        const rendered =
          gpuEnvelopeCopyWidth > 0 &&
          this._webglAdapter.render({
            channels: envelopeChannels,
            width,
            height,
            xOffset: gpuEnvelopeStartPx,
            color: gpuColor,
            clear: [0, 0, 0, 0],
          });
        if (rendered && this.ctx) {
          this.ctx.save();
          this.ctx.globalAlpha = 0.35;
          this.ctx.drawImage(
            this._gpuCanvas,
            gpuEnvelopeStartPx,
            0,
            gpuEnvelopeCopyWidth,
            height,
            gpuEnvelopeStartPx,
            0,
            gpuEnvelopeCopyWidth,
            height,
          );
          this.ctx.restore();
        }
      }
      // Build color→channels map preserving channel order.
      const groupColorOrder: string[] = [];
      const groupChannelIndices = new Map<string, number[]>();
      for (let ci = 0; ci < channelCount; ci++) {
        const mc = visibleMontageChannels[ci].channel;
        const color = this.normalizeStrokeColor(mc.color) ?? defaultGroupColor;
        if (!groupChannelIndices.has(color)) {
          groupColorOrder.push(color);
          groupChannelIndices.set(color, []);
        }
        groupChannelIndices.get(color)!.push(ci);
      }

      for (const groupColor of groupColorOrder) {
        const indices = groupChannelIndices.get(groupColor)!;

        this.ctx.save();
        this.ctx.strokeStyle = groupColor;
        this.ctx.lineWidth = baseLineWidth;

        // Envelope band (Phase 1 for this color group) — skipped when GPU envelope
        // already rendered all channels in one WebGL call between Pass A and Pass B.
        if (!useGpuEnvelope) {
          this.ctx.globalAlpha = 0.35;
          this.ctx.beginPath();
          for (const ci of indices) {
            const filteredData = this._batchFiltered[ci];
            if (!filteredData) continue;
            const trueEnvelope = this._batchEnvelopes[ci];
            const dataMean = this._batchDataMeans[ci];
            const scale = this._batchScales[ci];
            const baseY = this._batchBaseYs[ci];
            const channelTopBound = this._batchTopBounds[ci];
            const channelBottomBound = this._batchBotBounds[ci];
            const channelGain = this._batchGains[ci];
            const fixedAmpScaling = this._batchFixed[ci] === 1;
            const channelSampleXOffset = this._batchSampleXOffsets[ci];
            const channelSamplesPerPage =
              this._batchSamplesPerPage[ci] > 0
                ? this._batchSamplesPerPage[ci]
                : samplesPerPage;
            const envelopeIndexBase = this._batchEnvelopeIndexBases[ci];
            const useDisplayGeometry = this._batchUseDisplayGeometry[ci] === 1;
            const samplesPerPixel = filteredData.length / plotWidth;
            if (
              !suppressCpuEnvelopeAtUltraHighSpeed &&
              filteredData.length > 0 &&
              (samplesPerPixel > 4 || trueEnvelope)
            ) {
              const pixelCount = envPxCount;
              const minYs = this.envMinYs;
              const maxYs = this.envMaxYs;
              minYs.fill(channelTopBound, clipLeft, pixelCount);
              maxYs.fill(channelBottomBound, clipLeft, pixelCount);
              // DC-shift correction: fetch cached raw data (O(1) map lookup) to compute
              // the DC the HP filter removed, so the envelope band is re-centred on the
              // filtered baseline instead of the raw (offset) baseline.
              const _mcCpu = visibleMontageChannels[ci].channel;
              const rawChannelData =
                !useDisplayGeometry && trueEnvelope && this.config.highPassFilter > 0
                  ? this.getMontageChannelData(_mcCpu)
                  : null;
              const repDataCpu = this._batchRepData[ci] ?? filteredData;
              const rapidReviewSourceStride = rapidReviewSourceStrideFor(
                filteredData.length,
              );
              const envelopeRenderStride = resolveEnvelopeRenderStride({
                hasEnvelope: !!trueEnvelope,
                rapidReviewSourceStride,
                samplesPerPixel,
              });
              recordEnvelopeStride(
                rapidReviewSourceStride,
                envelopeRenderStride,
                samplesPerPixel,
                !!trueEnvelope,
              );
              for (
                let i = 0;
                i < filteredData.length;
                i += envelopeRenderStride
              ) {
                const px = Math.floor(
                  plotLeft +
              ((i + channelSampleXOffset) / channelSamplesPerPage) * plotWidth,
                );
                if (px < clipLeft || px >= pixelCount) continue;
                // Use repData (envelope midpoint) as DC reference for consistency
                // with the mean-line drawn in Phase 2.
                const envDcShift = rawChannelData
                  ? rawChannelData[startSample + i] - repDataCpu[i]
                  : 0;
                const rawMin = trueEnvelope
                  ? (trueEnvelope.min[envelopeIndexBase + i] ??
                      repDataCpu[i]) -
                    envDcShift
                  : repDataCpu[i];
                const rawMax = trueEnvelope
                  ? (trueEnvelope.max[envelopeIndexBase + i] ??
                      repDataCpu[i]) -
                    envDcShift
                  : repDataCpu[i];
                const toY = (raw: number): number => {
                  const norm = fixedAmpScaling ? raw : raw - dataMean;
                  const oriented = norm * polarityMultiplier * channelGain;
                  const rawY = baseY - oriented * scale;
                  return clampWaveformsToLane
                    ? Math.max(
                        channelTopBound,
                        Math.min(channelBottomBound, rawY),
                      )
                    : rawY;
                };
                const yForMin = toY(rawMin);
                const yForMax = toY(rawMax);
                const topY = Math.min(yForMin, yForMax);
                const botY = Math.max(yForMin, yForMax);
                // Skip full-height shadow bands (scale-mismatch at L3).
                if (botY - topY > (channelBottomBound - channelTopBound) * 0.9)
                  continue;
                if (topY < maxYs[px]) maxYs[px] = topY;
                if (botY > minYs[px]) minYs[px] = botY;
              }
              if (trueEnvelope && samplesPerPixel <= 1.0) {
                this.ctx.save();
                this.ctx.fillStyle = groupColor;
                this.ctx.globalAlpha = 0.18;
                this.ctx.beginPath();
                this.appendEnvelopeBandPolygon(
                  clipLeft,
                  pixelCount,
                  channelTopBound,
                  channelBottomBound,
                );
                this.ctx.fill();
                this.ctx.restore();
              } else {
                for (let px = clipLeft; px < pixelCount; px++) {
                  const top = maxYs[px];
                  const bot = minYs[px];
                  if (top >= channelBottomBound || bot <= channelTopBound)
                    continue;
                  this.ctx.moveTo(px + 0.5, top);
                  this.ctx.lineTo(px + 0.5, bot);
                }
              }
            }
          }
          this.ctx.stroke(); // ONE stroke per color group for envelope band
        } // end if (!useGpuEnvelope)

        // Mean line (Phase 2 for this color group)
        this.ctx.globalAlpha = 1.0;
        this.ctx.beginPath();
        for (const ci of indices) {
          const filteredData = this._batchFiltered[ci];
          if (!filteredData) continue;
          // Use envelope midpoint as representative when pyramid data is present.
          const repData = this._batchRepData[ci] ?? filteredData;
          const rapidReviewSourceStride = rapidReviewSourceStrideFor(
            repData.length,
          );
          const dataMean = this._batchDataMeans[ci];
          const scale = this._batchScales[ci];
          const baseY = this._batchBaseYs[ci];
          const channelTopBound = this._batchTopBounds[ci];
          const channelBottomBound = this._batchBotBounds[ci];
          const channelGain = this._batchGains[ci];
          const fixedAmpScaling = this._batchFixed[ci] === 1;
          const channelSampleXOffset = this._batchSampleXOffsets[ci];
          const channelSamplesPerPage =
            this._batchSamplesPerPage[ci] > 0
              ? this._batchSamplesPerPage[ci]
              : samplesPerPage;
          const samplesPerPixel = filteredData.length / plotWidth;
          const useEnvelopeOnly =
            !!this._batchEnvelopes[ci] && samplesPerPixel <= 1.0;
          const suppressMeanTraceForChannel =
            shouldUseRapidReviewEnvelopeOverview({
              playbackSpeed: this.config.playbackSpeed,
              transportMode: this.config.playbackTransportMode,
              hasEnvelope: !!this._batchEnvelopes[ci],
            }) &&
            !suppressCpuEnvelopeAtUltraHighSpeed;
          if (useEnvelopeOnly || suppressMeanTraceForChannel) {
            continue;
          }
          if (filteredData.length > 0 && samplesPerPixel > 1.0) {
            const pixelCount = envPxCount;
            const sumYs = this.envSumYs;
            const countPx = this.envCountPx;
            sumYs.fill(0, clipLeft, pixelCount);
            countPx.fill(0, clipLeft, pixelCount);
            for (let i = 0; i < repData.length; i += rapidReviewSourceStride) {
              const px = Math.floor(
                plotLeft +
              ((i + channelSampleXOffset) / channelSamplesPerPage) * plotWidth,
              );
              if (px < clipLeft || px >= pixelCount) continue;
              const norm = fixedAmpScaling ? repData[i] : repData[i] - dataMean;
              const oriented = norm * polarityMultiplier * channelGain;
              const rawY = baseY - oriented * scale;
              const y = clampWaveformsToLane
                ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
                : rawY;
              sumYs[px] += y;
              countPx[px]++;
            }
            let meanStarted = false;
            for (let px = clipLeft; px < pixelCount; px++) {
              if (countPx[px] === 0) {
                meanStarted = false;
                continue;
              }
              const meanY = sumYs[px] / countPx[px];
              if (!meanStarted) {
                this.ctx.moveTo(px + 0.5, meanY);
                meanStarted = true;
              } else {
                this.ctx.lineTo(px + 0.5, meanY);
              }
            }
          } else {
            // Sample-by-sample mode: sub-pixel density or no envelope — lineTo
            // for a fully connected trace (no gaps at 150× still-view).
            for (let i = 0; i < repData.length; i++) {
              const x =
                plotLeft +
              ((i + channelSampleXOffset) / channelSamplesPerPage) * plotWidth;
              if (x < clipLeft) continue;
              const norm = fixedAmpScaling ? repData[i] : repData[i] - dataMean;
              const oriented = norm * polarityMultiplier * channelGain;
              const rawY = baseY - oriented * scale;
              const y = clampWaveformsToLane
                ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
                : rawY;
              const prevX =
                plotLeft +
                ((i - 1 + channelSampleXOffset) / channelSamplesPerPage) *
                  plotWidth;
              if (i === 0 || prevX < clipLeft) {
                this.ctx.moveTo(x, y);
              } else {
                this.ctx.lineTo(x, y);
              }
            }
          }
        }
        this.ctx.stroke(); // ONE stroke per color group for mean line
        this.ctx.restore();
      }
    } else {
      visibleMontageChannels.forEach(
        (
          { channel: montageChannel, index: absoluteChannelIndex },
          channelIndex,
        ) => {
          const channelData = this.getMontageChannelData(montageChannel);
          if (!channelData) {
            return;
          }

          // Apply filters
          const filteredData = this.applyFilters(
            channelData,
            startSample,
            endSample,
          );
          if (!filteredData.length) {
            return;
          }

          // Calculate data statistics for auto-scaling (like Plotly does)
          let dataMin = Infinity;
          let dataMax = -Infinity;
          let dataSum = 0;
          for (let i = 0; i < filteredData.length; i++) {
            const val = filteredData[i];
            if (val < dataMin) dataMin = val;
            if (val > dataMax) dataMax = val;
            dataSum += val;
          }
          const dataMean = dataSum / filteredData.length;
          const dataRange = dataMax - dataMin || 1; // Avoid division by zero

          // Log data range into global debug object (not console) when throttled
          if (channelIndex === 0 && shouldLog) {
            (window as unknown as Record<string, unknown>).__eegRenderDebug = {
              channel: montageChannel.label,
              dataLength: filteredData.length,
              min: dataMin,
              max: dataMax,
              mean: dataMean,
              range: dataRange,
            };
          }

          // Calculate baseline Y position for this channel (below ruler)
          const baseY = this.getWaveformBaselineY(
            channelIndex,
            montageChannel.label,
            channelHeight,
            waveformAreaTop,
            waveformAreaTop + waveformAreaHeight,
          );

          const fixedAmplitudeScaling =
            this.config.amplitudeScalingMode === "fixed";
          const { usableHeight, channelTopBound, channelBottomBound } =
            this.getWaveformVerticalBounds(
              baseY,
              channelHeight,
              waveformAreaTop,
              waveformAreaHeight,
              spacingMultiplier,
              fixedAmplitudeScaling,
            );
          const scaleMultiplier = calculateWaveformScaleMultiplier(
            this.config.sensitivity,
            this.getSafePixelsPerMm(),
          );
          const scale = fixedAmplitudeScaling
            ? this.getSafePixelsPerMm() /
              Math.max(
                0.1,
                this.config.sensitivity || BASELINE_SENSITIVITY_UV_PER_MM,
              )
            : (usableHeight / dataRange) * scaleMultiplier;
          const channelGain = this.getChannelGainMultiplier(
            montageChannel.label,
          );

          // CRITICAL DEBUGGING: Log actual values for channel 0 (throttled)
          if (channelIndex === 0 && shouldLog) {
            const debugData = {
              filteredDataLength: filteredData.length,
              filteredDataType: filteredData.constructor.name,
              firstValues: Array.from(filteredData.slice(0, 10)),
              hasNaN: Array.from(filteredData.slice(0, 100)).some((v: number) =>
                isNaN(v),
              ),
              allZeros: Array.from(filteredData.slice(0, 100)).every(
                (v: number) => v === 0,
              ),
              samplesPerPage,
              width,
              canvasWidth: this.canvas.width,
              baseY,
              scale,
              dataMin,
              dataMax,
              dataMean,
              dataRange,
              usableHeight,
              channelHeight,
              channelGain,
              sensitivity: this.config.sensitivity,
              waveformAreaTop,
            };

            // Store globally for inspection (accessible via browser console: window.__eegDebugData)
            (window as unknown as Record<string, unknown>).__eegDebugData =
              debugData;
          }

          const isSelected = this.selectedMontageChannelLabels.has(
            montageChannel.label,
          );
          const polarityMultiplier =
            this.config.polarity === "positive-up" ? 1 : -1;
          // Draw waveform
          this.ctx.save();
          this.ctx.strokeStyle = this.getWaveformStrokeColor(
            montageChannel,
            absoluteChannelIndex,
          );
          // Keep stroke width identical across channels. Selection uses alpha
          // only — extra lineWidth/shadow makes high-frequency traces look
          // like a different pen and is not a voltage change.
          this.ctx.lineWidth = baseLineWidth;
          this.ctx.lineCap = "butt";
          this.ctx.lineJoin = "miter";
          this.ctx.shadowBlur = 0;
          if (hasSelection && !isSelected) {
            this.ctx.globalAlpha = 0.72;
          }
          this.ctx.beginPath();

          // Debug: Log first few coordinates for channel 0 (throttled)
          const yValues: number[] = [];
          const xValues: number[] = [];

          const samplesPerPixel = filteredData.length / plotWidth;
          // Use envelope mode when either (a) many samples map to one pixel, or
          // (b) the pyramid provides true per-bin min/max (level > 0). At high
          // speeds (100×+) pyramid level 3 has ~32 Hz effective sample rate giving
          // samplesPerPixel < 1, but we still want the envelope band to recover
          // full amplitude excursions from the true min/max columns.
          const trueEnvelope = this.getMontageChannelEnvelope(montageChannel);
          const rapidReviewSourceStride = rapidReviewSourceStrideFor(
            filteredData.length,
          );
          const envelopeRenderStride = resolveEnvelopeRenderStride({
            hasEnvelope: !!trueEnvelope,
            rapidReviewSourceStride,
            samplesPerPixel,
          });
          recordEnvelopeStride(
            rapidReviewSourceStride,
            envelopeRenderStride,
            samplesPerPixel,
            !!trueEnvelope,
          );
          const suppressMeanTraceForChannel =
            shouldUseRapidReviewEnvelopeOverview({
              playbackSpeed: this.config.playbackSpeed,
              transportMode: this.config.playbackTransportMode,
              hasEnvelope: !!trueEnvelope,
            });

          if (
            filteredData.length > 0 &&
            (samplesPerPixel > 4 || trueEnvelope)
          ) {
            // Min-max envelope rendering: for each pixel column, draw a vertical
            // line from min-Y to max-Y of all samples mapping to that column.
            // This faithfully preserves waveform morphology at high playback speeds.
            // Two passes: pass 1 = semi-transparent min-max band, pass 2 = mean line.
            //
            // When the pyramid provides true per-bin min/max columns (levels > 0),
            // use those instead of computing from the mean-downsampled data — this
            // recovers full amplitude excursions even at 1/8 resolution.
            //
            // Use pre-allocated scratch buffers (grown lazily) instead of
            // allocating new TypedArrays per channel: eliminates ~800 KB/frame GC.
            const pixelCount = envPxCount;
            const minYs = this.envMinYs;
            const maxYs = this.envMaxYs;
            const sumYs = this.envSumYs;
            const countPx = this.envCountPx;
            // minYs tracks the bottommost canvas Y (max Y value) per pixel — init to top so any real sample updates it
            // maxYs tracks the topmost canvas Y (min Y value) per pixel — init to bottom so any real sample updates it
            // In blit mode only fill / accumulate the right strip (clipLeft..pixelCount).
            minYs.fill(channelTopBound, clipLeft, pixelCount);
            maxYs.fill(channelBottomBound, clipLeft, pixelCount);
            if (!suppressMeanTraceForChannel) {
              sumYs.fill(0, clipLeft, pixelCount);
              countPx.fill(0, clipLeft, pixelCount);
            }

            for (
              let i = 0;
              i < filteredData.length;
              i += envelopeRenderStride
            ) {
              const px = Math.floor(
                plotLeft + ((i + sampleXOffset) / samplesPerPage) * plotWidth,
              );
              if (px < clipLeft || px >= pixelCount) continue;

              // Use true pyramid min/max when available; fall back to mean sample
              const rawMin = trueEnvelope
                ? (trueEnvelope.min[i] ?? filteredData[i])
                : filteredData[i];
              const rawMax = trueEnvelope
                ? (trueEnvelope.max[i] ?? filteredData[i])
                : filteredData[i];

              const toY = (raw: number) => {
                const norm = fixedAmplitudeScaling ? raw : raw - dataMean;
                const oriented = norm * polarityMultiplier * channelGain;
                const rawY = baseY - oriented * scale;
                return clampWaveformsToLane
                  ? Math.max(
                      channelTopBound,
                      Math.min(channelBottomBound, rawY),
                    )
                  : rawY;
              };

              // Canvas Y is inverted: smaller Y = higher on screen.
              // For the envelope band: topY (max amplitude) = smaller canvas Y.
              const yForMin = toY(rawMin);
              const yForMax = toY(rawMax);
              const topY = Math.min(yForMin, yForMax);
              const botY = Math.max(yForMin, yForMax);

              if (topY < maxYs[px]) maxYs[px] = topY;
              if (botY > minYs[px]) minYs[px] = botY;

              // Mean line uses the original filteredData (mean-downsampled)
              const normalizedValue = fixedAmplitudeScaling
                ? filteredData[i]
                : filteredData[i] - dataMean;
              const orientedValue =
                normalizedValue * polarityMultiplier * channelGain;
              const rawY = baseY - orientedValue * scale;
              const y = clampWaveformsToLane
                ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
                : rawY;
              if (!suppressMeanTraceForChannel) {
                sumYs[px] += y;
                countPx[px]++;
              }
            }

            // Pass 1: envelope band at reduced alpha — shows signal extent
            const savedAlpha = this.ctx.globalAlpha;
            if (trueEnvelope && samplesPerPixel <= 1.0) {
              this.ctx.globalAlpha = savedAlpha * 0.28;
              this.ctx.fillStyle = this.ctx.strokeStyle as string;
              this.ctx.beginPath();
              this.appendEnvelopeBandPolygon(
                clipLeft,
                pixelCount,
                channelTopBound,
                channelBottomBound,
              );
              this.ctx.fill();
            } else {
              this.ctx.globalAlpha = savedAlpha * 0.5;
              for (let px = clipLeft; px < pixelCount; px++) {
                const top = maxYs[px];
                const bot = minYs[px];
                if (top >= channelBottomBound || bot <= channelTopBound)
                  continue;
                // Skip full-height shadow bands (scale-mismatch at L3).
                if (bot - top > (channelBottomBound - channelTopBound) * 0.9)
                  continue;
                this.ctx.moveTo(px + 0.5, top);
                this.ctx.lineTo(px + 0.5, bot);
              }
              this.ctx.stroke();
            }
            this.ctx.globalAlpha = savedAlpha;

            // Pass 2: mean line — only when there are enough samples per pixel
            // to draw a trustworthy centre trace. At 150x/L3, the midpoint-only
            // line becomes a stair-stepped artefact, so keep the envelope band only.
            if (
              !suppressMeanTraceForChannel &&
              !(trueEnvelope && samplesPerPixel <= 1.0)
            ) {
              this.ctx.beginPath();
              let meanStarted = false;
              for (let px = clipLeft; px < pixelCount; px++) {
                if (countPx[px] === 0) {
                  meanStarted = false; // gap in data — lift the pen
                  continue;
                }
                const meanY = sumYs[px] / countPx[px];
                if (!meanStarted) {
                  this.ctx.moveTo(px + 0.5, meanY);
                  meanStarted = true;
                } else {
                  this.ctx.lineTo(px + 0.5, meanY);
                }
              }
            }
          } else {
            // Normal sample-by-sample rendering (1 sample ≤ 1.5 pixels)
            for (
              let i = 0;
              i < filteredData.length;
              i += rapidReviewSourceStride
            ) {
              const x =
                plotLeft + ((i + sampleXOffset) / samplesPerPage) * plotWidth;
              if (x < clipLeft) continue;

              // Fixed mode preserves absolute amplitudes; auto mode recenters per page.
              const normalizedValue = fixedAmplitudeScaling
                ? filteredData[i]
                : filteredData[i] - dataMean;
              const orientedValue =
                normalizedValue * polarityMultiplier * channelGain;
              const rawY = baseY - orientedValue * scale;
              const y = clampWaveformsToLane
                ? Math.max(channelTopBound, Math.min(channelBottomBound, rawY))
                : rawY;

              if (channelIndex === 0 && i < 5 && shouldLog) {
                yValues.push(y);
                xValues.push(x);

                // Store globally for inspection rather than logging to console
                const _w = window as unknown as Record<string, unknown>;
                if (!_w.__eegCoords) _w.__eegCoords = [];
                (_w.__eegCoords as Array<unknown>).push({ i, x, y });
              }

              // First visible sample — moveTo even if not i===0,
              // so the blit seam doesn't leave a gap.
              const prevX =
                plotLeft + ((i - 1 + sampleXOffset) / samplesPerPage) * plotWidth;
              if (i === 0 || prevX < clipLeft) {
                this.ctx.moveTo(x, y);
              } else {
                this.ctx.lineTo(x, y);
              }
            }
          }

          if (channelIndex === 0 && shouldLog) {
            (window as unknown as Record<string, unknown>).__eegStrokeDebug = {
              strokeStyle: this.ctx.strokeStyle,
              lineWidth: this.ctx.lineWidth,
              firstXValues: xValues,
              firstYValues: yValues,
              baseY,
              scale,
              dataMean,
              filteredDataSample: Array.from(filteredData.slice(0, 10)),
            };
          }

          this.ctx.stroke();
          this.ctx.restore();
        },
      );
    } // end else (per-channel rendering fallback)

    if (shouldLog) {
      // draw complete — channel count available in __samplingDebug if needed
    }

    // ── Composite waveform layer and update blit cache ────────────────────────
    if (blitCtx) {
      // Restore the real canvas context (drawing was redirected to blitCtx).
      this.ctx = mainCtx;
      // Composite the waveform-only layer onto the main canvas.  The blit canvas
      // has a transparent background so the grid drawn below the waveforms shows
      // through correctly.
      mainCtx.drawImage(this._blitCanvas!, 0, 0);
    }
    this._blitTime = this.currentTime;
    this._blitDirty = false;
    const metrics = this._lastWaveformRenderMetrics;
    const paintTransition = isPostFlushTransitionFrame
      ? "post-flush"
      : metrics?.useBlit
        ? "blit"
        : "full-redraw";
    this.samplePaintConsistency(paintTransition);
    // ──────────────────────────────────────────────────────────────────────
  }

  /**
   * Get data for a montage channel (differential if bipolar)
   */
  private getMontageChannelData(
    montageChannel: EEGMontageChannel,
  ): Float32Array | null {
    const positiveChannel = this.findChannelByInput(montageChannel.positiveInput);

    if (!positiveChannel) {
      return null;
    }

    // If no negative input, return raw channel data (no allocation needed)
    if (!montageChannel.negativeInput) {
      return positiveChannel.data;
    }

    // Cache key uniquely identifies this derivation within the current buffer
    const cacheKey = `${montageChannel.positiveInput}|${montageChannel.negativeInput}`;
    const cached = this.derivationCache.get(cacheKey);
    if (cached) return cached;

    // Bipolar montage: subtract negative from positive
    const negativeChannel = this.findChannelByInput(
      montageChannel.negativeInput,
    );

    // ------------------------------------------------------------------
    // Real-time Laplacian derivations
    // The renderer handles three spatial-filter families identified by
    // the negativeInput prefix (after the "EEG " prefix injected by
    // transformRendererChannelName in EEGViewer):
    //   "EEG LAP_X"  → Small Laplacian (4-neighbour)
    //   "EEG LLAP_X" → Large Laplacian (2-hop neighbour)
    //   "EEG HJR_X"  → Hjorth (3-nearest-neighbour)
    // ------------------------------------------------------------------
    const negInput = montageChannel.negativeInput!;
    let laplacianNeighborMap: Record<string, string[]> | null = null;
    let lapElectrode = "";

    if (negInput.startsWith("EEG LAP_")) {
      lapElectrode = negInput.slice(8).toUpperCase(); // "EEG LAP_FP1" → "FP1"
      laplacianNeighborMap = LAPLACIAN_SMALL_NEIGHBORS;
    } else if (negInput.startsWith("EEG LLAP_")) {
      lapElectrode = negInput.slice(9).toUpperCase(); // "EEG LLAP_FP1" → "FP1"
      laplacianNeighborMap = LAPLACIAN_LARGE_NEIGHBORS;
    } else if (negInput.startsWith("EEG HJR_")) {
      lapElectrode = negInput.slice(8).toUpperCase(); // "EEG HJR_FP1" → "FP1"
      laplacianNeighborMap = HJORTH_NEIGHBORS;
    }

    let result: Float32Array;

    if (laplacianNeighborMap && lapElectrode) {
      const neighborNames = laplacianNeighborMap[lapElectrode] ?? [];
      const neighborChannels = neighborNames
        .map((n) => this.findChannelByInput(n))
        .filter((ch): ch is NonNullable<typeof ch> => ch !== undefined);

      if (neighborChannels.length === 0) {
        // No neighbours found — fall back to raw (e.g. file only has subset)
        return positiveChannel.data;
      }

      const len = positiveChannel.data.length;
      result = new Float32Array(len);
      const n = neighborChannels.length;
      for (let i = 0; i < len; i++) {
        let neighborSum = 0;
        for (let k = 0; k < n; k++)
          neighborSum += neighborChannels[k].data[i] ?? 0;
        result[i] = positiveChannel.data[i] - neighborSum / n;
      }
    } else if (
      this.isAverageReferenceInput(negInput) ||
      this.isLinkedEarsReferenceInput(negInput)
    ) {
      const virtualReference = this.getVirtualReferenceData(negInput);
      if (!virtualReference) {
        return positiveChannel.data;
      }

      const len = Math.min(
        positiveChannel.data.length,
        virtualReference.length,
      );
      result = new Float32Array(len);
      for (let i = 0; i < len; i++) {
        result[i] = positiveChannel.data[i] - virtualReference[i];
      }
    } else if (!negativeChannel) {
      return positiveChannel.data;
    } else {
      result = new Float32Array(positiveChannel.data.length);
      for (let i = 0; i < result.length; i++) {
        result[i] = positiveChannel.data[i] - negativeChannel.data[i];
      }
    }

    this.derivationCache.set(cacheKey, result);
    return result;
  }

  private getMontageDisplayGeometry(
    montageChannel: EEGMontageChannel,
  ): EEGChannel["displayGeometry"] | null {
    const positiveGeometry =
      this.findChannelByInput(montageChannel.positiveInput)
        ?.displayGeometry ?? null;
    if (!positiveGeometry) {
      return null;
    }
    if (!montageChannel.negativeInput) {
      return positiveGeometry;
    }

    if (
      this.isAverageReferenceInput(montageChannel.negativeInput) ||
      this.isLinkedEarsReferenceInput(montageChannel.negativeInput)
    ) {
      return null;
    }

    const negativeGeometry =
      this.findChannelByInput(montageChannel.negativeInput)
        ?.displayGeometry ?? null;
    if (
      !negativeGeometry ||
      positiveGeometry.sourceSamplesPerBin !==
        negativeGeometry.sourceSamplesPerBin
    ) {
      return null;
    }

    const len = Math.min(
      positiveGeometry.mean.length,
      negativeGeometry.mean.length,
      positiveGeometry.min.length,
      positiveGeometry.max.length,
      negativeGeometry.min.length,
      negativeGeometry.max.length,
    );
    if (len <= 0) {
      return null;
    }

    const cacheKey = `${montageChannel.positiveInput}|${montageChannel.negativeInput}|${positiveGeometry.sourceSamplesPerBin}|${len}`;
    const cached = this.displayGeometryCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const min = new Float32Array(len);
    const max = new Float32Array(len);
    const mean = new Float32Array(len);
    for (let i = 0; i < len; i++) {
      mean[i] = positiveGeometry.mean[i] - negativeGeometry.mean[i];

      const positiveMid =
        (positiveGeometry.min[i] + positiveGeometry.max[i]) * 0.5;
      const negativeMid =
        (negativeGeometry.min[i] + negativeGeometry.max[i]) * 0.5;
      const diffMid = positiveMid - negativeMid;
      const positiveHalfRange = Math.max(
        0,
        (positiveGeometry.max[i] - positiveGeometry.min[i]) * 0.5,
      );
      const negativeHalfRange = Math.max(
        0,
        (negativeGeometry.max[i] - negativeGeometry.min[i]) * 0.5,
      );
      const diffHalfRange = Math.hypot(positiveHalfRange, negativeHalfRange);
      min[i] = diffMid - diffHalfRange;
      max[i] = diffMid + diffHalfRange;
    }

    const geometry = {
      min,
      max,
      mean,
      sourceSamplesPerBin: positiveGeometry.sourceSamplesPerBin,
    };
    this.displayGeometryCache.set(cacheKey, geometry);
    return geometry;
  }

  /**
   * Return the true min/max envelope arrays for a montage channel.
   * For bipolar channels, computes the differential envelope from the
   * positive and negative channel envelopes (conservative: widens the band).
   * Returns null if no envelope data is available (e.g. full-resolution L0).
   */
  private getMontageChannelEnvelope(
    montageChannel: EEGMontageChannel,
  ): { min: Float32Array; max: Float32Array } | null {
    const positiveChannel = this.findChannelByInput(montageChannel.positiveInput);
    if (!positiveChannel?.envelopeMin || !positiveChannel?.envelopeMax) {
      return null;
    }

    // Unipolar: return the channel's own envelope directly
    if (!montageChannel.negativeInput) {
      return {
        min: positiveChannel.envelopeMin,
        max: positiveChannel.envelopeMax,
      };
    }

    const negativeVirtualEnvelope =
      this.isAverageReferenceInput(montageChannel.negativeInput) ||
      this.isLinkedEarsReferenceInput(montageChannel.negativeInput)
        ? this.getVirtualReferenceEnvelope(montageChannel.negativeInput)
        : null;

    const negativeChannel = this.findChannelByInput(
      montageChannel.negativeInput,
    );
    if (
      !negativeVirtualEnvelope &&
      (!negativeChannel?.envelopeMin || !negativeChannel?.envelopeMax)
    ) {
      // No negative envelope — fall back to positive envelope
      return {
        min: positiveChannel.envelopeMin,
        max: positiveChannel.envelopeMax,
      };
    }

    // Bipolar differential envelope:
    // A strict worst-case envelope (posMin-negMax → posMax-negMin) expands to the
    // sum of both input ranges and turns into a full-lane block at 100x+ for
    // longitudinal bipolar montages. The previous UI workaround collapsed that
    // band to a midpoint-only trace, which made 150x look flat and stair-stepped.
    //
    // Use a bounded approximation instead:
    //   mid = posMid - negMid
    //   halfRange ~= hypot(posHalfRange, negHalfRange)
    // This preserves visible spread for rapid review without the pathological
    // over-expansion of the worst-case differential envelope.
    const negativeEnvelopeMin =
      negativeVirtualEnvelope?.min ?? negativeChannel?.envelopeMin;
    const negativeEnvelopeMax =
      negativeVirtualEnvelope?.max ?? negativeChannel?.envelopeMax;
    if (!negativeEnvelopeMin || !negativeEnvelopeMax) {
      return {
        min: positiveChannel.envelopeMin,
        max: positiveChannel.envelopeMax,
      };
    }

    const len = Math.min(
      positiveChannel.envelopeMin.length,
      negativeEnvelopeMin.length,
    );
    // Reuse pooled buffers to avoid per-frame Float32Array allocation (GC fix).
    const poolKey = `${montageChannel.positiveInput}|${montageChannel.negativeInput}`;
    const pooled = this.getPooledEnvPair(poolKey, len);
    for (let i = 0; i < len; i++) {
      const positiveMin = positiveChannel.envelopeMin[i];
      const positiveMax = positiveChannel.envelopeMax[i];
      const negativeMin = negativeEnvelopeMin[i];
      const negativeMax = negativeEnvelopeMax[i];
      const positiveMid = (positiveMin + positiveMax) * 0.5;
      const negativeMid = (negativeMin + negativeMax) * 0.5;
      const diffMid = positiveMid - negativeMid;
      const positiveHalfRange = Math.max(0, (positiveMax - positiveMin) * 0.5);
      const negativeHalfRange = Math.max(0, (negativeMax - negativeMin) * 0.5);
      const diffHalfRange = Math.hypot(positiveHalfRange, negativeHalfRange);
      pooled.min[i] = diffMid - diffHalfRange;
      pooled.max[i] = diffMid + diffHalfRange;
    }
    return pooled;
  }

  /**
   * Apply digital filters to the data
   */
  private applyFilters(
    data: Float32Array,
    startSample: number,
    endSample: number,
    hpPrefix?: Float32Array,
  ): Float32Array {
    // Filter-settings key — same settings across all channels in a frame
    const filterSettingsKey = `${this.config.highPassFilter}|${this.config.lowPassFilter}|${this.config.notchFilter ? "60" : this.config.notch50Hz ? "50" : "0"}|${this.config.removeDcOffset ? 1 : 0}`;

    // Look up per-channel cache (keyed by Float32Array object identity via WeakMap)
    let channelCache = this.filterCache.get(data);
    if (channelCache) {
      const cached = channelCache.get(filterSettingsKey);
      if (cached) {
        return cached.subarray(startSample, endSample);
      }
    } else {
      channelCache = new Map<string, Float32Array>();
      this.filterCache.set(data, channelCache);
    }

    // Cache miss — run full-buffer filter pass using pre-allocated scratch buffers.
    // Scratch buffers ping-pong between stages so only ONE new Float32Array is
    // allocated (the permanent cache entry).  This replaces the old pattern of
    // 3-4 short-lived allocations per channel that caused 2.7-3.5 MB of GC
    // pressure per preWarmForChannels() call (50-148ms GC pauses).
    const n = data.length;
    if (this.filterScratchA.length < n) {
      this.filterScratchA = new Float32Array(n);
      this.filterScratchB = new Float32Array(n);
    }

    // nextScratch() returns whichever scratch buffer is NOT currently holding
    // the live signal (cur), giving us a safe write destination at each stage.
    let cur: Float32Array = data;
    const nextScratch = (): Float32Array =>
      cur !== this.filterScratchA ? this.filterScratchA : this.filterScratchB;

    // EEG-CTRL-020: Remove DC offset
    if (this.config.removeDcOffset) {
      cur = this.removeDcOffset(cur, nextScratch());
    }

    // High-pass filter — with optional overlap-save prefix.
    // When hpPrefix is provided (supplied by preWarmForChannels), we build a
    // temporary combined array [prefix | cur] so the centred moving-average
    // window is fully seeded at position 0 of the new buffer, eliminating the
    // edge artefacts that otherwise cause visible waveform discontinuities when
    // the displayed time is near the new buffer's start.  Only the n-sample
    // new-buffer portion of the HP result is kept; the prefix is discarded.
    // This allocation (≈ 2 KB per channel) happens only during idle preWarm;
    // subsequent render calls get a filterCache hit and never reach this path.
    if (this.config.highPassFilter > 0) {
      if (hpPrefix && hpPrefix.length > 0) {
        const pLen = hpPrefix.length;
        const totalLen = pLen + n;
        // Grow the pre-allocated scratch buffers only when needed (lazy, once per
        // buffer-size change).  After the first preWarm at a given buffer size
        // these two write targets are reused without allocation.
        if (this.hpPrefixScratch.length < totalLen) {
          this.hpPrefixScratch = new Float32Array(totalLen);
          this.hpPrefixResultScratch = new Float32Array(totalLen);
        }
        this.hpPrefixScratch.set(hpPrefix, 0);
        this.hpPrefixScratch.set(cur.subarray(0, n), pLen);
        this.highPassFilter(
          this.hpPrefixScratch,
          this.config.highPassFilter,
          this.hpPrefixResultScratch,
        );
        const dst = nextScratch();
        dst.set(this.hpPrefixResultScratch.subarray(pLen, totalLen));
        cur = dst;
      } else {
        cur = this.highPassFilter(
          cur,
          this.config.highPassFilter,
          nextScratch(),
        );
      }
    }

    // Low-pass filter
    cur = this.lowPassFilter(cur, this.config.lowPassFilter, nextScratch());

    const notchMode = this.config.notchFilter
      ? "60hz"
      : this.config.notch50Hz
        ? "50hz"
        : "off";

    if (notchMode === "60hz") {
      cur = this.notchFilterAt60Hz(cur, nextScratch());
    } else if (notchMode === "50hz") {
      cur = this.notchFilterAt50Hz(cur, nextScratch());
    }

    // Permanent allocation: one per channel per filter-settings key.
    const filtered = new Float32Array(n);
    filtered.set(cur.subarray(0, n));

    channelCache.set(filterSettingsKey, filtered);

    return filtered.subarray(startSample, endSample);
  }

  /**
   * EEG-CTRL-020: Remove DC offset by subtracting the window mean.
   * Accepts an optional pre-allocated destination buffer to avoid allocation.
   */
  private removeDcOffset(data: Float32Array, dst?: Float32Array): Float32Array {
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i];
    const mean = sum / data.length;
    const result = dst ?? new Float32Array(data.length);
    for (let i = 0; i < data.length; i++) result[i] = data[i] - mean;
    return result;
  }

  /**
   * High-pass filter using O(n) sliding-window moving average subtraction.
   * windowSize samples either side → causal centred window; edges padded by
   * expanding the count to whatever samples exist.
   * Accepts an optional pre-allocated destination buffer to avoid allocation.
   */
  private highPassFilter(
    data: Float32Array,
    cutoffHz: number,
    dst?: Float32Array,
  ): Float32Array {
    if (cutoffHz <= 0) return data;

    const n = data.length;
    const half = Math.floor(this.sampleRate / cutoffHz);
    const result = dst ?? new Float32Array(n);

    // Full symmetric window size used as the divisor throughout, including at
    // boundary samples where fewer than half samples exist on the left.
    // Dividing by windowSize (rather than the actual count of available samples)
    // implicitly zero-pads the missing left-side history.  Without this, the
    // boundary at i=0 divides by ~401 instead of 801, overcorrecting the DC
    // estimate by 2× and producing large-amplitude artefacts in the first ~4 s
    // (the HP filter settling time = 2 × half at 200 Hz / 0.5 Hz = 4 s).
    // At steady state (i >> half) the actual count equals windowSize, so the
    // formula is bit-identical to the old code.
    const windowSize = 2 * half + 1;

    // Seed the sliding sum with the initial window [0 .. half]
    let sum = 0;
    const right0 = Math.min(half, n - 1);
    for (let j = 0; j <= right0; j++) sum += data[j];
    let leftEdge = 0;
    let rightEdge = right0;

    for (let i = 0; i < n; i++) {
      result[i] = data[i] - sum / windowSize;

      // Slide window forward: drop leftEdge if it has fallen behind i-half
      if (i - leftEdge >= half) {
        sum -= data[leftEdge];
        leftEdge++;
      }
      // Extend rightEdge up to i+half
      const nextRight = Math.min(i + half + 1, n - 1);
      if (nextRight > rightEdge) {
        sum += data[nextRight];
        rightEdge = nextRight;
      }
    }

    return result;
  }

  /**
   * Low-pass filter using O(n) sliding-window moving average.
   * Accepts an optional pre-allocated destination buffer to avoid allocation.
   */
  private lowPassFilter(
    data: Float32Array,
    cutoffHz: number,
    dst?: Float32Array,
  ): Float32Array {
    if (cutoffHz <= 0 || cutoffHz >= this.sampleRate / 2) return data;

    const n = data.length;
    const half = Math.max(1, Math.floor(this.sampleRate / (2 * cutoffHz)));
    const result = dst ?? new Float32Array(n);

    // Seed the sliding sum with the initial window [0 .. half]
    let sum = 0;
    const right0 = Math.min(half, n - 1);
    for (let j = 0; j <= right0; j++) sum += data[j];
    let leftEdge = 0;
    let rightEdge = right0;

    for (let i = 0; i < n; i++) {
      const count = rightEdge - leftEdge + 1;
      result[i] = sum / count;

      if (i - leftEdge >= half) {
        sum -= data[leftEdge];
        leftEdge++;
      }
      const nextRight = Math.min(i + half + 1, n - 1);
      if (nextRight > rightEdge) {
        sum += data[nextRight];
        rightEdge = nextRight;
      }
    }

    return result;
  }

  /**
   * Notch filter at 60Hz (removes North-American mains line noise)
   * Accepts an optional pre-allocated destination buffer to avoid allocation.
   */
  private notchFilterAt60Hz(
    data: Float32Array,
    dst?: Float32Array,
  ): Float32Array {
    // Simple notch filter using a comb filter
    const notchFreq = 60;
    const delaySamples = Math.round(this.sampleRate / notchFreq);
    const result = dst ?? new Float32Array(data.length);

    for (let i = 0; i < data.length; i++) {
      if (i < delaySamples) {
        result[i] = data[i];
      } else {
        result[i] = data[i] - 0.5 * data[i - delaySamples];
      }
    }

    return result;
  }

  /**
   * EEG-CTRL-003: Notch filter at 50Hz (removes European/international mains line noise)
   * Accepts an optional pre-allocated destination buffer to avoid allocation.
   */
  private notchFilterAt50Hz(
    data: Float32Array,
    dst?: Float32Array,
  ): Float32Array {
    const notchFreq = 50;
    const delaySamples = Math.round(this.sampleRate / notchFreq);
    const result = dst ?? new Float32Array(data.length);

    for (let i = 0; i < data.length; i++) {
      if (i < delaySamples) {
        result[i] = data[i];
      } else {
        result[i] = data[i] - 0.5 * data[i - delaySamples];
      }
    }

    return result;
  }

  /**
   * Draw annotation markers
   */
  private drawAnnotations(annotations: Annotation[]): void {
    const plot = this.getPlotGeometry();
    const waveformArea = this.getWaveformArea();
    const waveformAreaTop = waveformArea.top;
    const pixelsPerSecond = plot.pixelsPerSecond;
    const startTime = this.currentTime;
    const endTime = startTime + this.config.timeScale;
    const laneTop = waveformAreaTop + 6;
    const laneHeight = Math.max(8, Math.min(16, waveformArea.height * 0.16));
    const laneBottom = laneTop + laneHeight;

    annotations.forEach((annotation) => {
      // Skip annotations outside current time window
      if (annotation.end_time && annotation.end_time < startTime) return;
      if (annotation.start_time > endTime) return;
      if (!annotation.end_time && annotation.start_time < startTime) return;

      const color =
        annotation.color || this.getAnnotationColor(annotation.annotation_type);

      if (annotation.end_time) {
        // Region annotation (e.g., seizure)
        const clampedStartTime = Math.max(startTime, annotation.start_time);
        const clampedEndTime = Math.min(endTime, annotation.end_time);
        const startX =
          plot.left + (clampedStartTime - startTime) * pixelsPerSecond;
        const endX =
          plot.left + (clampedEndTime - startTime) * pixelsPerSecond;
        const regionWidth = Math.max(1, endX - startX);

        const annotationOpacity =
          typeof this.config.annotationOpacity === "number" &&
          Number.isFinite(this.config.annotationOpacity)
            ? Math.min(0.8, Math.max(0.05, this.config.annotationOpacity))
            : 0.28;

        this.ctx.fillStyle = color;
        this.ctx.globalAlpha = annotationOpacity;
        this.ctx.fillRect(startX, laneTop, regionWidth, laneHeight);
        this.ctx.globalAlpha = 1.0;

        // Draw compact border in the annotation lane.
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 1.5;
        this.ctx.strokeRect(startX, laneTop, regionWidth, laneHeight);
      } else {
        // Point annotation (e.g., spike)
        const startX =
          plot.left + (annotation.start_time - startTime) * pixelsPerSecond;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 1.5;
        this.ctx.beginPath();
        this.ctx.moveTo(startX, laneTop);
        this.ctx.lineTo(startX, laneBottom);
        this.ctx.stroke();

        // Draw label
        if (annotation.annotation_text) {
          this.ctx.fillStyle = color;
          this.ctx.font = "12px Arial";
          this.ctx.fillText(
            annotation.annotation_text,
            startX + 5,
            laneBottom + 12,
          );
        }
      }
    });
  }

  /**
   * Get color for annotation type
   */
  private getAnnotationColor(type: string): string {
    const colorMap: Record<string, string> = {
      seizure_onset: "#ff4444",
      seizure_offset: "#44ff44",
      spike: "#ffaa00",
      artifact: "#0f766e",
      sleep_stage: "#4444ff",
      clinical_event: "#ff44ff",
      edf_import: "#64748b",
      technical_imported: "#64748b",
    };
    return colorMap[type] || "#888888";
  }

  /**
   * Draw channel labels
   */
  private drawLabels(): void {
    const visibleMontageChannels = this.getVisibleMontageChannels();
    const displayMode = this.getSafeDisplayMode();

    const { height } = this.canvas;
    const plot = this.getPlotGeometry();
    const waveformArea = this.getWaveformArea();
    const waveformAreaTop = waveformArea.top;
    const waveformAreaHeight = waveformArea.height - 2; // Minimal bottom margin
    const channelCount = visibleMontageChannels.length;
    if (channelCount <= 0) return;
    const layoutChannelCount = this.getLayoutVisibleChannelCount(channelCount);
    const channelHeight = waveformAreaHeight / layoutChannelCount;

    if (plot.left > 0) {
      const darkPaper = this.isBackgroundDark();
      this.ctx.save();
      this.ctx.fillStyle = this.config.backgroundColor;
      this.ctx.fillRect(0, plot.top, plot.left, Math.max(0, plot.height));
      this.ctx.strokeStyle = darkPaper
        ? "rgba(226, 232, 240, 0.22)"
        : "rgba(15, 23, 42, 0.18)";
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(plot.left - 0.5, plot.top);
      this.ctx.lineTo(plot.left - 0.5, plot.bottom);
      this.ctx.stroke();
      this.ctx.restore();
    }

    const labelSize = this.getSafeLabelFontSize();
    const labelX = 8;
    this.ctx.font = `bold ${labelSize}px Arial`;
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "middle";
    const hasSelection = this.selectedMontageChannelLabels.size > 0;

    if (displayMode === "butterfly") {
      const lineHeight = labelSize + 4;
      const maxVisibleLabels = Math.max(
        1,
        Math.floor(Math.max(lineHeight, waveformAreaHeight - 8) / lineHeight),
      );
      visibleMontageChannels
        .slice(0, maxVisibleLabels)
        .forEach(({ channel }, index) => {
          const isSelected = this.selectedMontageChannelLabels.has(
            channel.label,
          );
          const labelY = waveformAreaTop + labelSize + index * lineHeight;
          if (isSelected && plot.left > 0) {
            this.ctx.fillStyle = this.getSelectedChannelLaneFillColor();
            this.ctx.fillRect(
              0,
              labelY - lineHeight / 2,
              plot.left,
              lineHeight,
            );
            this.ctx.fillStyle = this.getSelectedChannelRailColor();
            this.ctx.fillRect(1, labelY - lineHeight / 2 + 1, 3, lineHeight - 2);
          }
          this.ctx.fillStyle =
            hasSelection && !isSelected
              ? this.getOverlayLabelColor(true)
              : isSelected
                ? this.getSelectedOverlayLabelColor()
                : this.getWaveformStrokeColor(channel, index);
          this.ctx.fillText(channel.label, labelX, labelY);
        });

      if (channelCount > maxVisibleLabels) {
        this.ctx.fillStyle = this.getOverlayLabelColor(true);
        this.ctx.fillText(
          `+${channelCount - maxVisibleLabels} more`,
          labelX,
          waveformAreaTop + labelSize + maxVisibleLabels * lineHeight,
        );
      }
      return;
    }

    // Add padding to ensure first and last labels are visible
    const labelPadding = 5; // Minimal padding like Plotly.js
    const waveformAreaBottom = waveformAreaTop + waveformAreaHeight;
    visibleMontageChannels.forEach(({ channel }, index) => {
      const isSelected = this.selectedMontageChannelLabels.has(channel.label);
      const lane = this.getChannelVerticalBounds(
        index,
        channelHeight,
        waveformAreaTop,
        waveformAreaBottom,
      );
      if (isSelected && plot.left > 0) {
        const laneHeight = Math.max(0, lane.bottom - lane.top);
        this.ctx.fillStyle = this.getSelectedChannelLaneFillColor();
        this.ctx.fillRect(0, lane.top, plot.left, laneHeight);
        this.ctx.fillStyle = this.getSelectedChannelRailColor();
        this.ctx.fillRect(
          1,
          lane.top + 2,
          3,
          Math.max(0, laneHeight - 4),
        );
      }
      this.ctx.fillStyle =
        hasSelection && !isSelected
          ? this.getOverlayLabelColor(true)
          : isSelected
            ? this.getSelectedOverlayLabelColor()
            : this.getOverlayLabelColor();
      let y = this.getWaveformBaselineY(
        index,
        channel.label,
        channelHeight,
        waveformAreaTop,
        waveformAreaBottom,
      );
      // Clamp Y to keep labels within waveform area bounds
      y = Math.max(
        waveformAreaTop + labelPadding + 6,
        Math.min(height - labelPadding - 6, y),
      );
      this.ctx.fillText(channel.label, labelX, y);
    });

    if (plot.left > 0) {
      const darkPaper = this.isBackgroundDark();
      this.ctx.save();
      this.ctx.strokeStyle = darkPaper
        ? "rgba(226, 232, 240, 0.22)"
        : "rgba(15, 23, 42, 0.18)";
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(plot.left - 0.5, plot.top);
      this.ctx.lineTo(plot.left - 0.5, plot.bottom);
      this.ctx.stroke();
      this.ctx.restore();
    }
  }

  /**
   * Draw compact timescale ruler (supports top or bottom position)
   */
  private drawTimescale(): void {
    const plot = this.getPlotGeometry();
    const { width, height } = this.canvas;
    const pixelsPerSecond = plot.pixelsPerSecond;
    const startTime = this.currentTime;

    // Calculate ruler position based on config
    const rulerHeight = this.RULER_HEIGHT;
    const rulerY =
      this.config.timelinePosition === "bottom"
        ? height - rulerHeight - this.RULER_MARGIN
        : this.RULER_MARGIN;

    this.ctx.save();

    const darkPaper = this.isBackgroundDark();
    this.ctx.fillStyle = darkPaper
      ? "rgba(30, 30, 30, 0.9)"
      : "rgba(248, 250, 252, 0.94)";
    this.ctx.fillRect(0, rulerY, width, rulerHeight);

    this.ctx.strokeStyle = darkPaper
      ? "rgba(80, 80, 80, 0.8)"
      : "rgba(148, 163, 184, 0.85)";
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(0, rulerY, width, rulerHeight);

    // Adaptive major ticks — keep labels readable for the current viewport width.
    const minLabelSpacingPx = 56;
    const tickCandidates = [0.5, 1, 2, 5, 10, 15, 30, 60];
    let majorTickSeconds = 1;
    for (const candidate of tickCandidates) {
      const spacing = candidate * pixelsPerSecond;
      if (spacing >= minLabelSpacingPx) {
        majorTickSeconds = candidate;
        break;
      }
    }

    this.ctx.fillStyle = darkPaper ? "#aaaaaa" : "#334155";
    this.ctx.font = "9px Arial";
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";

    let previousLabelRight = -Infinity;
    for (
      let t = 0;
      t <= this.config.timeScale + 0.001;
      t += majorTickSeconds
    ) {
      const x = plot.left + t * pixelsPerSecond;
      const time = startTime + t;
      const timeLabel = this.formatTimeCompact(time);
      const labelWidth = this.ctx.measureText(timeLabel).width;
      const labelLeft = x - labelWidth / 2;
      const showLabel =
        labelLeft >= previousLabelRight + 4 ||
        t === 0 ||
        t + majorTickSeconds > this.config.timeScale;

      // Draw tick mark
      this.ctx.strokeStyle = darkPaper
        ? showLabel
          ? "#666666"
          : "#444444"
        : showLabel
          ? "#64748b"
          : "#94a3b8";
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      if (this.config.timelinePosition === "bottom") {
        this.ctx.moveTo(x, rulerY);
        this.ctx.lineTo(x, rulerY + (showLabel ? 4 : 2));
      } else {
        this.ctx.moveTo(x, rulerY + rulerHeight - (showLabel ? 4 : 2));
        this.ctx.lineTo(x, rulerY + rulerHeight);
      }
      this.ctx.stroke();

      if (showLabel) {
        this.ctx.fillText(timeLabel, x, rulerY + rulerHeight / 2);
        previousLabelRight = x + labelWidth / 2;
      }
    }

    this.ctx.restore();
  }

  /**
   * Draw always-on clinical calibration bar (EEG-CTRL-002).
   * Renders an amplitude scale bar and a 1-second time scale bar in the
   * bottom-right corner of the canvas to avoid obscuring channel labels.
   * Required for clinical documentation.
   *
   * Keeps a compact translucent chip so white marks stay readable on light
   * paper grids without oversized empty padding around the time bar.
   */
  private drawCalibrationBar(): void {
    if (this.config.showScaleMarkers === false) {
      return;
    }

    const { width } = this.canvas;
    const plot = this.getPlotGeometry();
    const pxPerMm =
      this.config.pixelsPerMm &&
      this.config.pixelsPerMm >= MIN_PIXELS_PER_MM &&
      this.config.pixelsPerMm <= MAX_PIXELS_PER_MM
        ? this.config.pixelsPerMm
        : DEFAULT_PIXELS_PER_MM;

    const sensitivity =
      this.config.sensitivity ?? BASELINE_SENSITIVITY_UV_PER_MM;

    const ampBarHeightPx = Math.max(8, Math.min(60, sensitivity * pxPerMm));
    const timeBarWidthPx = Math.max(
      10,
      Math.min(width * 0.25, plot.pixelsPerSecond),
    );
    const margin = 10;
    const padX = 8;
    const padY = 8;
    const fontSize = 10;
    const endCap = 3;
    const ampLabelGap = 6;
    const stackGap = 8;
    const timeLabelGap = 4;
    const ampLabel = `${sensitivity} µV`;
    const timeLabel = "1 s";

    this.ctx.save();
    this.ctx.font = `${fontSize}px monospace`;
    const ampLabelMetrics = this.ctx.measureText(ampLabel);
    const timeLabelMetrics = this.ctx.measureText(timeLabel);
    const ampLabelW = ampLabelMetrics.width;
    const timeLabelW = timeLabelMetrics.width;
    const timeLabelHeight = Math.max(
      fontSize,
      (timeLabelMetrics.actualBoundingBoxAscent ?? fontSize * 0.8) +
        (timeLabelMetrics.actualBoundingBoxDescent ?? fontSize * 0.2),
    );

    // Content width from left of amp end-cap through amp label, vs time bar / label.
    // Stem sits `endCap` from the content left edge.
    const contentWidth = Math.max(
      2 * endCap + ampLabelGap + ampLabelW,
      endCap + timeBarWidthPx,
      endCap + timeBarWidthPx / 2 + timeLabelW / 2,
    );
    const contentHeight =
      ampBarHeightPx + stackGap + endCap + timeLabelGap + timeLabelHeight;

    const boxWidth = contentWidth + padX * 2;
    const boxHeight = contentHeight + padY * 2;
    const waveformArea = this.getWaveformArea();
    const boxX = Math.max(0, width - boxWidth - margin);
    const boxY = waveformArea.bottom - margin - boxHeight;

    // Compact translucent chip: readable on light grids, minimal occlusion.
    this.ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    this.ctx.beginPath();
    if (this.ctx.roundRect) {
      this.ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 3);
    } else {
      this.ctx.rect(boxX, boxY, boxWidth, boxHeight);
    }
    this.ctx.fill();

    this.ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    this.ctx.lineWidth = 0.5;
    this.ctx.stroke();

    this.ctx.fillStyle = "#ffffff";
    this.ctx.textBaseline = "middle";
    this.ctx.lineWidth = 1.5;
    this.ctx.strokeStyle = "#ffffff";

    const ampX = boxX + padX + endCap;
    const ampTop = boxY + padY;
    const ampBottom = ampTop + ampBarHeightPx;
    const ampMid = (ampTop + ampBottom) / 2;

    this.ctx.beginPath();
    this.ctx.moveTo(ampX, ampTop);
    this.ctx.lineTo(ampX, ampBottom);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(ampX - endCap, ampTop);
    this.ctx.lineTo(ampX + endCap, ampTop);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(ampX - endCap, ampBottom);
    this.ctx.lineTo(ampX + endCap, ampBottom);
    this.ctx.stroke();

    this.ctx.textAlign = "left";
    this.ctx.fillText(ampLabel, ampX + endCap + ampLabelGap, ampMid);

    const timeBarY = ampBottom + stackGap;
    const timeBarX = ampX;
    const timeBarEnd = timeBarX + timeBarWidthPx;

    this.ctx.beginPath();
    this.ctx.moveTo(timeBarX, timeBarY);
    this.ctx.lineTo(timeBarEnd, timeBarY);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(timeBarX, timeBarY - endCap);
    this.ctx.lineTo(timeBarX, timeBarY + endCap);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(timeBarEnd, timeBarY - endCap);
    this.ctx.lineTo(timeBarEnd, timeBarY + endCap);
    this.ctx.stroke();

    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "top";
    this.ctx.fillText(
      timeLabel,
      timeBarX + timeBarWidthPx / 2,
      timeBarY + endCap + timeLabelGap,
    );

    this.ctx.restore();
  }

  private drawScaleMarkers(): void {
    if (this.config.showScaleMarkers === false) {
      return;
    }

    const timeScale = this.config.timeScale ?? 10;
    const pxPerMm = this.getSafePixelsPerMm();
    const parts: string[] = [];
    if (this.config.physicalTimebaseTrusted) {
      const mmPerSec = calculateHorizontalMmPerSecond(
        this.canvas.width,
        timeScale,
        pxPerMm,
      );
      if (mmPerSec != null) {
        parts.push(`${mmPerSec.toFixed(1)} mm/s`);
      }
    }
    if (parts.length === 0) {
      return;
    }
    const markerLabel = parts.join(" | ");

    const waveformArea = this.getWaveformArea();
    const y = waveformArea.top + 6;
    const xPadding = 10;

    this.ctx.save();
    this.ctx.font = "11px monospace";
    this.ctx.textAlign = "right";
    this.ctx.textBaseline = "top";
    const textWidth = this.ctx.measureText(markerLabel).width;
    const boxWidth = textWidth + 12;
    const boxHeight = 20;
    const boxX = Math.max(0, this.canvas.width - boxWidth - xPadding);
    const textX = boxX + boxWidth - 6;
    this.ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    this.ctx.fillRect(boxX, y - 2, boxWidth, boxHeight);
    this.ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(boxX, y - 2, boxWidth, boxHeight);
    this.ctx.fillStyle = "#e2e8f0";
    this.ctx.fillText(markerLabel, textX, y + 2);
    this.ctx.restore();
  }

  private drawPersistentTimestamp(): void {
    if (this.config.persistentTimestamp === false) {
      return;
    }

    const viewportAnchorSeconds = this.currentTime + this.config.timeScale / 2;
    const startIso = this.config.recordingStartIso || null;
    const startEpochMs = startIso ? Date.parse(startIso) : Number.NaN;
    const absoluteDate = Number.isFinite(startEpochMs)
      ? new Date(startEpochMs + viewportAnchorSeconds * 1000)
      : null;
    const absoluteLabel = absoluteDate
      ? absoluteDate.toISOString().replace("T", " ").replace("Z", " UTC")
      : `Relative ${this.formatTimePrecise(viewportAnchorSeconds)}`;

    const waveformArea = this.getWaveformArea();
    const xPadding = 10;
    const y = waveformArea.top + 30;

    this.ctx.save();
    this.ctx.font = "11px monospace";
    this.ctx.textAlign = "right";
    this.ctx.textBaseline = "top";
    const textWidth = this.ctx.measureText(absoluteLabel).width;
    const boxWidth = textWidth + 12;
    const boxX = Math.max(0, this.canvas.width - boxWidth - xPadding);
    this.ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    this.ctx.fillRect(boxX, y - 2, boxWidth, 20);
    this.ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(boxX, y - 2, boxWidth, 20);
    this.ctx.fillStyle = "#cbd5e1";
    this.ctx.fillText(absoluteLabel, boxX + boxWidth - 6, y + 2);
    this.ctx.restore();
  }

  private drawCursorOverlay(): void {
    const plot = this.getPlotGeometry();
    const { width } = this.canvas;
    if (
      width <= 0 ||
      !Number.isFinite(this.config.timeScale) ||
      this.config.timeScale <= 0
    ) {
      return;
    }

    const waveformArea = this.getWaveformArea();
    const waveformTop = waveformArea.top;
    const waveformBottom = waveformArea.bottom;
    if (waveformBottom <= waveformTop) return;

    const viewportStart = this.currentTime;
    const viewportEnd = this.currentTime + this.config.timeScale;

    const drawVerticalLine = (
      x: number,
      color: string,
      lineWidth: number,
      dashed: boolean,
    ) => {
      this.ctx.save();
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = lineWidth;
      if (dashed) {
        this.ctx.setLineDash([4, 3]);
      } else {
        this.ctx.setLineDash([]);
      }
      this.ctx.beginPath();
      this.ctx.moveTo(x, waveformTop);
      this.ctx.lineTo(x, waveformBottom);
      this.ctx.stroke();
      this.ctx.restore();
    };

    // Trackit-style page playhead (reading line) — always drawn when valid.
    const playheadOffset = Math.max(
      0,
      Math.min(this.config.timeScale, this.pagePlayheadOffsetSeconds),
    );
    const playheadX = Math.min(
      Math.max(plot.left + 0.5, plot.left + (playheadOffset / this.config.timeScale) * plot.width),
      Math.max(plot.left + 0.5, plot.right - 0.5),
    );
    const playheadAbsTime = viewportStart + playheadOffset;
    drawVerticalLine(playheadX, "rgba(239, 68, 68, 0.95)", 1.5, false);

    // Top grip so the reading line is obviously draggable.
    this.ctx.save();
    this.ctx.fillStyle = "rgba(239, 68, 68, 0.95)";
    this.ctx.beginPath();
    this.ctx.moveTo(playheadX, waveformTop);
    this.ctx.lineTo(playheadX - 5, waveformTop + 8);
    this.ctx.lineTo(playheadX + 5, waveformTop + 8);
    this.ctx.closePath();
    this.ctx.fill();

    // Time chip lives in the ruler (or to the right of the line). Header /
    // Go-to-time remain the authoritative clock; this chip is a local hint.
    const labelColumnWidth = plot.left;
    this.ctx.font = "bold 11px monospace";
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";
    const playheadLabel = this.formatTimePrecise(playheadAbsTime);
    const chipPadX = 5;
    const chipWidth = this.ctx.measureText(playheadLabel).width + chipPadX * 2;
    const chipLayout = resolvePagePlayheadTimeChipLayout({
      playheadX,
      canvasWidth: width,
      chipWidth,
      chipHeight: PAGE_PLAYHEAD_CHIP_HEIGHT_PX,
      labelColumnWidth,
      waveformTop,
      ruler: this.getTimescaleRulerRect(),
    });
    if (chipLayout.visible) {
      this.ctx.fillStyle = "#dc2626";
      this.ctx.fillRect(
        chipLayout.x,
        chipLayout.y,
        chipWidth,
        PAGE_PLAYHEAD_CHIP_HEIGHT_PX,
      );
      this.ctx.strokeStyle = "#ffffff";
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(
        chipLayout.x + 0.5,
        chipLayout.y + 0.5,
        chipWidth - 1,
        PAGE_PLAYHEAD_CHIP_HEIGHT_PX - 1,
      );
      this.ctx.fillStyle = "#ffffff";
      this.ctx.fillText(
        playheadLabel,
        chipLayout.x + chipWidth / 2,
        chipLayout.y + PAGE_PLAYHEAD_CHIP_HEIGHT_PX / 2,
      );
    }
    this.ctx.restore();

    if (
      this.pinnedCursorTime !== null &&
      this.pinnedCursorTime >= viewportStart &&
      this.pinnedCursorTime <= viewportEnd
    ) {
      const pinnedX = this.timeToX(this.pinnedCursorTime);
      drawVerticalLine(pinnedX, "rgba(245, 158, 11, 0.95)", 1.5, false);

      const pinnedLabel = `REF ${this.formatTimePrecise(this.pinnedCursorTime)}`;
      this.ctx.save();
      this.ctx.font = "10px monospace";
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "top";
      const labelWidth = this.ctx.measureText(pinnedLabel).width;
      const padding = 4;
      const boxWidth = labelWidth + padding * 2;
      const boxX = Math.max(
        0,
        Math.min(width - boxWidth, pinnedX - boxWidth / 2),
      );
      const boxY = waveformTop + 4;
      this.ctx.fillStyle = "rgba(17, 24, 39, 0.85)";
      this.ctx.fillRect(boxX, boxY, boxWidth, 16);
      this.ctx.strokeStyle = "rgba(245, 158, 11, 0.95)";
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(boxX, boxY, boxWidth, 16);
      this.ctx.fillStyle = "#fbbf24";
      this.ctx.fillText(pinnedLabel, boxX + boxWidth / 2, boxY + 3);
      this.ctx.restore();
    }

    if (!this.crosshairPosition) {
      return;
    }

    const x = Math.max(0, Math.min(width, this.crosshairPosition.x));
    const y = Math.max(
      waveformTop,
      Math.min(waveformBottom, this.crosshairPosition.y),
    );
    const crosshairTime = this.currentTime + this.canvasXToRelativeSeconds(x);
    const pointHit = this.getMontageChannelHitAtCanvasPoint(x, y);
    const hit = pointHit
      ? { channel: pointHit.channel, index: pointHit.index }
      : null;
    const horizontalGuideY = pointHit?.traceY ?? y;
    const polarityMultiplier = this.getPolarityMultiplier();

    drawVerticalLine(x, "rgba(56, 189, 248, 0.9)", 1, true);
    this.ctx.save();
    this.ctx.strokeStyle = "rgba(56, 189, 248, 0.9)";
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([4, 3]);
    this.ctx.beginPath();
    this.ctx.moveTo(plot.left, horizontalGuideY);
    this.ctx.lineTo(plot.right, horizontalGuideY);
    this.ctx.stroke();
    this.ctx.restore();

    const readoutLines = [`T ${this.formatTimePrecise(crosshairTime)}`];
    if (hit) {
      readoutLines.push(`Ch ${hit.channel.label}`);
      const sampleIndex = Math.round(
        (crosshairTime - this.dataTimeOffset) * this.sampleRate,
      );
      const channelValue = this.getMontageChannelValueAtSample(
        hit.channel,
        sampleIndex,
      );
      if (channelValue !== null) {
        const gain = this.getChannelGainMultiplier(hit.channel.label);
        const displayedValue = channelValue * polarityMultiplier * gain;
        readoutLines.push(`Amp ${displayedValue.toFixed(1)} uV`);
        if (Math.abs(gain - 1) > 0.0001) {
          readoutLines.push(`Gain ${gain.toFixed(2)}x`);
        }
      }
    }

    if (this.pinnedCursorTime !== null) {
      const delta = crosshairTime - this.pinnedCursorTime;
      readoutLines.push(`dT ${delta >= 0 ? "+" : ""}${delta.toFixed(2)}s`);
    }

    this.ctx.save();
    this.ctx.font = "11px monospace";
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "middle";
    const lineHeight = 14;
    const padding = 6;
    const boxWidth =
      Math.max(
        ...readoutLines.map((line) => this.ctx.measureText(line).width),
      ) +
      padding * 2;
    const boxHeight = readoutLines.length * lineHeight + padding * 2;
    const idealX = x <= width * 0.6 ? x + 12 : x - boxWidth - 12;
    const boxX = Math.max(0, Math.min(width - boxWidth, idealX));
    const boxY = Math.max(
      waveformTop + 4,
      Math.min(waveformBottom - boxHeight - 4, horizontalGuideY + 12),
    );

    this.ctx.fillStyle = "rgba(17, 24, 39, 0.85)";
    this.ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
    this.ctx.strokeStyle = "rgba(56, 189, 248, 0.9)";
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);
    this.ctx.fillStyle = "#e2e8f0";
    readoutLines.forEach((line, index) => {
      this.ctx.fillText(
        line,
        boxX + padding,
        boxY + padding + lineHeight * index + lineHeight / 2,
      );
    });
    this.ctx.restore();
  }

  /**
   * Format time in compact MM:SS format
   */
  private formatTimeCompact(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }

  /**
   * Format time in HH:MM:SS format
   */
  private formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  private formatTimePrecise(seconds: number): string {
    if (!Number.isFinite(seconds)) return "00:00:00.0";
    const nonNegativeSeconds = Math.max(0, seconds);
    const totalTenths = Math.round(nonNegativeSeconds * 10);
    const wholeSeconds = Math.floor(totalTenths / 10);
    const tenths = totalTenths % 10;
    return `${this.formatTime(wholeSeconds)}.${tenths}`;
  }

  /**
   * Leave the canvas empty when no channels are loaded yet.
   * Do not paint "No EEG data loaded" — that flashes during the normal gap
   * between chrome mount and first loadChannels(), and permanent empty states
   * are handled by the React viewer error UI instead.
   */
  private drawPlaceholder(): void {
    // clear() already painted the canvas background; intentional no-op text.
  }

  /**
   * Generate synthetic test data for demonstration
   */
  public static generateTestData(
    channelNames: string[],
    durationSeconds: number,
    sampleRate: number = 256,
  ): EEGChannel[] {
    const sampleCount = durationSeconds * sampleRate;

    return channelNames.map((name, index) => {
      const data = new Float32Array(sampleCount);

      // Generate synthetic EEG-like signal
      for (let i = 0; i < sampleCount; i++) {
        const t = i / sampleRate;

        // Base alpha rhythm (8-12 Hz)
        const alpha = 30 * Math.sin(2 * Math.PI * 10 * t + index * 0.5);

        // Beta activity (13-30 Hz)
        const beta = 10 * Math.sin(2 * Math.PI * 20 * t + index * 0.3);

        // Theta activity (4-8 Hz)
        const theta = 20 * Math.sin(2 * Math.PI * 6 * t + index * 0.7);

        // Deterministic noise using sine function (instead of Math.random())
        const noise = Math.sin(t * 100 + index * 1.3) * 5;

        // Combine signals
        data[i] = alpha + beta + theta + noise;
      }

      return {
        name,
        data,
        sampleRate,
        unit: "μV",
      };
    });
  }
}

export default EEGWaveformRenderer;
